/**
 * App.tsx — root component for the Fornland heritage map.
 *
 * Owns global state (active layer/group/tab, language, theme, selected
 * pin, user data, the map ref) and orchestrates the layer fetch
 * pipelines, search, enrichment, and detail panels.
 *
 * Self-contained sub-concerns have been extracted into hooks:
 *   - useFetchLayers     viewport-gated layer fetching
 *   - useEnrichment      detail-panel enrichment pipeline
 *   - useSearch          unified pin+place search
 *   - useUserData        visited / saved / collections persistence
 *   - useDevice          UA + viewport device-class flags
 *   - useInstallPrompt   PWA install nudge orchestration
 *   - useCompass         walk-to-pin compass overlay
 *   - useTerrain3D       MapLibre 3D terrain toggle
 *   - useSeaMonsterOverlay  decorative theme-adaptive image
 *
 * Render sub-trees in src/components/: WelcomeSheet, InstallPromptBanner,
 * Detail*, modals (About/Legal/Methodology/Auth/etc).
 *
 * This file is still large (~3 kLOC) because the MapGL <Source>/<Layer>
 * declarations for ~20 layer types plus the nav-bar rendering live
 * inline; both are candidates for further extraction.
 */
import { useState, useCallback, useRef, useEffect, useMemo, useDeferredValue, Suspense } from "react";
import { lazyWithStrings } from "./i18n/lazyComponent.ts";
import React from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import MapGL, {
  Source,
  Layer,
} from "react-map-gl/maplibre";
import type {
  MapRef,
  MapLayerMouseEvent,
  ViewStateChangeEvent,
} from "react-map-gl/maplibre";
import type { GeoJSONSource, FilterSpecification } from "maplibre-gl";

// Types
import type {
  LayerType, HeritageSite, FolkloreRecord, FolkloreContext, RuneStone, HistoricalEvent, Monument,
  ProtectedBuilding, Shipwreck, Museum, RockArt, UnescoSite, IndustrialSite,
  HistoricRoute, CuratedWalk, WalkStop, ThingSite, BarrowSite, MegalithSite, TownscapeSite, PlaqueSite, StolpersteinSite, ByleksikonSite, BBox, EnrichmentData, ActiveTab, CategoryGroup,
  Lang, ThemeMode, BadgeDef, NearbyPinItem, UnifiedSearchResult, Country,
} from "./types/index.ts";

// Constants
import {
  GROUP_TABS, GROUP_COLORS, LAYER_GROUP, PERIOD_COLORS, OSLO_BOUNDS, BYLEKSIKON_TYPE_COLORS,
  IS_BOUNDS, FO_BOUNDS, DE_BOUNDS, GB_BOUNDS, EN_BOUNDS, UK_BOXES, IE_BOUNDS, FI_FOLKLORE_BOUNDS, SHETLAND_BOUNDS, ORKNEY_BOUNDS,
  DK_BOUNDS, NO_BOUNDS, SE_BOUNDS, FI_BOUNDS, GL_BOUNDS, IM_BOUNDS, EE_BOUNDS, LV_BOUNDS, LT_BOUNDS, NL_BOUNDS,
} from "./constants/index.ts";
import { SEA_MONSTERS, seaMonsterCorners, SEA_MONSTER_MAX_ZOOM } from "./constants/seaMonsters.ts";

// i18n
import { readingLang, walkIn } from "./utils/walkReading.ts";
import { t, detectLang, detectLangFromIP, langFromHash, langFromBrowser, localizeFolkloreTitle } from "./i18n/index.ts";

// Hooks
import { detectTheme, resolveTheme } from "./hooks/useTheme.ts";
import { useUserData } from "./hooks/useUserData.ts";
import { useSheetDismiss } from "./hooks/useSheetDismiss.ts";
import { useGeoJsonLayers } from "./hooks/useGeoJsonLayers.ts";
import { useEnrichment } from "./hooks/useEnrichment.ts";
import { useSearch } from "./hooks/useSearch.ts";
import { useFetchLayers } from "./hooks/useFetchLayers.ts";

// Utils — geo
import { haversineKm, determineCountry, boundsOf, bboxOfBounds, bboxOverlaps, bboxOverlapsAny, viewportFitsACountryFile } from "./utils/geo.ts";
import { primeCompassPermission } from "./utils/compassPermission.ts";

// Utils — geojson
import { toSimpleGeoJSON, deduplicateMonuments } from "./utils/geojson.ts";
import { warnFetch } from "./utils/logErr.ts";
import { parseDeepLinkTarget, parseDeepLinkTab, parseDeepLinkYear, parseDeepLinkShowUnbuilt, coordinateFromPageMetadata, SURVEY_HOME } from "./utils/deepLink.ts";
import { useSiteDates } from "./hooks/useSiteDates.ts";
import { runestoneYears, eventYears, megalithYears, stolpersteinYears } from "./utils/yearRange.ts";
import { stopsFor } from "./utils/timelineStops.ts";
import { initialTabState } from "./utils/tabState.ts";
import { pastSheetAt, pastSheetView, sheetFromYear, type PastSheet } from "./utils/pastSheets.ts";
import { isFolkloreDuplicate } from "./utils/folkloreDedup.ts";


// Utils — map expressions
import {
  periodColorExpr, clusterProps, dominantPeriodColor,
  folkloreCatColorExpr, monumentCatColorExpr,
  clusterRadiusExpr, CLUSTER_OPACITY,
} from "./utils/mapExpressions.ts";
import type { MapExpression } from "./utils/mapExpressions.ts";

// API — archaeological is dynamic-imported below (one-time fetch when
// heritage tab opens). Avoids pulling its Wikidata SPARQL helpers into
// the critical path bundle.

// API — pre-generated enrichment cache
// loadPregeneratedEnrichment is no longer needed at module load; the
// cache lazy-fetches on the first detail-panel open inside useEnrichment.

// Auth
import { useAuth } from "./hooks/useAuth.ts";
import { useDevice } from "./hooks/useDevice.ts";
import { useInstallPrompt } from "./hooks/useInstallPrompt.ts";
import { useCompass } from "./hooks/useCompass.ts";
import { useTerrain3D } from "./hooks/useTerrain3D.ts";
import { useSeaMonsters } from "./hooks/useSeaMonsters.ts";
// AuthModal is lazy-loaded below — rarely opened, keeps the
// `@supabase/auth-ui-react`-style form bundle off the critical path.
const AuthModal = lazyWithStrings(() => import("./components/AuthModal.tsx").then(m => ({ default: m.AuthModal })));

// Supabase client (for community content fetches)
import { supabase } from "./lib/supabase.ts";

// API — folklore is dynamic-imported below when the folklore layer is
// toggled on. Avoids ~18 kB of country-specific loaders on every page.

// API — overpass
// API — overpass is dynamic-imported. The module bundles the
// runestones/battlefields/monuments/churches fetchers, all of which
// only run on layer toggle or inside the idle-deferred preload.
import type * as OverpassMod from "./api/overpass.ts";
let _overpassMod: Promise<typeof OverpassMod> | null = null;
const loadOverpass = (): Promise<typeof OverpassMod> => (_overpassMod ??= import("./api/overpass.ts"));
const fetchRunestones: typeof OverpassMod.fetchRunestones = (...a) => loadOverpass().then(m => m.fetchRunestones(...a));
const fetchBattlefields: typeof OverpassMod.fetchBattlefields = (...a) => loadOverpass().then(m => m.fetchBattlefields(...a));
const fetchWikidataBattles: typeof OverpassMod.fetchWikidataBattles = (...a) => loadOverpass().then(m => m.fetchWikidataBattles(...a));
const fetchHistoricMonuments: typeof OverpassMod.fetchHistoricMonuments = (...a) => loadOverpass().then(m => m.fetchHistoricMonuments(...a));
const fetchChurchesByCountry: typeof OverpassMod.fetchChurchesByCountry = (...a) => loadOverpass().then(m => m.fetchChurchesByCountry(...a));
// deduplicateBattles is sync; the one caller below chains it through a
// loadOverpass().then(...) to use it from the already-cached module.






// API — megaliths is dynamic-imported below — opt-in megalith layer.
// fetchTownscapes is dynamic-imported below — townscape layer is opt-in
// and we don't want its api/townscape module on the critical path.

// API — plaques
// fetchPlaques is dynamic-imported below — plaque layer is opt-in.

// API — stolpersteine
// fetchStolpersteine is dynamic-imported below — stolperstein layer is opt-in.

// API — lighthouses
// fetchLighthouses is dynamic-imported below — lighthouse layer is opt-in.

// API — Northern Ireland (battlesites still used in battlefield loading)
// fetchNIBattlesites is dynamic-imported below — runs only when battlefield layer is shown.

// API — knowledge graph
import type { WikidataRelation } from "./api/graph.ts";
import type { WikiInfo } from "./api/wikiArticle.ts";
// WikiArticleViewer is lazy-loaded below — only opened when the user
// clicks a Wikipedia relation chip in the detail panel.
const WikiArticleViewer = lazyWithStrings(() => import("./components/WikiArticleViewer.tsx").then(m => ({ default: m.WikiArticleViewer })));

// API — wikidata is dynamic-imported below — museum/UNESCO/historical-event
// loaders are needed only when those layers open.

// API — static
// fetchThingSites + fetchHistoricRoutes are dynamic-imported below — opt-in layers.

// API — curated conflicts is dynamic-imported below — used only in the
// battlefield layer pipeline.

// API — Oslo Byleksikon
// loadOsloByleksikon is dynamically imported below (heavy: 6,100-entry index
// + per-article text loaders). Avoid pulling it into the main bundle since
// the byleksikon layer is Oslo-specific and most users won't enable it.

// Icons
import { Search, MapPin, Feather, X, Sun, Moon, Eye, Crosshair, Plus, Minus, User, Info, Compass, Landmark, Sparkles, History, Focus, Hourglass } from "lucide-react";

// Styles
import appStyles from "./App.module.css";

// Components
import { NavBtn } from "./components/NavBtn.tsx";
import { MapLegend, type LegendSection } from "./components/MapLegend.tsx";
import { BadgeToast } from "./components/BadgeToast.tsx";
const AboutModal = lazyWithStrings(() => import("./components/AboutModal.tsx").then(m => ({ default: m.AboutModal })));
const LegalModal = lazyWithStrings(() => import("./components/LegalModal.tsx").then(m => ({ default: m.LegalModal })));
const MethodologyModal = lazyWithStrings(() => import("./components/MethodologyModal.tsx").then(m => ({ default: m.MethodologyModal })));
import { CompassOverlay } from "./components/CompassOverlay.tsx";
// Lazy: seen once per browser and never again, so it has no business in the
// main bundle. The map is already up by the time it renders.
const WelcomeSheet = lazyWithStrings(() => import("./components/WelcomeSheet.tsx").then(m => ({ default: m.WelcomeSheet })));
import { InstallPromptBanner } from "./components/InstallPromptBanner.tsx";
const ExplorerProfile = lazyWithStrings(() => import("./components/ExplorerProfile.tsx").then(m => ({ default: m.ExplorerProfile })));
const ModerationPanel = lazyWithStrings(() => import("./components/ModerationPanel.tsx").then(m => ({ default: m.ModerationPanel })));
const CollectionPage = lazyWithStrings(() => import("./components/CollectionPage.tsx").then(m => ({ default: m.CollectionPage })));
const SharedListSheet = lazyWithStrings(() => import("./components/SharedListSheet.tsx").then(m => ({ default: m.SharedListSheet })));
const HiddenHistory = lazyWithStrings(() => import("./components/HiddenHistory.tsx").then(m => ({ default: m.HiddenHistory })));
const ExploreMode = lazyWithStrings(() => import("./components/ExploreMode.tsx").then(m => ({ default: m.ExploreMode })));
// ExploreAddressInput is lazy-loaded below — only shown when the user
// opens the "Explore hidden history near…" prompt.
const ExploreAddressInput = lazyWithStrings(() => import("./components/ExploreAddressInput.tsx").then(m => ({ default: m.ExploreAddressInput })));
// The collapsed state of an open walk. Only ever reached from the walk panel,
// which is itself lazy, so the bar joins it on this side of the split.
const PastLens = lazyWithStrings(() => import("./components/PastLens.tsx").then(m => ({ default: m.PastLens })));
const TimelineBar = lazyWithStrings(() => import("./components/TimelineBar.tsx").then(m => ({ default: m.TimelineBar })));
const WalkModeBar = lazyWithStrings(() => import("./components/WalkModeBar.tsx").then(m => ({ default: m.WalkModeBar })));

// Detail panels
import { DetailPanel, preloadDetail } from "./components/DetailPanel.tsx";

/**
 * The layer of the record the visitor landed on, if they landed on one: a
 * /s/ page (its script has written #site=…&layer=… by now) or a #walk= link.
 * Its panel is fetched at once, and the speculative preload of other layers
 * waits for it; see preloadDetail and the preload effect below.
 */
const LANDING_LAYER: string | null = (() => {
  if (typeof window === "undefined") return null;
  const h = new URLSearchParams(window.location.hash.slice(1));
  if (h.get("walk")) return "walk";
  if (h.get("site")) return h.get("layer") ?? "heritage";
  return /^\/s\/([a-z_-]+)\//.exec(window.location.pathname)?.[1] ?? null;
})();
if (LANDING_LAYER) preloadDetail(LANDING_LAYER);


/**
 * The language the URL fragment asked for, read once at module load.
 *
 * Deliberately not called again inside the effects below. The fragment is
 * consumed and then stripped, so a second call would answer null, and which
 * effect happens to run first would decide whether the IP guess overwrites a
 * language the visitor asked for by name.
 */
const HASH_LANG = langFromHash();

// Clean up stale/old cache keys on startup
try {
  const VALID_KEYS = new Set(["fornland-lang", "fornland-theme", "fornland-user", "fornland-user-id", "fornland-install-dismissed", "fornland-welcome-seen", "fornland-map-view", "fornland-active-tab", "fornland-active-group", "fornland-explore-opened", "fornland-explore-coach-dismissed", "fornland-last-error", "fornland-runestones-v6", "fornland-battlefields-v6", "fornland-battles-wikidata-v4", "fornland-monuments-v6", "fornland-churches-dk-v3", "fornland-churches-no-v3", "fornland-churches-se-v3", "fornland-churches-fi-v3", "fornland-churches-is-v3", "fornland-churches-gl-v3", "fornland-churches-fo-v3", "fornland-churches-gb-v1", "fornland-churches-ie-v1", "fornland-churches-im-v1", "fornland-churches-ee-v1", "fornland-churches-lv-v1", "fornland-churches-lt-v1", "fornland-churches-nl-v1", "fornland-museums-v7", "fornland-osm-museums-v7", "fornland-unesco-v6", "fornland-geoparks-v1", "fornland-thingsites-v7", "fornland-historical-events-v15", "fornland-megaliths-v8", "fornland-wikidata-arch-v6", "fornland-sagas-v4", "fornland-plaques-v7"]);
  // Prefix-allowed keys: dynamic suffixes (e.g. address/radius/lang) are versioned via the prefix itself.
  const VALID_PREFIXES = ["fornland-nar-v3-"];
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith("fornland-")) continue;
    if (VALID_KEYS.has(key)) continue;
    if (VALID_PREFIXES.some(p => key.startsWith(p))) continue;
    localStorage.removeItem(key);
  }
} catch { /* ignore */ }


/** Debounced state setter — batches rapid-fire updates into one rerender */
function useDebouncedState<T>(initial: T, delay = 250): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(initial);
  const pendingRef = useRef<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSet = useCallback((action: React.SetStateAction<T>) => {
    const next = typeof action === "function" ? (action as (prev: T) => T)(pendingRef.current ?? value) : action;
    pendingRef.current = next;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setValue(pendingRef.current!);
      pendingRef.current = null;
      timerRef.current = null;
    }, delay);
  }, [value, delay]);
  return [value, debouncedSet];
}

/**
 * Did this visit ask for one specific place, rather than for the map in
 * general? True for a shared /s/ URL, for a #site= or #walk= hash, and for
 * anything the deep-link parser answers, which is a coordinate or the
 * survey-sheet link. The list used to be `site=` and `lat=` alone, so the
 * campaign link (#hist=1 with no coordinate) counted as a plain visit and
 * the first-visit geo-IP flight took it from Denmark to the visitor's own
 * country, measured from Oslo on 2 Sep 2026 as a synchronous jump to the
 * Norwegian overview plus a fetch of every layer for it.
 *
 * Only meaningful before the onLoad deep-link handler clears the hash, so
 * callers capture it during the first render and keep it in a ref.
 */
function arrivedViaDeepLink(): boolean {
  try {
    const h = window.location.hash;
    if (parseDeepLinkTarget(h) || h.includes("site=") || h.includes("walk=")) return true;
    return window.location.pathname.startsWith("/s/");
  } catch { return false; }
}

// Static map-filter/style expressions. Module scope on purpose: defined
// inside App() they got a fresh identity every render, which churned the
// filter/paint props of every <Layer> that uses them.
const noCluster = ["!", ["has", "point_count"]] as FilterSpecification;
const byleksikonColorExpr: MapExpression[] = ["match", ["get", "oblType"], ...Object.entries(BYLEKSIKON_TYPE_COLORS).flat(), "#6a7a7a"];

export default function App() {
  const mapRef = useRef<MapRef>(null);

  // Load pre-generated enrichment cache on startup (lazy, non-blocking)
  // (loadPregeneratedEnrichment is now lazy on first detail-panel open;
  //  the previous eager-fetch on App mount cost 286 kB for users who
  //  never opened a pin.)

  // Dev-only: expose the underlying MapLibre map instance on window so E2E
  // tests (Playwright) can drive zoom/center directly via `window.__map`
  // instead of pixel-clicking the canvas. Stripped from production builds
  // by Vite's `import.meta.env.DEV` constant.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const id = setInterval(() => {
      const m = mapRef.current?.getMap();
      if (m) {
        (window as unknown as { __map: unknown }).__map = m;
        clearInterval(id);
      }
    }, 50);
    return () => clearInterval(id);
  }, []);

  // Auth state
  const { user, callbackError, clearCallbackError, signInWithEmail, signUpWithEmail, resendConfirmation, resetPassword, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const handleLoginRequest = useCallback((m: "login" | "signup" = "login") => {
    // Alt der ikke er «signup» er et login. Grunden er ikke smag: gives denne
    // haandtering direkte til et onClick, sender React klikhaendelsen som
    // foerste argument, og flere forbrugere typer proppen `() => void`, saa
    // tsc ser det ikke. Uden denne linje ville authMode blive et MouseEvent,
    // og modalen aabne i hverken login eller oprettelse, men i nulstilling.
    setAuthMode(m === "signup" ? "signup" : "login");
    setShowAuthModal(true);
  }, []);
  /**
   * Om det er en oprettelse eller et login siges af formularen, ikke gaettes
   * af om navnefeltet er udfyldt.
   *
   * Det blev udledt af `displayName`, og navnefeltet er ikke paakraevet. En
   * laeser der valgte «opret konto» og sprang navnet over, sendte altsaa et
   * login paa en konto der ikke fandtes, og fik «forkert email eller
   * adgangskode» uden at forstaa hvorfor.
   */
  const handleLogin = useCallback(async (mode: "login" | "signup", email?: string, password?: string, displayName?: string) => {
    if (!email || !password) return { error: new Error("Missing credentials") };
    if (mode === "signup") return signUpWithEmail(email, password, displayName?.trim() || undefined);
    return signInWithEmail(email, password);
  }, [signInWithEmail, signUpWithEmail]);

  // Heritage state
  const [heritageFeatures, setHeritageFeatures] = useState<GeoJSON.Feature[]>([]);
  // Mirror heritageFeatures into a ref so callbacks captured at first render
  // (notably the onLoad deep-link handler's trySelect) can read the latest
  // array without forcing a re-create of the callback. Avoids the stale-
  // closure that previously caused /#site=… deep links to fail.
  const heritageFeaturesRef = useRef<GeoJSON.Feature[]>([]);
  useEffect(() => { heritageFeaturesRef.current = heritageFeatures; }, [heritageFeatures]);
  // Cross-layer detail state — narrowed by `selectedType` in DetailPanel.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selected, setSelected] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<LayerType>("heritage");
  const [activePeriod, setActivePeriod] = useState("all");
  // The timeline's year, null while the bar is closed. Read from `year=` in
  // the hash at first render like the camera and the tab, so a shared link
  // opens onto the same picture (utils/deepLink.ts).
  const [timelineYear, setTimelineYear] = useState<number | null>(() => parseDeepLinkYear(window.location.hash));
  // Whether the timeline draws sites not yet built as hollow rings. On by
  // default; `unbuilt=0` in the hash turns them off (utils/deepLink.ts).
  const [showUnbuilt, setShowUnbuilt] = useState<boolean>(() => parseDeepLinkShowUnbuilt(window.location.hash));
  // The country under the map's centre, for the timeline's era labels. Set
  // in onLoad and onMoveEnd beside the lens sheet.
  const [viewCountry, setViewCountry] = useState<Country>("DK");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState({ dk: 0, no: 0, se: 0 });
  // Welcome sheet: once per browser, and never on a deep link. Someone
  // arriving from a shared /s/ URL or a #site= hash asked for one specific
  // place, so an overlay explaining the map is just in the way.
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      if (localStorage.getItem("fornland-welcome-seen")) return false;
      return !arrivedViaDeepLink();
    } catch { return false; }
  });
  // Captured during the first render, because the onLoad handler clears the
  // hash once it has flown to the target. Anything that wants to know "did
  // this visit ask for a specific place?" later — notably the geo-IP effect,
  // which resolves seconds after load — has to read it from here.
  const deepLinkedRef = useRef(arrivedViaDeepLink());
  const welcomeClose = useCallback(() => setShowWelcome(false), []);
  const welcomeDismiss = useSheetDismiss(welcomeClose);
  // showInstallPrompt + deferredPromptRef are now owned by useInstallPrompt.
  const [showLegal, setShowLegal] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showHiddenHistory, setShowHiddenHistory] = useState(false);
  const [showExploreMode, setShowExploreMode] = useState(false);
  // First-time coachmark pointing at the explore-mode entry. Dismissed
  // permanently on first interaction (tap pill / tap close / open mode)
  // or after 12 s of being visible. Persisted in localStorage.
  const [paperTexture, setPaperTexture] = useState(false);
  useEffect(() => {
    const show = () => setPaperTexture(true);
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const t = window.setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(show, { timeout: 4000 }) : show()), 2500);
    return () => window.clearTimeout(t);
  }, []);
  const welcomeAtStart = useRef(showWelcome);
  const [showExploreCoach, setShowExploreCoach] = useState(false);
  // Records that Explore Mode has been opened at least once. Used to be
  // read back as a "NEW" dot on the compass button; the dot is gone, but
  // the flag stays written so a future onboarding pass can tell a first
  // visit from a returning one without resetting anyone's history.
  const markExploreOpened = useCallback(() => {
    try { localStorage.setItem("fornland-explore-opened", "1"); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem("fornland-explore-coach-dismissed") === "1") return;
    } catch { /* private mode */ }
    // Explorer mode needs a position, which a desktop rarely has; the
    // coachmark pointed a mouse user at a "location denied" screen.
    if (!window.matchMedia?.("(pointer: coarse)").matches) return;
    // One introduction per visit: a reader who has just closed the welcome
    // sheet does not get a second prompt at once. It waits for the next visit.
    if (welcomeAtStart.current) return;
    // Delay so the welcome sheet / first-paint animations aren't competing.
    const showT = setTimeout(() => setShowExploreCoach(true), 4500);
    return () => clearTimeout(showT);
  }, []);
  const dismissExploreCoach = useCallback(() => {
    setShowExploreCoach(false);
    try { localStorage.setItem("fornland-explore-coach-dismissed", "1"); } catch { /* ignore */ }
  }, []);
  /**
   * The only way in, so the compass ask always rides the opening tap.
   *
   * iOS grants DeviceOrientationEvent access only while a gesture is still
   * active, and ExploreMode is a lazy chunk — asking from its mount effect
   * arrives after the download, with the activation gone, and Safari rejects
   * it. That is why the mode used to open with an "Aktivér kompas" button
   * showing instead of a live dial. Priming here is fire-and-forget: the
   * answer is cached, and useExploreMode reads it back a moment later.
   */
  const openExploreMode = useCallback(() => {
    primeCompassPermission();
    markExploreOpened();
    dismissExploreCoach();
    setShowExploreMode(true);
  }, [markExploreOpened, dismissExploreCoach]);
  // Auto-hide after 12 s of being visible.
  useEffect(() => {
    if (!showExploreCoach) return;
    const t = setTimeout(dismissExploreCoach, 12000);
    return () => clearTimeout(t);
  }, [showExploreCoach, dismissExploreCoach]);
  const [hiddenHistoryLocation, setHiddenHistoryLocation] = useState<{ lat: number; lng: number; address: string; narrative?: string; narrativeRadius?: number } | null>(null);
  const [returnToHH, setReturnToHH] = useState(false);
  const openHHAfterLocateRef = useRef(false);
  // Open Hidden History after GPS locate completes
  // Compass state owned by useCompass — see hook call further down once
  // userLocation + handleLocate are in scope.
  const [lang, setLang] = useState<Lang>(detectLang);
  const [relationViewer, setRelationViewer] = useState<WikiInfo | null>(null);
  const enrichmentCache = useRef(new Map<string, EnrichmentData>());
  useEffect(() => { enrichmentCache.current.clear(); }, [lang]);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [showExplorePrompt, setShowExplorePrompt] = useState(false);
  const [exploreRadius, setExploreRadius] = useState(1);
  // After GPS locate, show address prompt
  useEffect(() => {
    if (userLocation && openHHAfterLocateRef.current) {
      openHHAfterLocateRef.current = false;
      setShowExplorePrompt(true);
    }
  }, [userLocation]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastBboxRef = useRef<BBox | null>(null);

  // User data (gamification)
  const { userData, markVisited, unmarkVisited, toggleSaved, isSaved, isVisited, addBadge, addNote, persist, addRecentlyViewed, createCollection, addToCollection, removeFromCollection, deleteCollection, updateCollection } = useUserData(user);
  const [showProfile, setShowProfile] = useState(false);
  const [showModeration, setShowModeration] = useState(false);
  const [viewingCollection, setViewingCollection] = useState<import("./types/index.ts").UserCollection | null>(null);
  const isAdmin = user?.email === "thormj@gmail.com";
  const [moderationCount, setModerationCount] = useState(0);
  const [badgeToast, setBadgeToast] = useState<BadgeDef | null>(null);
  const badgeToastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Fetch moderation count (recent content in last 7 days) for admin badge
  useEffect(() => {
    if (!isAdmin || !supabase) return;
    const since = new Date(Date.now() - 7 * 86400000).toISOString();
    Promise.all([
      supabase.from("contributions").select("id", { count: "exact", head: true }).gte("created_at", since),
      supabase.from("pin_images").select("id", { count: "exact", head: true }).gte("created_at", since),
      supabase.from("discussions").select("id", { count: "exact", head: true }).gte("created_at", since),
    ]).then(([c, p, d]) => {
      setModerationCount((c.count ?? 0) + (p.count ?? 0) + (d.count ?? 0));
    }).catch(() => {});
  }, [isAdmin]);

  // Refs for viewport-fetch: allow direct MapLibre listener to read latest state without stale closures
  const fetchForLayersRef = useRef<((layers: Set<ActiveTab>, bbox: BBox) => void) | null>(null);
  const getVisibleLayersRef = useRef<((group: CategoryGroup, tab: ActiveTab) => Set<ActiveTab>) | null>(null);
  // Guards against onLoad firing more than once (style change, programmatic
  // reload) which would stack duplicate moveend listeners.
  const moveendListenerAttachedRef = useRef(false);
  // Monotonic counter for deep-link selection attempts. Incremented every
  // time onLoad's deep-link handler kicks off a retry chain; the chain
  // self-cancels if the counter advances (i.e. a newer deep link came in).
  const deepLinkSelectTokenRef = useRef(0);
  /** Bumped by each search selection, so an older one stops upgrading its panel. */
  const searchUpgradeTokenRef = useRef(0);
  const activeGroupRef = useRef<CategoryGroup>("all");
  const activeTabRef = useRef<ActiveTab>("all");

  // Theme state
  const [themeMode, setThemeMode] = useState<ThemeMode>(detectTheme);
  const [resolved, setResolved] = useState<"dark" | "light">(() => resolveTheme(themeMode));

  // Apply theme to DOM
  useEffect(() => {
    const r = resolveTheme(themeMode);
    setResolved(r);
    document.documentElement.setAttribute("data-theme", r);
    document.documentElement.style.colorScheme = r;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", r === "dark" ? "#0a0a0a" : "#f5f5f7");
    localStorage.setItem("fornland-theme", themeMode);
  }, [themeMode]);

  // Listen for OS preference changes
  useEffect(() => {
    if (themeMode !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      setResolved(mq.matches ? "dark" : "light");
      document.documentElement.setAttribute("data-theme", mq.matches ? "dark" : "light");
      document.documentElement.style.colorScheme = mq.matches ? "dark" : "light";
      document.querySelector('meta[name="theme-color"]')?.setAttribute("content", mq.matches ? "#0a0a0a" : "#f5f5f7");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [themeMode]);

  // Mark it seen the moment it renders, not on close. If the user reloads
  // mid-sheet or navigates away, they have still met it once, and showing
  // it again on every reload until they press a button would be worse.
  useEffect(() => {
    if (!showWelcome) return;
    try { localStorage.setItem("fornland-welcome-seen", "1"); } catch { /* private mode */ }
  }, [showWelcome]);

  // Map style based on resolved theme + satellite toggle
  const [satellite, setSatellite] = useState(false);
  const satelliteStyle = useMemo(() => ({
    version: 8 as const,
    glyphs: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/font/{fontstack}/{range}.pbf",
    sources: {
      "esri-satellite": {
        type: "raster" as const,
        tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
        tileSize: 256,
        maxzoom: 19,
        attribution: "Esri, Maxar, Earthstar Geographics",
      },
    },
    layers: [{ id: "satellite", type: "raster" as const, source: "esri-satellite" }],
  }), []);
  /**
   * The ETK mode: Denmark drawn the decade the legends were written down.
   * Høje målebordsblade 1842-1899, self-hosted (the tiles are public domain
   * era survey sheets; the mirror and its provenance live in
   * data/maalebordsblade/ and scripts/mirror-maalebordsblade.mjs, and the
   * runtime depends on no external key or service). `bounds` stops MapLibre
   * from even requesting tiles outside the survey's coverage; the paper-tone
   * background carries the rest of the world. maxzoom 13 with overzoom: the
   * 1:20,000 scans stay readable scaled to z14-15.
   */
  const [histMap, setHistMap] = useState(false);
  // The lens: the same sheets under a disc you drag over today's map. Only
  // offered where the sheets exist, so it follows the map's centre, and
  // never together with the full 1890 mode, which would put paper on paper.
  const [lensOn, setLensOn] = useState(false);
  const [lensSheet, setLensSheet] = useState<PastSheet | null>(null);
  const histStyle = useMemo(() => ({
    version: 8 as const,
    glyphs: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/font/{fontstack}/{range}.pbf",
    sources: {
      "hmb": {
        type: "raster" as const,
        tiles: ["https://thormartinbaerug.com/evald/tiles/{z}/{x}/{y}.png"],
        tileSize: 256,
        minzoom: 7,
        maxzoom: 14,
        bounds: [7.5, 54.3, 15.4, 58.0] as [number, number, number, number],
        attribution:
          "Indeholder data fra Styrelsen for Dataforsyning og Infrastruktur, Danske målebordsblade · Jernbaner: C. Fertner, KU (CC BY)",
      },
    },
    layers: [
      { id: "hist-paper", type: "background" as const, paint: { "background-color": "#eee7d8" } },
      { id: "hmb", type: "raster" as const, source: "hmb" },
    ],
  }), []);

  const mapStyle = histMap
    ? histStyle
    : satellite
      ? satelliteStyle
      : resolved === "dark"
        ? "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        : "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";

  // MapLibre paint colors (dynamic via resolved). The historical map is a
  // light surface whatever the app theme, so it takes the light-mode inks.
  const mapTextColor = histMap ? "#222" : satellite || resolved === "dark" ? "#fff" : "#222";
  // Hairline in the basemap's own colour. It separates clusters that
  // different layers draw on top of each other; without it two stacked
  // CLUSTER_OPACITY circles read as one shapeless blob. Doubles as the
  // halo behind cluster counts, which sit on a lighter fill than before.
  const clusterStroke = !histMap && (satellite || resolved === "dark") ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.9)";

  /**
   * Outline on every individual pin, and the opposite polarity from
   * clusterStroke above. That one borrows the basemap's own colour to keep
   * stacked clusters apart; this one has to *contrast*, because it is the only
   * thing making a pin findable.
   *
   * Measured 22/8-2026: the pin fills cannot carry that job themselves. Against
   * Carto Voyager's three surfaces, land #faf7f5, park #d8e8d0 and water
   * #a5d0e0, fourteen of the twenty-three category colours fall under 3:1
   * somewhere, Stenalder #d4a843 as low as 1,34 on water. No palette fixes it:
   * ten periods and seven folklore categories all have to stay distinguishable
   * from each other while clearing 3:1 on a pale yellow-white, a pale green and
   * a mid blue at once, and satellite mode puts aerial photography behind them,
   * which no colour clears. So the fill stays the palette it was designed as
   * and the outline carries the contrast. Do not darken the palette instead;
   * that was measured and it cannot reach.
   */
  // The historical map takes the dark stroke like light mode. Measured
  // 23/8-2026 against the tiles themselves (400-pixel samples over twelve
  // random z13 tiles): black at 0.55 clears 4,70:1 on the paper tone, 4,53:1
  // on the median surface and 3,21:1 on the darkest tenth, so the outline
  // carries the pins on the survey sheets the same way it does on Voyager.
  const pinStroke = !histMap && (satellite || resolved === "dark") ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.55)";
  /** Hairline, per the design language, but scaled so it stays one at z14. */
  const pinStrokeWidth = ["interpolate", ["linear"], ["zoom"], 5, 0.75, 10, 1, 14, 1.25];
  // What the timeline does to a pin's fill. Inside its build span it carries
  // `tf` 0..1 from splitByYear and fades in across the register's era; past
  // it, it is in use and full. A pin with no y0 is undated — the register did
  // not say, and «Oldtid» is how it usually does not say — so it is drawn at
  // half strength rather than either hidden or passed off as standing in the
  // chosen year. With the bar closed this is the old constant.
  const timelineFade: MapExpression = useMemo(() => timelineYear == null ? 0.9 : [
    "case",
    ["has", "tf"], ["+", 0.3, ["*", 0.6, ["get", "tf"]]],
    ["has", "y0"], 0.9,
    0.45,
  ], [timelineYear]);

  // Tab state (single-select) — persisted to localStorage. A deep link that
  // names a tab wins from the first render (utils/tabState.ts): effects gated
  // on the initial tab run before the map's load event, and one of them once
  // fetched for the stored tab a second before the linked one was applied.
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => initialTabState(window.location.hash, localStorage).tab);
  const [activeGroup, setActiveGroup] = useState<CategoryGroup>(() => initialTabState(window.location.hash, localStorage).group);
  const [activeFolkloreCat, setActiveFolkloreCat] = useState("all");
  const [activeMonumentCat, setActiveMonumentCat] = useState("all");

  // --- PERSIST TAB STATE ---
  useEffect(() => { localStorage.setItem("fornland-active-tab", activeTab); }, [activeTab]);
  useEffect(() => { localStorage.setItem("fornland-active-group", activeGroup); }, [activeGroup]);

  // --- LANG + MAP POSITION FROM IP ---
  useEffect(() => {
    const isFirstVisit = !localStorage.getItem("fornland-map-view");
    // `lang === "en"` is how "nobody has told us otherwise" is detected, but
    // the hreflang alternate /#lang=en tells us exactly that in words. Without
    // the third clause an English request from Google resolves to English,
    // then gets overwritten by the IP guess three seconds later.
    // And only when the browser named no language Fornland has. An en-GB
    // browser is an answer, not a default (see langFromBrowser).
    const needsLang = !localStorage.getItem("fornland-lang") && lang === "en" && !HASH_LANG && !langFromBrowser();
    if (needsLang || isFirstVisit) {
      // Country center points for initial map positioning
      const countryCenters: Record<string, { lng: number; lat: number; zoom: number }> = {
        DK: { lng: 10.4, lat: 56.0, zoom: 7 },
        NO: { lng: 10.0, lat: 62.0, zoom: 5 },
        SE: { lng: 16.0, lat: 62.5, zoom: 5 },
        FI: { lng: 25.5, lat: 63.5, zoom: 5 },
        IS: { lng: -19.0, lat: 65.0, zoom: 6 },
        FO: { lng: -7.0, lat: 62.0, zoom: 9 },
        GL: { lng: -51.7, lat: 64.2, zoom: 6 },
      };
      detectLangFromIP().then((result) => {
        if (!result) return;
        // The language guess is always welcome. The camera move is not: this
        // resolves up to 3 s after load, long after the onLoad handler has
        // flown to a deep-linked site, and a duration-0 flyTo would silently
        // throw that away. Someone opening a shared /s/ link asked for one
        // specific place; their own country's overview is not an improvement.
        if (needsLang) setLang(result.lang);
        if (isFirstVisit && !deepLinkedRef.current) {
          const center = countryCenters[result.country];
          if (center) {
            mapRef.current?.flyTo({ center: [center.lng, center.lat], zoom: center.zoom, duration: 0 });
          }
        }
      });
    }
  }, []);
  // detectLang has already consumed the fragment, so drop the parameter now.
  // Left in place it outranks localStorage on every reload, and the language
  // the visitor then picks in the switcher would not survive one. The rest of
  // the fragment is the map deep link and has to stay.
  useEffect(() => {
    if (!HASH_LANG) return;
    const rest = window.location.hash.replace(/^#/, "").split("&").filter((p) => !/^lang=/i.test(p)).join("&");
    window.history.replaceState(null, "", window.location.pathname + window.location.search + (rest ? `#${rest}` : ""));
  }, []);

  // The timeline's year in the hash, so the picture can be shared: written
  // on change, dropped when the bar closes, the rest of the fragment kept.
  // Skips the first render, which only reads what the link brought.
  const yearHashArmed = useRef(false);
  useEffect(() => {
    if (!yearHashArmed.current) { yearHashArmed.current = true; return; }
    const parts = window.location.hash.replace(/^#/, "").split("&").filter((p) => p && !/^(year|unbuilt)=/.test(p));
    if (timelineYear != null) parts.push(`year=${timelineYear}`);
    if (timelineYear != null && !showUnbuilt) parts.push("unbuilt=0");
    window.history.replaceState(null, "", window.location.pathname + window.location.search + (parts.length ? `#${parts.join("&")}` : ""));
  }, [timelineYear, showUnbuilt]);

  /**
   * Sidens titel og beskrivelse, og den ene side hvor appen ikke maa skrive
   * dem.
   *
   * `api/og.ts` skriver et rigtigt navn i `<title>` paa hver /s/-side. Denne
   * effekt skrev den over med appens egen, ubetinget, og React vandt fordi den
   * koerer bagefter. Maalt i det renderede DOM med Googlebots user-agent den
   * 21. september 2026: alle 149.047 delelink-sider renderede med «Fornland |
   * Utforsk kulturminner i Nord- og Sentral-Europa». Det er den titel Google
   * har at vise i en resultatliste for 149.047 forskellige steder, og det er
   * hvad «dublet, Google valgte en anden canonical» taeller.
   *
   * Paa /s/ lader vi derfor serverens titel og beskrivelse staa. Sproget og
   * `documentElement.lang` saettes stadig, for de hoerer til laeseren og ikke
   * til siden. /explore/ er statiske dokumenter uden React og naar aldrig
   * herind.
   */
  useEffect(() => {
    localStorage.setItem("fornland-lang", lang);
    document.documentElement.lang = lang === "no" ? "nb" : lang === "kl" ? "kl" : lang;
    if (window.location.pathname.startsWith("/s/")) return;
    const titles: Partial<Record<Lang, string>> = {
      da: "Fornland | Udforsk kulturarven i Nord- og Mellemeuropa",
      no: "Fornland | Utforsk kulturminner i Nord- og Sentral-Europa",
      sv: "Fornland | Utforska kulturarvet i Nord- och Centraleuropa",
      en: "Fornland | Explore Heritage across Northern and Central Europe",
      fi: "Fornland | Tutki Pohjois- ja Keski-Euroopan kulttuuriperintöä",
      is: "Fornland | Kannaðu menningararf Norður- og Mið-Evrópu",
      de: "Fornland | Kulturerbe in Nord- und Mitteleuropa entdecken",
      // nl/et/lv/fo take the reviewed a11y.pageTitle wording rather than a
      // guessed "explore" phrasing; without an entry they fell back to English.
      nl: "Fornland | Erfgoed in Noord- en Midden-Europa ontdekken",
      et: "Fornland | Põhja- ja Kesk-Euroopa muinsusmälestiste kaart",
      lv: "Fornland | Kultūras pieminekļu karte Ziemeļeiropā un Centrāleiropā",
      fo: "Fornland | Kort yvir fornminni í Norður- og Miðeuropa",
      kl: "Fornland | Europami avannarlerni kulturikkut kingornussiat",
    };
    document.title = titles[lang] || titles.en!;
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) {
      const descs: Partial<Record<Lang, string>> = {
        da: "Interaktivt kort over 1,8 millioner registrerede kulturminder i Nord- og Mellemeuropa. Fortidsminder, runesten, folkesagn, megalitter, skibsvrag, slagmarker og museer i Norden, på De Britiske Øer, i Baltikum, Nederlandene og Tyskland.",
        no: "Interaktivt kart over 1,8 millioner registrerte kulturminner i Nord- og Sentral-Europa. Runesteiner, folkesagn, megalitter, skipsvrak, slagmarker og museer i Norden, på De britiske øyene, i Baltikum, Nederland og Tyskland.",
        sv: "Interaktiv karta över 1,8 miljoner registrerade kulturminnen i Nord- och Centraleuropa. Runstenar, folksägner, megaliter, skeppsvrak, slagfält och museer i Norden, på Brittiska öarna, i Baltikum, Nederländerna och Tyskland.",
        en: "Interactive map of 1.8 million heritage records across Northern and Central Europe. Ancient monuments, runestones, folklore, megaliths, shipwrecks, battlefields and museums across Scandinavia, the British Isles, the Baltics, the Netherlands and Germany.",
        fi: "Interaktiivinen kartta 1,8 miljoonasta kulttuuriperintötietueesta Pohjois- ja Keski-Euroopassa. Muinaisjäännöksiä, riimukiviä, kansantaruja ja megaliitteja.",
        is: "Gagnvirkt kort yfir 1,8 milljón skráðar menningarminjar í Norður- og Mið-Evrópu. Fornminjar, rúnasteinar, þjóðsögur, risavarðar og söfnhús.",
        kl: "Europami avannarlerni kulturikkut kingornussat 1,8 millionit nunaleriummi. Itsarnitsat, ruunikkut ujarak, oqaluttuarissat aammalu katersugaasiviit.",
        de: "Interaktive Karte mit 1,8 Millionen erfassten Denkmälern in Nord- und Mitteleuropa. Grabhügel, Runensteine, Sagen, Megalithgräber, Schiffswracks, Schlachtfelder und Museen in Skandinavien, auf den Britischen Inseln, im Baltikum, in den Niederlanden und in Deutschland.",
      };
      descEl.setAttribute("content", descs[lang] || descs.en!);
    }
  }, [lang]);

  // Update map labels to match user language
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const langKey = lang === "no" ? "nb" : lang; // OSM uses "nb" for Norwegian Bokmål
    const update = () => {
      // MapLibre names its credits button once, in English, when the control
      // is built; this is the one place that already runs on a language change.
      const credits = t("map.credits", lang);
      map.getContainer().querySelectorAll(".maplibregl-ctrl-attrib-button").forEach((b) => { b.setAttribute("aria-label", credits); b.setAttribute("title", credits); });
      const style = map.getStyle();
      if (!style?.layers) return;
      for (const layer of style.layers) {
        const tf = (layer as { layout?: { "text-field"?: string | unknown[] | { stops?: unknown } } }).layout?.["text-field"];
        if (!tf) continue;
        // Three shapes reach here. A template string, "{name}" or Carto's
        // "{name_en}"; a zoom-stop object, which Carto uses to show English at
        // low zoom and the local name from z13, and which the old check never
        // matched, so every country and capital read "NORWAY" in Danish too
        // (25 Sep 2026); and an expression. Carto's tiles carry name:da,
        // name:sv and the rest, so all three become the reader's language with
        // the local name as the fallback.
        const localised = ["coalesce", ["get", `name:${langKey}`], ["get", "name"]];
        if (typeof tf === "string" && /\{name(_en)?\}/.test(tf)) {
          map.setLayoutProperty(layer.id, "text-field", localised);
        } else if (!Array.isArray(tf) && typeof tf === "object" && /\{name(_en)?\}/.test(JSON.stringify(tf))) {
          map.setLayoutProperty(layer.id, "text-field", localised);
        } else if (Array.isArray(tf)) {
          const json = JSON.stringify(tf);
          if (json.includes('"name"') && !json.includes(`"name:${langKey}"`)) {
            map.setLayoutProperty(layer.id, "text-field", ["coalesce", ["get", `name:${langKey}`], ["get", "name"]]);
          }
        }
      }
    };
    if (map.isStyleLoaded()) update();
    map.on("styledata", update);
    return () => { map.off("styledata", update); };
  }, [lang, mapStyle]);

  // --- DEVICE FLAGS + INSTALL PROMPT (extracted to hooks) ---
  const { isMobileOrTablet, isIOS, isStandalone } = useDevice();
  const [terrain3d, _setTerrain3d] = useState(false);
  const [mapCursor, setMapCursor] = useState("");
  // hasEngaged = the user has opened at least one detail panel; gates the
  // PWA install prompt so it never appears on a cold-mount map view.
  // Engagement is sticky once a panel has been opened; `returning` is read once,
  // before this visit saves a camera of its own.
  const [hasEngaged, setHasEngaged] = useState(false);
  useEffect(() => { if (selected != null) setHasEngaged(true); }, [selected]);
  const [returningVisitor] = useState(() => { try { return !!localStorage.getItem("fornland-map-view"); } catch { return false; } });
  const { showInstallPrompt, canPromptNatively, dismissInstall, handleInstall } = useInstallPrompt({
    isMobileOrTablet, isStandalone, showWelcome, hasEngaged, returning: returningVisitor, panelOpen: selected != null,
  });

  // --- TRANSLATED ARRAYS ---
  const categoryGroups = useMemo(() => [
    { key: "all" as CategoryGroup, label: t("tab.all", lang), color: "var(--text-primary)", icon: null },
    { key: "archaeology" as CategoryGroup, label: t("group.archaeology", lang), color: "#9b8ec4", icon: "archaeology" },
    { key: "built" as CategoryGroup, label: t("group.built", lang), color: "#2d8a56", icon: "built" },
    { key: "events" as CategoryGroup, label: t("group.events", lang), color: "#c62828", icon: "events" },
    { key: "folklore_group" as CategoryGroup, label: t("group.folklore_group", lang), color: "#d4a843", icon: "folklore_group" },
    { key: "explore" as CategoryGroup, label: t("group.explore", lang), color: "#0288d1", icon: "explore" },
  ], [lang]);

  const TAB_COLORS: Record<ActiveTab, string> = {
    all: "#888", heritage: "#9b8ec4", folklore: "#d4a843", runestone: "#c47a2e",
    battlefield: "#c62828", monument: "#2d8a56", shipwreck: "#0288d1",
    museum: "#2e7d4f", rockart: "#7b1fa2", unesco: "#1565c0", industrial: "#607d8b",
    route: "#ad1457", thingsite: "#4527a0", barrow: "#6a1b9a", megalith: "#5e35b1", townscape: "#d84315", plaque: "#8d6e63", stolperstein: "#a08040", lighthouse: "#e68a00", byleksikon: "#5c8a8a",
    // `walk` is not in GROUP_TABS, so nothing renders in this colour yet. The
    // Record over ActiveTab demands an answer the moment the union grows, so
    // the colour is chosen here rather than there, and turning the layer on
    // stays a change to constants/index.ts alone.
    walk: "#00838f",
  };

  const groupSubTabs = useMemo((): { key: ActiveTab; label: string; color: string }[] => {
    if (activeGroup === "all") return [];
    const tabs = GROUP_TABS[activeGroup];
    // Skip sub-tab row for groups with only 1 tab (e.g., folklore)
    if (tabs.length <= 1) return [];
    const allOption = { key: "all" as ActiveTab, label: t("tab.all", lang), color: GROUP_COLORS[activeGroup] };
    const subs = tabs.map(k => ({ key: k as ActiveTab, label: t(`tab.${k}`, lang), color: TAB_COLORS[k] }));
    return [allOption, ...subs];
  }, [activeGroup, lang]);

  const periods = useMemo(() => [
    { id: "all", label: t("period.all", lang) },
    { id: "Oldtid", label: t("period.Oldtid", lang) },
    { id: "Stenalder", label: t("period.Stenalder", lang) },
    { id: "Bronzealder", label: t("period.Bronzealder", lang) },
    { id: "Jernalder", label: t("period.Jernalder", lang) },
    { id: "Roman", label: t("period.Roman", lang) },
    { id: "Vikingetid", label: t("period.Vikingetid", lang) },
    { id: "Middelalder", label: t("period.Middelalder", lang) },
    { id: "Historisk Tid", label: t("period.Historisk Tid", lang) },
    { id: "Sami", label: t("period.Sami", lang) },
  ], [lang]);

  const monumentCategories = useMemo(() => [
    { id: "all", label: t("monument.all", lang), color: "#2d8a56" },
    { id: "castle", label: t("monument.castle", lang), color: "#2d8a56" },
    { id: "manor", label: t("monument.manor", lang), color: "#2e7d4f" },
    { id: "monastery", label: t("monument.monastery", lang), color: "#2e7d4f" },
    { id: "fort", label: t("monument.fort", lang), color: "#2d7a4a" },
    { id: "church", label: t("monument.church", lang), color: "#2d7a4a" },
    { id: "protected", label: t("monument.protected", lang), color: "#2d8a56" },
  ], [lang]);

  const folkloreCategories = useMemo(() => [
    { id: "all", label: t("folklore.all", lang), color: "#d4a843" },
    { id: "huldrefolk", label: t("folklore.huldrefolk", lang), color: "#7a8a6a" },
    { id: "monsters", label: t("folklore.monsters", lang), color: "#a78b5a" },
    { id: "ghosts", label: t("folklore.ghosts", lang), color: "#8b9dc3" },
    { id: "sorcery", label: t("folklore.sorcery", lang), color: "#9b6b9b" },
    { id: "legends", label: t("folklore.legends", lang), color: "#8d6e63" },
    { id: "folklife", label: t("folklore.folklife", lang), color: "#c49a6c" },
  ], [lang]);

  // Derived visibility flags
  // When activeGroup is "all" and activeTab is "all", show everything.
  // When a group is active and activeTab is "all", show all types in that group.
  // When a specific sub-tab is selected, show only that type.
  const isGroupAll = activeTab === "all";
  const groupTypes = activeGroup !== "all" ? GROUP_TABS[activeGroup] : [];
  /**
   * "All" means every published layer, and the walks are not one yet.
   *
   * `walk` is deliberately absent from GROUP_TABS until the Oslo grant period
   * opens, so there is no tab to reach it by; the deep link still resolves it.
   * But "all" returned true for it, and the two walk files, 69 kB, were
   * fetched by every visitor to draw pins no one could see. Reading the
   * registration here keeps the publish switch a single line, which is the
   * point of it.
   */
  const published = useMemo(() => new Set(Object.values(GROUP_TABS).flat()), []);
  /**
   * Whether the reader is on the default view of everything, everywhere.
   *
   * That is the view the file gate is for. A reader who has picked a layer or
   * a group has asked for it and gets it at any zoom, which is also what
   * `/explore/runestones` is: a landing page that names one layer and mounts
   * on no particular place. Gating that would serve an empty map to the one
   * visitor who said what they wanted.
   */
  const browsingEverything = activeGroup === "all" && isGroupAll;
  // The hook reads it through a ref, so a tab change does not rebuild every
  // fetch callback and re-trigger every consumer's effect.
  const browsingEverythingRef = useRef(browsingEverything);
  browsingEverythingRef.current = browsingEverything;
  const wantsCountryFiles = (bbox: BBox | null | undefined): boolean =>
    !!bbox && (!browsingEverything || viewportFitsACountryFile(bbox));
  const showLayer = (layer: ActiveTab) => {
    if (activeGroup === "all" && isGroupAll) return published.has(layer);
    if (isGroupAll) return groupTypes.includes(layer);
    return activeTab === layer;
  };
  const showHeritage = showLayer("heritage");
  const showFolklore = showLayer("folklore");
  const showRunestone = showLayer("runestone");
  const showBattlefield = showLayer("battlefield");
  const showMonument = showLayer("monument");
  const showBuilding = showMonument;
  const showShipwreck = showLayer("shipwreck");
  const showMuseum = showLayer("museum");
  const showRockArt = showLayer("rockart");
  const showUnesco = showLayer("unesco");
  const showIndustrial = showLayer("industrial");
  const showThingSite = showLayer("thingsite");
  const showBarrow = showLayer("barrow");
  const showMegalith = showLayer("megalith");
  const showTownscape = showLayer("townscape");
  const showPlaque = showLayer("plaque");
  const showStolperstein = showLayer("stolperstein");
  const showLighthouse = showLayer("lighthouse");
  const showRoute = showLayer("route");
  const showWalk = showLayer("walk");
  const showByleksikon = showLayer("byleksikon");

  // Memoized heritage coordinate set for deduplication
  const heritageCoordSet = useMemo(() => {
    const set = new Set<string>();
    heritageFeatures.forEach(f => {
      if (f.geometry.type === "Point") {
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        set.add(`${lng.toFixed(3)},${lat.toFixed(3)}`);
      }
    });
    return set;
  }, [heritageFeatures]);

  // Folklore state
  const [folkloreFeatures, setFolkloreFeatures] = useState<GeoJSON.Feature[]>([]);
  const folkloreIndex = useRef<Map<string, FolkloreRecord>>(new Map());
  const folkloreByCoord = useRef<Map<string, FolkloreRecord[]>>(new Map());
  const [folkloreCounts, setFolkloreCounts] = useState(0);
  const folkDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const folkLastBboxRef = useRef<BBox | null>(null);
  const [danishLoaded, setDanishLoaded] = useState(false);
  const [icelandicFolkloreLoaded, setIcelandicFolkloreLoaded] = useState(false);
  const [sagnagrunnurLoaded, setSagnagrunnurLoaded] = useState(false);
  const sagnagrunnurLoadedRef = useRef(false);

  // Runestone + Battlefield state
  const [runestoneFeatures, setRunestoneFeatures] = useState<GeoJSON.Feature[]>([]);
  const [battlefieldFeatures, setBattlefieldFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const runestoneIndex = useRef<Map<string, RuneStone>>(new Map());
  const battlefieldIndex = useRef<Map<string, HistoricalEvent>>(new Map());
  const [runestoneCount, setRunestoneCount] = useState(0);
  const [battlefieldCount, setBattlefieldCount] = useState(0);
  const [runesLoaded, setRunesLoaded] = useState(false);
  const [battlesLoaded, setBattlesLoaded] = useState(false);

  // Monument state
  const [monumentFeatures, setMonumentFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const monumentIndex = useRef<Map<string, Monument>>(new Map());
  const [monumentCount, setMonumentCount] = useState(0);
  const [monumentsLoaded, setMonumentsLoaded] = useState(false);
  const monumentsRawRef = useRef<Monument[]>([]);
  /**
   * The German monuments and museums the tiles have handed over so far.
   *
   * Both layers rebuild from their own sources when a phase lands, and both
   * clear their index to do it, so a merge that only wrote to the index would
   * be wiped by the next phase. They read this instead, and the tile effects
   * below write to it as the map moves.
   */
  const germanExtrasRef = useRef<{ monuments: Monument[]; museums: Museum[] }>({ monuments: [], museums: [] });
  const [monumentError, setMonumentError] = useState(false);

  // Building state (dynamic per bbox)
  const [buildingFeatures, setBuildingFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const buildingIndex = useRef<Map<string, ProtectedBuilding>>(new Map());
  const [buildingCount, setBuildingCount] = useState(0);
  const bldDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const bldLastBboxRef = useRef<BBox | null>(null);

  // Shipwreck state (viewport-based)
  const [shipwreckFeatures, setShipwreckFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const shipwreckIndex = useRef<Map<string, Shipwreck>>(new Map());
  const [shipwreckCount, setShipwreckCount] = useState(0);
  const wreckDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wreckLastBboxRef = useRef<BBox | null>(null);

  // Museum state (one-time fetch)
  const [museumFeatures, setMuseumFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const museumIndex = useRef<Map<string, Museum>>(new Map());
  const [museumCount, setMuseumCount] = useState(0);
  const [museumsLoaded, setMuseumsLoaded] = useState(false);

  // Rock Art state (viewport-based)
  const [rockArtFeatures, setRockArtFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const rockArtIndex = useRef<Map<string, RockArt>>(new Map());
  const [rockArtCount, setRockArtCount] = useState(0);
  const rockDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const rockLastBboxRef = useRef<BBox | null>(null);

  // UNESCO state (one-time fetch)
  const [unescoFeatures, setUnescoFeatures] = useState<GeoJSON.Feature[]>([]);
  const unescoIndex = useRef<Map<string, UnescoSite>>(new Map());
  const [unescoCount, setUnescoCount] = useState(0);
  const [unescoLoaded, setUnescoLoaded] = useState(false);

  // Industrial Heritage state (viewport-based)
  const [industrialFeatures, setIndustrialFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const industrialIndex = useRef<Map<string, IndustrialSite>>(new Map());
  const [industrialCount, setIndustrialCount] = useState(0);
  const indDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const indLastBboxRef = useRef<BBox | null>(null);

  // Barrow state (viewport-based)
  const [barrowFeatures, setBarrowFeatures] = useDebouncedState<GeoJSON.Feature[]>([]);
  const barrowIndex = useRef<Map<string, BarrowSite>>(new Map());
  const [barrowCount, setBarrowCount] = useState(0);
  const barDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const barLastBboxRef = useRef<BBox | null>(null);

  // Megalith state (one-time fetch)
  const [megalithFeatures, setMegalithFeatures] = useState<GeoJSON.Feature[]>([]);
  const megalithIndex = useRef<Map<string, MegalithSite>>(new Map());
  const [megalithCount, setMegalithCount] = useState(0);
  const [megalithsLoaded, setMegalithsLoaded] = useState(false);

  // Townscape state (one-time fetch + viewport-based English)
  const [townscapeFeatures, setTownscapeFeatures] = useState<GeoJSON.Feature[]>([]);
  const townscapeIndex = useRef<Map<string, TownscapeSite>>(new Map());
  const [townscapeCount, setTownscapeCount] = useState(0);
  const enTownLastBboxRef = useRef<BBox | null>(null);
  const enTownDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Plaque state (one-time fetch)
  const [plaqueFeatures, setPlaqueFeatures] = useState<GeoJSON.Feature[]>([]);
  const plaqueIndex = useRef<Map<string, PlaqueSite>>(new Map());
  const [plaqueCount, setPlaqueCount] = useState(0);

  // Stolperstein state (one-time fetch)
  const [stolpersteinFeatures, setStolpersteinFeatures] = useState<GeoJSON.Feature[]>([]);
  const stolpersteinIndex = useRef<Map<string, StolpersteinSite>>(new Map());
  const [stolpersteinCount, setStolpersteinCount] = useState(0);
  /**
   * Where the map is, for the layers that follow it.
   *
   * Eight layers are already viewport-driven through useFetchLayers. The rest
   * load a whole dataset the first time they are switched on, which is what a
   * default visit to Berlin paid 6.36 MB on the wire for on 5 Sep 2026, most
   * of it files about other countries. These two give the rest of them the
   * viewport without a re-render per pan: the ref always holds the real bbox,
   * and the key only changes when the centre crosses an eighth of a degree,
   * which is the tile size api/pointTiles.ts uses.
   */
  const viewBboxRef = useRef<BBox | null>(null);
  const [viewKey, setViewKey] = useState("");
  /**
   * Porten for havuhyrerne. Den er skilt fra `noteViewport` med vilje: halen
   * af `onLoad` kalder aldrig den, så en forside uden deeplink ville aldrig
   * åbne porten — målt 18/9-2026, hvor hverken en frisk eller en
   * tilbagevendende besøgende fik et eneste uhyre at se på www.fornland.com.
   * Og den spørger til zoom og ikke til bbox'ens bredde, fordi bredden
   * afhænger af skærmen: 2,5° er zoom 7 på en telefon og zoom 9 på en bred
   * skærm, så den samme port lukkede for tidligt det ene sted og for sent
   * det andet.
   */
  const [wideView, setWideView] = useState(false);
  const noteZoom = useCallback((zoom: number) => {
    const bredt = zoom < SEA_MONSTER_MAX_ZOOM;
    setWideView((prev) => (prev === bredt ? prev : bredt));
  }, []);
  const noteViewport = useCallback((bbox: BBox, lng: number, lat: number) => {
    viewBboxRef.current = bbox;
    const key = `${Math.round(lng * 8)}_${Math.round(lat * 8)}`;
    setViewKey((prev) => (prev === key ? prev : key));
  }, []);
  /**
   * Én aflæsning af hvor kameraet endte, for hver sti der kan afslutte en
   * kamerabevægelse. Der er fire af dem, og indtil 19/9-2026 noterede kun to
   * udsnittet: forsiden uden hash og enhver hash uden koordinat gik gennem
   * halen af `onLoad`, som hentede lag men aldrig skrev bbox'en. Så ventede
   * de femogtyve udsnitsdrevne effekter på en panorering der aldrig kom.
   * Målt i produktion: 358 pins mod 779 for det samme kamera nået ad et
   * deeplink, og /explore/runestones uden et eneste runestenslag for alle
   * andre end en førstegangsbesøgende fra et af syv lande.
   *
   * Den fjerde sti er den rå `moveend`-lytter nedenfor, som fandtes fordi
   * react-map-gls prop kan misse et event efter en flyvning. Den hentede lag
   * og lod `viewKey` stå forældet — samme hul, et andet sted.
   *
   * Sikker at lukke direkte ind i lytteren: `noteViewport` og `noteZoom` er
   * begge `useCallback(…, [])`, så `noteCamera` har samme identitet
   * komponenten ud. Det er derfor den ikke behøver en ref, sådan som
   * `fetchForLayersRef` lige nedenfor gør.
   */
  const noteCamera = useCallback((m: { getCenter(): { lng: number; lat: number }; getZoom(): number }, bbox: BBox) => {
    const c = m.getCenter();
    noteViewport(bbox, c.lng, c.lat);
    noteZoom(m.getZoom());
  }, [noteViewport, noteZoom]);

  // Lighthouse state (one-time fetch)
  const [lighthouseFeatures, setLighthouseFeatures] = useState<GeoJSON.Feature[]>([]);
  const lighthouseIndex = useRef<Map<string, HeritageSite>>(new Map());
  const [lighthouseCount, setLighthouseCount] = useState(0);
  const [lighthousesLoaded, setLighthousesLoaded] = useState(false);

  // Byleksikon state (Oslo Byleksikon map layer)
  const [byleksikonFeatures, setByleksikonFeatures] = useState<GeoJSON.Feature[]>([]);
  const byleksikonIndex = useRef<Map<string, ByleksikonSite>>(new Map());
  const [byleksikonCount, setByleksikonCount] = useState(0);
  const [byleksikonLoaded, setByleksikonLoaded] = useState(false);

  // Wikidata archaeological supplement for heritage layer (one-time fetch)
  const wikidataArchRef = useRef<HeritageSite[]>([]);
  const [wikidataArchLoaded, setWikidataArchLoaded] = useState(false);

  /**
   * De fire statiske heritage-tilskud manglede begge sine porte.
   *
   * De spurgte ikke om deres eget lag var tændt, så en læser på
   * /explore/runestones hentede roman-en.json og hillforts.json — 1 054 KiB
   * afkodet, målt 19/9-2026 — til et lag hun ikke kunne se. Og de havde intet
   * hent-én-gang-flag, så de `JSON.parse`'ede 708 kB om igen for hver
   * ottendedel grad kortet flyttede sig. Prisen dér er hovedtråden og ikke
   * ledningen: HTTP-cachen tager bytene, men parsningen og `.map`-ombygningen
   * af cache-arrayet kører hver gang.
   *
   * Flaget sættes *før* hentningen, så en byge af panoreringer ikke kapløber,
   * og ryddes i `catch`, så ét afbrudt svar ikke slukker laget sessionen ud.
   */
  // Doggerland heritage sites (submerged North Sea archaeology)
  const doggerHeritageCacheRef = useRef<HeritageSite[]>([]);
  /** Set once useFetchLayers has run (below); the static caches call it when they fill. */
  const mergeStaticHeritageRef = useRef<() => void>(() => {});
  const doggerLoadedRef = useRef(false);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // Doggerland er den ene undtagelse fra `showHeritage` alene: den lægger
    // også Storegga i battlefieldIndex nedenfor.
    if (!showHeritage && !showBattlefield) return;
    if (doggerLoadedRef.current || !wantsCountryFiles(bbox)) return;
    doggerLoadedRef.current = true;
    fetch("/data/doggerland.json").then(r => r.json()).then(data => {
      interface DoggerSite extends HeritageSite { typeEn?: string; descriptionEn?: string; wikipediaUrl?: string }
      doggerHeritageCacheRef.current = ((data.heritage ?? []) as DoggerSite[]).map((d) => ({
        ...d,
        type: lang !== "da" ? (d.typeEn || d.type) : d.type,
        description: lang !== "da" ? (d.descriptionEn || d.description) : d.description,
        sourceUrl: d.sourceUrl || d.wikipediaUrl || "",
      }));
      mergeStaticHeritageRef.current();
      // Also add Storegga event to battlefield index
      for (const evt of data.events ?? []) {
        if (lang === "en" && evt.descriptionEn) evt.description = evt.descriptionEn;
        if (lang === "en" && evt.nameEn) evt.name = evt.nameEn;
        battlefieldIndex.current.set(evt.id, evt);
      }
    }).catch(() => { doggerLoadedRef.current = false; });
  }, [showHeritage, showBattlefield, viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // UK/IE static datasets (roman, hillforts, hoards) total ~1.1 MB. Most
  // visitors load a Scandinavian map view and never see British Isles
  // pins, so defer their download to an idle frame. The cache feeds into
  // useFetchLayers' heritage merge, which only includes UK pins inside
  // the current viewport bbox — so a late arrival from idle simply means
  // the first heritage merge runs without the UK supplement and the
  // next viewport change picks it up.
  const idle = (cb: () => void) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(cb, { timeout: 5_000 });
    } else {
      setTimeout(cb, 2_000);
    }
  };

  /**
   * Four caches that feed the heritage layer used to load on mount, for every
   * visitor, wherever they were: Roman Britain (111 kB), the hillfort atlas
   * (102 kB), the hoards and Doggerland. `idle()` kept them off the critical
   * path but they were still fetched by somebody looking at Finland. Each one
   * now waits for a viewport small enough to be worth a country file and, for
   * the three that are about one place, for a viewport over that place.
   */
  const romanCacheRef = useRef<HeritageSite[]>([]);
  const romanLoadedRef = useRef(false);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // Britain and not the British Isles: GB_BOUNDS reaches into Ireland, where
    // the layer has no records at all. Scotland is in, though — 568 of the
    // 2 727 are, from the Antonine Wall north (scripts/build-roman-en.mjs).
    if (!showHeritage || romanLoadedRef.current) return;
    if (!bbox || !wantsCountryFiles(bbox) || !bboxOverlapsAny(bbox, UK_BOXES)) return;
    romanLoadedRef.current = true;
    idle(() => {
      fetch("/data/roman-en.json").then(r => r.json()).then((data: HeritageSite[]) => {
        romanCacheRef.current = data.map((d) => ({
          ...d,
          sourceUrl: d.sourceUrl || "",
        }));
        mergeStaticHeritageRef.current();
      }).catch(() => { romanLoadedRef.current = false; });
    });
  }, [showHeritage, viewKey]);

  // Atlas of Hillforts of Britain and Ireland (4,147 hillforts, compact format)
  const hillfortCacheRef = useRef<HeritageSite[]>([]);
  const hillfortLoadedRef = useRef(false);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    if (!showHeritage || hillfortLoadedRef.current) return;
    if (!bbox || !wantsCountryFiles(bbox) || !(bboxOverlaps(bbox, GB_BOUNDS) || bboxOverlaps(bbox, IE_BOUNDS))) return;
    hillfortLoadedRef.current = true;
    idle(() => {
    type HillfortRow = [string, number, number, string, string, string, string | number, string];
    fetch("/data/hillforts.json").then(r => r.json()).then((data: HillfortRow[]) => {
      // Compact format: [name, lat, lng, country, type, period, atlasNum, qid]
      // Descriptions loaded on-demand from hillfort-descriptions.json (4.4MB saved at startup)
      hillfortCacheRef.current = data.map((d) => ({
        id: `hf-${d[6]}`,
        name: d[0],
        lat: d[1],
        lng: d[2],
        country: d[3] as Country,
        type: d[4],
        period: d[5],
        sourceUrl: d[7] ? `https://www.wikidata.org/wiki/${d[7]}` : `https://doi.org/10.5284/1118744`,
        wikidataId: d[7] || undefined,
      } as HeritageSite));
      mergeStaticHeritageRef.current();
    }).catch(() => { hillfortLoadedRef.current = false; });
    });
  }, [showHeritage, viewKey]);  

  // Viking hoards and treasure finds (66 hoards from Wikidata, static)
  const hoardCacheRef = useRef<HeritageSite[]>([]);
  const hoardLoadedRef = useRef(false);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    if (!showHeritage || hoardLoadedRef.current) return;
    if (!wantsCountryFiles(bbox)) return;
    hoardLoadedRef.current = true;
    idle(() => {
    interface HoardRecord { id: string; name: string; lat: number; lng: number; country?: Country; date?: string; wikipediaUrl?: string; wikidataId?: string }
    fetch("/data/hoards.json").then(r => r.json()).then((data: HoardRecord[]) => {
      hoardCacheRef.current = data.map((d) => ({
        ...d,
        country: d.country || determineCountry(d.lat, d.lng),
        type: "Hoard",
        period: d.date && +d.date.slice(0, 4) < 400 ? "Romertid" : d.date && +d.date.slice(0, 4) < 800 ? "Tidlig middelalder" : "Vikingetid",
        sourceUrl: d.wikipediaUrl || `https://www.wikidata.org/wiki/${d.wikidataId}`,
      } as HeritageSite));
      mergeStaticHeritageRef.current();
    }).catch(() => { hoardLoadedRef.current = false; });
    });
  }, [showHeritage, viewKey]);

  /**
   * Wikidata's 28 143 wrecks, one viewport at a time.
   *
   * One file of 475 kB on the wire, fetched the moment the layer was on,
   * about every coast from Ireland to the Gulf of Finland. They are tiles now
   * (`public/data/wrecks-wd/`, a quarter degree, written by
   * scripts/tile-static-sets.mjs) because wrecks follow coastlines: the cells
   * that exist are long and thin and a city view touches two or three.
   *
   * The cache accumulates, like every tiled layer here: sailing along a coast
   * keeps what is behind you rather than re-fetching it on the way back.
   */
  const wdWreckCacheRef = useRef<Shipwreck[]>([]);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    if (!showShipwreck || !bbox || !wantsCountryFiles(bbox)) return;
    let cancelled = false;
    import("./api/pointTiles.ts").then(({ loadPointTiles }) => loadPointTiles("wrecks-wd", bbox)).then((recs) => {
      if (cancelled || !recs.length) return;
      const seen = new Set(wdWreckCacheRef.current.map((w) => w.id));
      const fresh: Shipwreck[] = [];
      for (const r of recs) {
        const [qid, lng, lat, name, image] = r as [string, number, number, string, string];
        if (lng < bbox.minLng || lng > bbox.maxLng || lat < bbox.minLat || lat > bbox.maxLat) continue;
        const id = `wd-wreck-${qid}`;
        if (seen.has(id)) continue;
        seen.add(id);
        fresh.push({
          id, name, type: "Shipwreck", country: determineCountry(lat, lng), lat, lng,
          wikidataId: qid, sourceUrl: `https://www.wikidata.org/wiki/${qid}`,
          ...(image ? { image } : {}),
        } as Shipwreck);
      }
      if (!fresh.length) return;
      wdWreckCacheRef.current = [...wdWreckCacheRef.current, ...fresh];
      // Only the cache is written here. useFetchLayers owns the wreck layer's
      // features and merges this ref on every pass, and it is the half that
      // drops inland wrecks and the ones a heritage pin already covers; two
      // writers would put those back.
    }).catch((e) => warnFetch("Wikidata wrecks failed:", e));
    return () => { cancelled = true; };
  }, [showShipwreck, viewKey]);  

  // Thing Site state (one-time fetch)
  const [thingsiteFeatures, setThingsiteFeatures] = useState<GeoJSON.Feature[]>([]);
  const thingsiteIndex = useRef<Map<string, ThingSite>>(new Map());
  const [thingsiteCount, setThingsiteCount] = useState(0);
  const [thingsitesLoaded, setThingsitesLoaded] = useState(false);

  // Route state (one-time fetch)
  const [routeGeoJSON, setRouteGeoJSON] = useState<GeoJSON.FeatureCollection>({ type: "FeatureCollection", features: [] });
  const routeIndex = useRef<Map<string, HistoricRoute>>(new Map());
  const [routeCount, setRouteCount] = useState(0);
  const [routesLoaded, setRoutesLoaded] = useState(false);

  // Curated walk state (one-time fetch of a small static file).
  //
  // Two pin layers come out of this. The walk pins are the layer proper, one
  // per walk, and they behave like any other. The stop pins are not a layer at
  // all: they exist only while a walk is open, and disappear with it, so they
  // have no index, no count and no tab. `activeStopId` is which stop the reader
  // is at, and it is also what `#stop=` names.
  const [walkGeoJSON, setWalkGeoJSON] = useState<GeoJSON.FeatureCollection>({ type: "FeatureCollection", features: [] });
  const walkIndex = useRef<Map<string, CuratedWalk>>(new Map());
  const [walkCount, setWalkCount] = useState(0);
  const [activeStopId, setActiveStopId] = useState<string | null>(null);
  // The walk sheet collapsed to WalkModeBar. This exists because the sheet's
  // modal backdrop blocks the map: without it, seeing the map mid-walk meant
  // closing the walk, which also wipes the `#walk=` resume point. The walk
  // stays `selected` while this is true — only the chrome changes.
  const [walkMapView, setWalkMapView] = useState(false);

  // --- SEARCH (extracted to useSearch hook) ---
  // All twenty are useRef handles, so the dependency list can never fire and
  // this object is built exactly once. Listing them anyway rather than passing
  // [] keeps exhaustive-deps quiet without a suppression to explain later.
  //
  // Written inline — which it was until now — it became a fresh literal on
  // every render, churning searchLocal's identity and re-running the pan effect
  // in useSearch.ts:346. Measured with a query open and the map settled: 15
  // full index sweeps per 3 idle seconds, 0 after this change. It did not
  // remount the result rows, though (probed: same DOM node after 3s), so this
  // is wasted work and not the search flake it was suspected of being.
  const searchIndexes = useMemo(() => ({
    folkloreIndex, runestoneIndex, battlefieldIndex, monumentIndex,
    buildingIndex, shipwreckIndex, museumIndex, rockArtIndex,
    unescoIndex, industrialIndex, barrowIndex, megalithIndex,
    townscapeIndex, plaqueIndex, stolpersteinIndex, lighthouseIndex,
    thingsiteIndex, routeIndex, walkIndex, byleksikonIndex,
  }), [
    folkloreIndex, runestoneIndex, battlefieldIndex, monumentIndex,
    buildingIndex, shipwreckIndex, museumIndex, rockArtIndex,
    unescoIndex, industrialIndex, barrowIndex, megalithIndex,
    townscapeIndex, plaqueIndex, stolpersteinIndex, lighthouseIndex,
    thingsiteIndex, routeIndex, walkIndex, byleksikonIndex,
  ]);

  const {
    searchOpen, setSearchOpen, searchQuery, setSearchQuery,
    searchResults, setSearchResults, searchLoading, setSearchLoading,
    searchTrigger: _searchTrigger, setSearchTrigger,
    searchInputRef, searchDebounceRef, activeSearchQueryRef, searchPanTimerRef,
    searchUnified, handleSearchSelect, handleSearchSelectRef,
  } = useSearch({
    heritageFeatures,
    indexes: searchIndexes,
    mapRef,
    lang,
  });

  // Arrow-key position in the search dropdown. -1 means nothing highlighted,
  // and Enter then falls back to the first result as it always has. The flat
  // list must mirror the rendered order exactly: locals grouped by layer in
  // first-seen order, then places.
  const [searchActiveIdx, setSearchActiveIdx] = useState(-1);
  const searchFlat = useMemo(() => {
    const grouped = new Map<string, UnifiedSearchResult[]>();
    for (const r of searchResults.local) {
      const arr = grouped.get(r.layerType) || [];
      arr.push(r);
      grouped.set(r.layerType, arr);
    }
    return [...(searchResults.ask ? [searchResults.ask] : []), ...Array.from(grouped.values()).flat(), ...searchResults.places];
  }, [searchResults]);
  useEffect(() => { setSearchActiveIdx(-1); }, [searchResults]);
  const searchActiveId = searchFlat[searchActiveIdx]?.id;

  // Loading indicators (pulsing dot per tab)
  const [loadingLayers, setLoadingLayers] = useState<Set<ActiveTab>>(new Set());
  const addLoading = useCallback((tab: ActiveTab) =>
    setLoadingLayers(s => new Set(s).add(tab)), []);
  const removeLoading = useCallback((tab: ActiveTab) =>
    setLoadingLayers(s => { const n = new Set(s); n.delete(tab); return n; }), []);

  // --- GEOJSON MEMOS (delegated to useGeoJsonLayers hook) ---
  const showBuildingPoints = showBuilding && (activeMonumentCat === "all" || activeMonumentCat === "protected");
  const visitedIdSet = useMemo(() => new Set(Object.keys(userData.visited)), [userData.visited]);
  const savedIdSet = useMemo(() => new Set(userData.saved), [userData.saved]);

  const ghostShow = useMemo(() => ({ heritage: showHeritage, barrow: showBarrow, rockart: showRockArt, industrial: showIndustrial, shipwreck: showShipwreck, building: showBuildingPoints, runestone: showRunestone, battlefield: showBattlefield, megalith: showMegalith, stolperstein: showStolperstein, lighthouse: showLighthouse, monument: showMonument }),
    [showHeritage, showBarrow, showRockArt, showIndustrial, showShipwreck, showBuildingPoints, showRunestone, showBattlefield, showMegalith, showStolperstein, showLighthouse, showMonument]);
  const siteDates = useSiteDates(timelineYear != null);
  // The lens shows a town plan from a named year, so it has nothing to say to
  // a reader standing before it. Offering an 1868 Kristiania in the Bronze Age
  // was the timeline and the lens talking past each other.
  const lensOffered = !!lensSheet && !histMap && (timelineYear == null || timelineYear >= sheetFromYear(lensSheet));
  /**
   * Hvilket blad linsen viser, og hvad der staar paa skiven.
   *
   * Memoiseret fordi `mapStyle` er en reference: en ny ved hver render ville
   * faa react-map-gl til at bygge linsens kort om, og den bygger et helt
   * MapLibre-kort. Stockholm er den eneste stak hvor den afhaenger af
   * aarstallet; resten svarer det samme uanset.
   */
  const lensView = useMemo(
    () => (lensSheet ? pastSheetView(lensSheet, timelineYear) : null),
    [lensSheet, timelineYear],
  );
  // The bar's own year is instant; the year that re-splits twelve layers is
  // deferred, so dragging the handle stays smooth on a phone and the work
  // happens once the finger settles. Without this every step of a drag
  // rebuilt every period-bearing source synchronously, 32 ms a step measured
  // on a warm Mac with one layer, and far worse with the map full.
  const splitYear = useDeferredValue(timelineYear);
  // Every dated span on the map, for the timeline's histogram. Only while the
  // bar is open, so a closed bar costs nothing on each fetch.
  const timelineSpans = useMemo<Array<[number, number]>>(() => {
    if (timelineYear == null) return [];
    const out: Array<[number, number]> = [];
    for (const arr of [heritageFeatures, barrowFeatures, rockArtFeatures, industrialFeatures, shipwreckFeatures, buildingFeatures, runestoneFeatures, battlefieldFeatures, megalithFeatures, stolpersteinFeatures, lighthouseFeatures, monumentFeatures]) {
      for (const f of arr) { const p = f.properties; if (typeof p?.y0 === "number" && typeof p?.y1 === "number") out.push([p.y0, p.y1]); }
    }
    return out;
  }, [timelineYear == null, heritageFeatures, barrowFeatures, rockArtFeatures, industrialFeatures, shipwreckFeatures, buildingFeatures, runestoneFeatures, battlefieldFeatures, megalithFeatures, stolpersteinFeatures, lighthouseFeatures, monumentFeatures]); // eslint-disable-line react-hooks/exhaustive-deps

  // The named moments, from the hand-dated table crossed with whatever is
  // loaded. Only heritage carries such ids today; the others are passed in
  // so a table for a barrow or a wreck needs no change here.
  const timelineStops = useMemo(() => timelineYear == null ? []
    : stopsFor([...heritageFeatures, ...barrowFeatures, ...megalithFeatures, ...monumentFeatures], siteDates),
    [timelineYear == null, heritageFeatures, barrowFeatures, megalithFeatures, monumentFeatures, siteDates]); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    heritageGeoJSON, folkloreGeoJSON, runestoneGeoJSON, battlefieldGeoJSON,
    monumentGeoJSON, buildingGeoJSON, shipwreckGeoJSON, museumGeoJSON,
    rockArtGeoJSON, unescoGeoJSON, industrialGeoJSON, barrowGeoJSON,
    megalithGeoJSON, townscapeGeoJSON, plaqueGeoJSON, stolpersteinGeoJSON,
    lighthouseGeoJSON, thingsiteGeoJSON, byleksikonGeoJSON,
    visitedOverlayGeoJSON, savedOverlayGeoJSON, ghostGeoJSON,
  } = useGeoJsonLayers({
    heritageFeatures, folkloreFeatures, runestoneFeatures, battlefieldFeatures,
    monumentFeatures, buildingFeatures, shipwreckFeatures, museumFeatures,
    rockArtFeatures, unescoFeatures, industrialFeatures, barrowFeatures,
    megalithFeatures, townscapeFeatures, plaqueFeatures, stolpersteinFeatures,
    lighthouseFeatures, thingsiteFeatures, byleksikonFeatures,
    activePeriod, activeFolkloreCat, activeMonumentCat, showBuildingPoints,
    timelineYear: splitYear, ghostShow, siteDates,
    folkloreIndex,
    visitedIdSet, savedIdSet,
  });
  const folkloreEmptyHere = useMemo(() => {
    const b = viewBboxRef.current;
    if (activeGroup !== "folklore_group" || !b || folkloreCounts === 0 || loadingLayers.has("folklore")) return false;
    for (const f of (folkloreGeoJSON?.features ?? [])) {
      const [x, y] = (f.geometry as GeoJSON.Point).coordinates;
      if (x >= b.minLng && x <= b.maxLng && y >= b.minLat && y <= b.maxLat) return false;
    }
    return true;
  }, [viewKey, activeGroup, folkloreCounts, loadingLayers, folkloreGeoJSON]); // eslint-disable-line react-hooks/exhaustive-deps

  // routeGeoJSON is already a FeatureCollection state (line geometry from fetch)

  // --- ENRICHMENT ---
  const { enrichment, enrichLoading } = useEnrichment({
    selected,
    selectedType,
    lang,
    enrichmentCache,
    heritageFeatures,
    mapRef,
    folkloreIndex,
    battlefieldIndex,
    monumentIndex,
    runestoneIndex,
    barrowIndex,
    megalithIndex,
    addRecentlyViewed,
  });

  // --- LOAD STATIC DATA ON TAB ACTIVATION ---
  useEffect(() => {
    if (showRunestone && !runesLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setRunesLoaded(true);
      addLoading("runestone");
      Promise.all([
        fetchRunestones(),
        // Ogham and Pictish stones, 31 kB about Britain and Ireland, which a
        // reader in Oslo was handed along with the runestones.
        (bboxOverlaps(viewBboxRef.current!, GB_BOUNDS) || bboxOverlaps(viewBboxRef.current!, IE_BOUNDS))
          ? fetch("/data/inscribed-stones.json").then(r => r.json()).catch(() => [])
          : Promise.resolve([]),
      ]).then(([osmStones, inscribed]) => {
        // Merge OSM runestones + Wikidata Ogham/Pictish stones
        const allStones = [...osmStones];
        const existingCoords = new Set(osmStones.map((s: RuneStone) => `${s.lat.toFixed(3)},${s.lng.toFixed(3)}`));
        // Compact format: [name, lat, lng, type, qid, inscription, image, wikiUrl]
        for (const d of inscribed) {
          const key = `${d[1].toFixed(3)},${d[2].toFixed(3)}`;
          if (existingCoords.has(key)) continue;
          existingCoords.add(key);
          allStones.push({
            id: `wd-stone-${d[4]}`, name: d[0], lat: d[1], lng: d[2],
            country: determineCountry(d[1], d[2]),
            inscription: d[5] || undefined, wikidataId: d[4],
            wikipediaUrl: d[7] || undefined,
            type: d[3],  // "Ogham stone" or "Pictish stone"
          } as RuneStone);
        }
        allStones.forEach((s) => runestoneIndex.current.set(s.id, s));
        setRunestoneFeatures(toSimpleGeoJSON(allStones.map((st) => ({ ...st, period: st.type ? undefined : "Vikingetid", years: runestoneYears(st.type, st.country) })), "runestone").features);
        setRunestoneCount(allStones.length);
        removeLoading("runestone");
      }).catch((e) => { console.warn("Failed to load runestones:", e); removeLoading("runestone"); });
    }
  }, [showRunestone, runesLoaded, viewKey]);

  useEffect(() => {
    if (showBattlefield && !battlesLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setBattlesLoaded(true);
      addLoading("battlefield");
      /**
       * De kuraterede konflikter laa som fase 4 i en kaede der begynder med
       * Overpass, og en kaede er ikke en raekkefoelge man kan vente paa:
       * maalt 21/9-2026 svarede overpass-api.de 429 og WDQS fejlede paa CORS,
       * og de 146 kuraterede slagmarker naaede aldrig indekset. Deeplinket
       * opgiver foer da, saa panelet aabnede paa nul af seks proevede.
       *
       * Filen er statisk og ligger paa vores egen server. Den hentes derfor
       * nu ved siden af kaeden og ikke bagefter, og fletningen sker hver gang
       * noget lander, uanset i hvilken raekkefoelge. Kuraterede poster
       * erstatter stadig dubletter, for de er rigere.
       */
      let allItems: HistoricalEvent[] = [];
      let curatedItems: HistoricalEvent[] = [];
      const flet = (items: HistoricalEvent[]): HistoricalEvent[] => {
        if (!curatedItems.length) return items;
        let ud = items;
        for (const c of curatedItems) {
          const coordKey = `${c.lng.toFixed(2)},${c.lat.toFixed(2)}`;
          ud = ud.filter(b => {
            if (c.wikidataId && b.wikidataId === c.wikidataId) return false;
            return `${b.lng.toFixed(2)},${b.lat.toFixed(2)}` !== coordKey;
          });
        }
        return [...ud, ...curatedItems];
      };
      const applyBattles = (items: HistoricalEvent[]) => {
        const alle = flet(items);
        battlefieldIndex.current.clear();
        alle.forEach(b => battlefieldIndex.current.set(b.id, b));
        setBattlefieldFeatures(toSimpleGeoJSON(alle.map((b) => ({ ...b, years: eventYears(b.date) })), "battlefield").features);
        setBattlefieldCount(alle.length);
      };
      import("./api/curated.ts").then(({ loadCuratedConflicts }) => loadCuratedConflicts()).then((curated) => {
        curatedItems = curated;
        applyBattles(allItems);
      }).catch(e => console.warn("Failed to load curated conflicts:", e));
      // Phase 1: OSM battlefields (fast, cached)
      fetchBattlefields().then((osmBattles) => {
        allItems = osmBattles;
        applyBattles(allItems);
        // Phase 2: Wikidata battles (supplement)
        return fetchWikidataBattles().then(async (wdBattles) => {
          const { deduplicateBattles } = await loadOverpass();
          const newBattles = deduplicateBattles(wdBattles, osmBattles);
          allItems = [...osmBattles, ...newBattles];
          applyBattles(allItems);
        }).catch(e => console.warn("Failed to load Wikidata battles:", e));
      }).then(() => {
        // Phase 3: Wikidata conflict events (execution sites, camps, massacres, etc.)
        return import("./api/wikidata.ts").then(({ fetchWikidataHistoricalEvents }) => fetchWikidataHistoricalEvents()).then((events) => {
          const existingCoords = new Set(flet(allItems).map(b => `${b.lng.toFixed(3)},${b.lat.toFixed(3)}`));
          const existingQids = new Set(flet(allItems).filter(b => b.wikidataId).map(b => b.wikidataId!));
          const newEvents = events.filter(e => {
            if (e.wikidataId && existingQids.has(e.wikidataId)) return false;
            return !existingCoords.has(`${e.lng.toFixed(3)},${e.lat.toFixed(3)}`);
          });
          if (newEvents.length > 0) {
            allItems = [...allItems, ...newEvents];
            applyBattles(allItems);
          }
        }).catch(e => console.warn("Failed to load conflict events:", e));
      }).then(() => {
        // Phase 3b: Pre-fetched fortifications (bunkers, coastal batteries — static JSON)
        return fetch("/data/fortifications.json").then(r => r.json()).then((forts: HistoricalEvent[]) => {
          const existingQids = new Set(flet(allItems).filter(b => b.wikidataId).map(b => b.wikidataId!));
          const newForts = forts.filter((f) => !existingQids.has(f.wikidataId ?? "")).map((f) => ({
            ...f, country: determineCountry(f.lat, f.lng), eventCategory: "fortification" as const,
          }));
          if (newForts.length > 0) {
            allItems = [...allItems, ...newForts];
            applyBattles(allItems);
          }
        }).catch(e => console.warn("Failed to load fortifications:", e));
      }).then(() => {
        // Phase 4 var her: de kuraterede konflikter. De hentes nu ved siden af
        // kaeden, se flet() ovenfor.
        // Phase 5: NI battlesites (HED FeatureServer) — fetch all, not viewport-filtered
        return import("./api/northernireland.ts").then(({ fetchNIBattlesites }) => fetchNIBattlesites({ minLng: -8.2, minLat: 54.0, maxLng: -5.4, maxLat: 55.4 })).then((niBattles) => {
          const existingCoords = new Set(flet(allItems).map(b => `${b.lng.toFixed(3)},${b.lat.toFixed(3)}`));
          const newItems = niBattles.filter(b => !existingCoords.has(`${b.lng.toFixed(3)},${b.lat.toFixed(3)}`));
          if (newItems.length > 0) {
            allItems = [...allItems, ...newItems];
            applyBattles(allItems);
          }
        }).catch(e => console.warn("Failed to load NI battlesites:", e));
      }).catch((e) => { console.warn("Failed to load battlefields:", e); })
        .finally(() => removeLoading("battlefield"));
    }
  }, [showBattlefield, battlesLoaded, viewKey]);

  /**
   * The fourteen countries whose churches the app knows, with the box each
   * one's records lie in. One table, because it was two: the preloader below
   * filtered by viewport from 12 Sep 2026 and the monument effect above did
   * not, so a reader in Copenhagen with the monument layer on downloaded
   * 2,74 MB of British churches, 313 kB of Irish and 54 kB of Manx to draw
   * Danish ones. Measured 18 Sep 2026, 579 kB of it on the wire.
   */
  const CHURCH_COUNTRIES = useMemo((): [string, string, { minLng: number; maxLng: number; minLat: number; maxLat: number }[]][] => [
    ["DK", "fornland-churches-dk-v3", [DK_BOUNDS]], ["NO", "fornland-churches-no-v3", [NO_BOUNDS]],
    ["SE", "fornland-churches-se-v3", [SE_BOUNDS]], ["FI", "fornland-churches-fi-v3", [FI_BOUNDS]],
    ["IS", "fornland-churches-is-v3", [IS_BOUNDS]], ["GL", "fornland-churches-gl-v3", [GL_BOUNDS]],
    ["FO", "fornland-churches-fo-v3", [FO_BOUNDS]],
    // The United Kingdom and not GB_BOUNDS, which reaches 7,79° W and would
    // hand 15 320 British churches to a reader in Dublin. The file does hold
    // Northern Ireland, 49 of them, so the box is UK_BOXES and not Britain.
    ["GB", "fornland-churches-gb-v1", UK_BOXES],
    ["IE", "fornland-churches-ie-v1", [IE_BOUNDS]], ["IM", "fornland-churches-im-v1", [IM_BOUNDS]],
    ["EE", "fornland-churches-ee-v1", [EE_BOUNDS]], ["LV", "fornland-churches-lv-v1", [LV_BOUNDS]],
    ["LT", "fornland-churches-lt-v1", [LT_BOUNDS]], ["NL", "fornland-churches-nl-v1", [NL_BOUNDS]],
  ], []);

  /**
   * Monuments, and then the churches of the countries in view.
   *
   * Phase 2 used to ask for all fourteen countries whatever the map showed,
   * which is how a reader in Copenhagen downloaded 2,74 MB of British
   * churches, 313 kB of Irish and 54 kB of Manx. The preloader below was
   * given a viewport filter on 12 Sep 2026; this one was not.
   *
   * It cannot simply be filtered and left, because it ran once a session: a
   * reader who then panned to Britain would have no churches at all. So the
   * countries already fetched are remembered, and a pan that brings a new one
   * into view asks for that one and no more.
   */
  const historicMonumentsRef = useRef<Monument[]>([]);
  const churchesRef = useRef<Monument[]>([]);
  const churchCountriesDoneRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const bbox = viewBboxRef.current;
    if (!showMonument || !bbox || !wantsCountryFiles(bbox)) return;
    const wanted = CHURCH_COUNTRIES.filter(([code, , boxes]) => bboxOverlapsAny(bbox, boxes) && !churchCountriesDoneRef.current.has(code));
    const needHistoric = !monumentsLoaded;
    if (!needHistoric && !wanted.length) return;
    for (const [code] of wanted) churchCountriesDoneRef.current.add(code);
    addLoading("monument");
      const applyMonuments = (items: Monument[]) => {
        monumentsRawRef.current = items;
        const known = new Set(items.map(m => m.id));
        const withGermany = germanExtrasRef.current.monuments.length
          ? [...items, ...germanExtrasRef.current.monuments.filter(m => !known.has(m.id))]
          : items;
        const deduped = deduplicateMonuments(withGermany, heritageFeatures);
        monumentIndex.current.clear();
        deduped.forEach(m => monumentIndex.current.set(m.id, m));
        setMonumentFeatures(toSimpleGeoJSON(deduped.map((m) => ({ ...m, dating: m.startDate })), "monument").features);
        setMonumentCount(deduped.length);
      };
      const applyAll = () => applyMonuments([...historicMonumentsRef.current, ...churchesRef.current]);
      const loadChurches = () => {
        if (!wanted.length) { removeLoading("monument"); return; }
        let done = 0;
        for (const [code, key] of wanted) {
          fetchChurchesByCountry(code, key)
            .then((churches) => { churchesRef.current.push(...churches); applyAll(); })
            .catch((e) => console.warn("Church load failed:", e))
            .finally(() => { done++; if (done === wanted.length) removeLoading("monument"); });
        }
      };
      if (!needHistoric) { loadChurches(); return; }
      setMonumentsLoaded(true);
      setMonumentError(false);
      fetchHistoricMonuments({ gb: bboxOverlaps(bbox, GB_BOUNDS) }).then((historic) => {
        historicMonumentsRef.current = historic;
        applyAll();
        loadChurches();
      }).catch((e) => { console.warn("Failed to load monuments:", e); setMonumentError(true); removeLoading("monument"); });
  }, [showMonument, monumentsLoaded, viewKey, CHURCH_COUNTRIES]);

  useEffect(() => {
    if (showFolklore && !danishLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setDanishLoaded(true);
      addLoading("folklore");
      import("./api/folklore.ts")
        .then(({ loadDanishFolklore }) => (bboxOverlaps(viewBboxRef.current!, DK_BOUNDS) ? loadDanishFolklore() : []))
        .then((records) => {
        records.forEach((r) => {
          folkloreIndex.current.set(r.id, r);
          const ck = `${r.lat},${r.lng}`;
          const arr = folkloreByCoord.current.get(ck) ?? [];
          if (!isFolkloreDuplicate(arr, r)) arr.push(r);
          folkloreByCoord.current.set(ck, arr);
        });
        const newFeatures = records.map((r) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [r.lng, r.lat] },
          properties: { id: r.id, name: r.name, layerType: "folklore", category: r.category, country: r.country },
        }));
        setFolkloreFeatures((prev) => [...prev, ...newFeatures]);
        setFolkloreCounts((prev) => prev + records.length);
        removeLoading("folklore");
      }).catch((e) => { console.warn("Failed to load Danish folklore:", e); removeLoading("folklore"); });
    }
  }, [showFolklore, danishLoaded, viewKey]);

  // Load Icelandic folklore (DataARC sagas) + British, Irish, Greenlandic, Finnish
  useEffect(() => {
    if (showFolklore && !icelandicFolkloreLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setIcelandicFolkloreLoaded(true);
      const addRecords = (records: FolkloreRecord[]) => {
        records.forEach((r) => {
          folkloreIndex.current.set(r.id, r);
          const ck = `${r.lat},${r.lng}`;
          const arr = folkloreByCoord.current.get(ck) ?? [];
          if (!isFolkloreDuplicate(arr, r)) arr.push(r);
          folkloreByCoord.current.set(ck, arr);
        });
        const newFeatures = records.map((r) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [r.lng, r.lat] },
          properties: { id: r.id, name: r.name, layerType: "folklore", category: r.category, country: r.country },
        }));
        setFolkloreFeatures((prev) => [...prev, ...newFeatures]);
        setFolkloreCounts((prev) => prev + records.length);
      };
      // Each collection is about one country, and each was fetched wherever
      // the reader stood: a map over Denmark pulled the British, Irish,
      // Estonian, Greenlandic and Finnish files. Norse World is the one that
      // is genuinely everywhere, from Vinland to Jerusalem, so it has no box.
      const over = (box: { minLng: number; maxLng: number; minLat: number; maxLat: number }) =>
        bboxOverlaps(viewBboxRef.current!, box);
      import("./api/folklore.ts").then(({ loadBritishFolklore, loadIrishMythology, loadGreenlandicFolklore, loadIcelandicSagas, loadFinnishFolklore, loadNorseWorld, loadEstonianSacredSites }) => {
        if (over(GB_BOUNDS)) loadBritishFolklore().then(addRecords).catch((e) => console.warn("British folklore failed:", e));
        if (over(IE_BOUNDS)) loadIrishMythology().then(addRecords).catch((e) => console.warn("Irish mythology failed:", e));
        loadNorseWorld().then(addRecords).catch((e) => console.warn("Norse World failed:", e));
        if (over(GL_BOUNDS)) loadGreenlandicFolklore().then(addRecords).catch((e) => console.warn("Greenlandic folklore failed:", e));
        if (over(EE_BOUNDS)) loadEstonianSacredSites().then(addRecords).catch((e) => console.warn("Estonian sacred sites failed:", e));
        if (over(IS_BOUNDS)) loadIcelandicSagas().then(addRecords).catch((e) => console.warn("Icelandic sagas failed:", e));
        // Defer large folklore file (load after 3s to keep UI responsive)
        if (over(FI_FOLKLORE_BOUNDS)) setTimeout(() => {
          loadFinnishFolklore().then(addRecords).catch((e) => console.warn("Finnish folklore failed:", e));
        }, 3000);
      });
    }
  }, [showFolklore, icelandicFolkloreLoaded, viewKey]);

  /**
   * Load Sagnagrunnur Icelandic folklore (~2.9 MB) — viewport-gated to
   * Iceland/Faroe Islands.
   *
   * `viewKey` staar i afhaengighederne fordi effekten ellers koerte netop én
   * gang, i det oejeblik folklore-fanen blev taendt. Paa et deeplink sker det
   * foer kortet findes, saa `if (!m) return` slog til, og intet abonnement
   * paa `moveend` blev nogensinde oprettet. Kameraet saettes desuden gennem
   * `initialViewState` paa den vej, saa der kommer heller aldrig et
   * `moveend`. Maalt 21/9-2026: /s/folklore/4950 ligger paa 66,20°N
   * -15,33°Ø, og siden hentede greenland-folklore.json men aldrig
   * icelandic-folklore-pins.json. 9.078 af de 10.111 folklore-adresser i
   * sitemappen er islandske, og panelet kunne ikke aabne paa nogen af dem.
   */
  useEffect(() => {
    // viewport-gate-exempt: porten er IS_BOUNDS/FO_BOUNDS i checkAndLoad og
    // ikke wantsCountryFiles. Saettet er ét lands og hoerer kun hjemme over
    // det land, hvilket er strengere end arealporten.
    if (!showFolklore || sagnagrunnurLoaded) return;
    const m = mapRef.current?.getMap();
    if (!m) return;

    const checkAndLoad = () => {
      if (sagnagrunnurLoadedRef.current) return;
      const b = m.getBounds();
      const vp = { minLng: b.getWest(), maxLng: b.getEast(), minLat: b.getSouth(), maxLat: b.getNorth() };
      const overlapsIS = vp.maxLng >= IS_BOUNDS.minLng && vp.minLng <= IS_BOUNDS.maxLng && vp.maxLat >= IS_BOUNDS.minLat && vp.minLat <= IS_BOUNDS.maxLat;
      const overlapsFO = vp.maxLng >= FO_BOUNDS.minLng && vp.minLng <= FO_BOUNDS.maxLng && vp.maxLat >= FO_BOUNDS.minLat && vp.minLat <= FO_BOUNDS.maxLat;
      // Nordøerne: 1.449 af Sagnagrunnurs sagn er fra Shetland og Orkney.
      // Se SHETLAND_BOUNDS i constants for målingen.
      const overlapsNord = bboxOverlaps(vp, SHETLAND_BOUNDS) || bboxOverlaps(vp, ORKNEY_BOUNDS);
      if (!overlapsIS && !overlapsFO && !overlapsNord) return;
      sagnagrunnurLoadedRef.current = true;
      setSagnagrunnurLoaded(true);
      const addRecords = (records: FolkloreRecord[]) => {
        records.forEach((r) => {
          folkloreIndex.current.set(r.id, r);
          const ck = `${r.lat},${r.lng}`;
          const arr = folkloreByCoord.current.get(ck) ?? [];
          if (!isFolkloreDuplicate(arr, r)) arr.push(r);
          folkloreByCoord.current.set(ck, arr);
        });
        const newFeatures = records.map((r) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [r.lng, r.lat] },
          properties: { id: r.id, name: r.name, layerType: "folklore", category: r.category, country: r.country },
        }));
        setFolkloreFeatures((prev) => [...prev, ...newFeatures]);
        setFolkloreCounts((prev) => prev + records.length);
      };
      import("./api/folklore.ts").then(({ loadSagnagrunnurFolklore }) => loadSagnagrunnurFolklore()).then(addRecords).catch((e) => console.warn("Sagnagrunnur failed:", e));
    };

    // Check immediately + on every map move
    checkAndLoad();
    m.on("moveend", checkAndLoad);
    return () => { m.off("moveend", checkAndLoad); };
  }, [showFolklore, sagnagrunnurLoaded, viewKey]);

  // Load museums (phased: cache/static first, then live API refresh)
  useEffect(() => {
    if (showMuseum && !museumsLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setMuseumsLoaded(true);
      addLoading("museum");

      const applyMuseums = (items: Museum[]) => {
        const known = new Set(items.map(m => m.id));
        const all = germanExtrasRef.current.museums.length
          ? [...items, ...germanExtrasRef.current.museums.filter(m => !known.has(m.id))]
          : items;
        museumIndex.current.clear();
        all.forEach(m => museumIndex.current.set(m.id, m));
        setMuseumFeatures(toSimpleGeoJSON(all, "museum").features);
        setMuseumCount(all.length);
      };

      import("./api/wikidata.ts").then(({ fetchMuseums, fetchOsmMuseums, mergeMuseums, fetchHeAccreditedMuseums }) => {
        // Phase 1: Show cached data instantly (localStorage)
        let shownQuick = false;
        try {
          // Cache version keys MUST match the writers in api/wikidata.ts
          // (fornland-museums-v7, fornland-osm-museums-v7). When the writer
          // bumps its version, this Phase-1 read silently misses every
          // load until the keys are aligned — a regression worth a comment
          // since this entire Phase-1 branch is dead until they match.
          // storageKeys.test.ts checks the VALID_KEYS allowlist but not this
          // read, so it is the half that has to be done by hand. It was
          // missed once already, when v5 became v6 for the Baltic countries.
          const wdC = localStorage.getItem("fornland-museums-v7");
          const osmC = localStorage.getItem("fornland-osm-museums-v7");
          if (wdC || osmC) {
            const wdItems: Museum[] = wdC ? JSON.parse(wdC).data ?? [] : [];
            const osmItems: Museum[] = osmC ? JSON.parse(osmC).data ?? [] : [];
            const merged = mergeMuseums([...wdItems], osmItems);
            if (merged.length > 0) { applyMuseums(merged); shownQuick = true; removeLoading("museum"); }
          }
        } catch { /* ignore corrupt cache */ }

        // Phase 1b: with no cache, paint from the tiles for this viewport.
        //
        // This was `museums.json` whole, 5 681 museums across twelve countries
        // and 316 kB on the wire, fetched to show something while the live
        // Wikidata query answered. The tiles do the same job for the cells in
        // view (`public/data/museums-wd/`, half a degree, written by
        // scripts/tile-static-sets.mjs). The file itself stays where it is:
        // api/wikidataSnapshot.ts still serves it as the outage fallback.
        const staticPromise = !shownQuick
          ? import("./api/pointTiles.ts")
              .then(({ loadPointTiles }) => loadPointTiles("museums-wd", viewBboxRef.current!))
              .then((recs) => {
                const items: Museum[] = recs.map((r) => {
                  const [id, lng, lat, name, country, wikidataId, wikipediaUrl, image, sourceUrl, website, address] =
                    r as [string, number, number, string, string, string, string, string, string, string, string];
                  return {
                    id, name, type: "Museum", country: country as Country, lng, lat, sourceUrl,
                    ...(wikidataId ? { wikidataId } : {}),
                    ...(wikipediaUrl ? { wikipediaUrl } : {}),
                    ...(image ? { image } : {}),
                    ...(website ? { website } : {}),
                    ...(address ? { address } : {}),
                  } as Museum;
                });
                if (items.length > 0 && !shownQuick) { applyMuseums(items); shownQuick = true; removeLoading("museum"); }
              }).catch(() => {})
          : Promise.resolve();

        // Phase 2: Background refresh from live APIs (including HE Accredited Museums)
        staticPromise.then(() => {
          // museums-osm-gb.json is 295 kB and museums-he.json 66, both about
          // British museums, and both were fetched over Denmark.
          const overGB = bboxOverlaps(viewBboxRef.current!, GB_BOUNDS);
          Promise.all([
            fetchMuseums().catch(() => [] as Museum[]),
            overGB ? fetchOsmMuseums().catch(() => [] as Museum[]) : Promise.resolve([] as Museum[]),
            overGB ? fetchHeAccreditedMuseums().catch(() => [] as Museum[]) : Promise.resolve([] as Museum[]),
          ]).then(([wdItems, osmItems, heItems]) => {
            const items = mergeMuseums(mergeMuseums(wdItems, osmItems), heItems);
            if (items.length > 0) applyMuseums(items);
          }).catch((e) => {
            console.warn("Museum live refresh failed:", e);
          }).finally(() => {
            removeLoading("museum");
          });
        });
      });
    }
  }, [showMuseum, museumsLoaded, viewKey]);

  // Load UNESCO sites (one-time fetch)
  useEffect(() => {
    if (showUnesco && !unescoLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setUnescoLoaded(true);
      addLoading("unesco");
      // Load WHS + Geoparks in parallel; both render into the same UNESCO
      // layer, with the type field ("Cultural" / "Natural" / "Mixed" /
      // "Geopark") distinguishing them in the detail panel.
      import("./api/wikidata.ts").then(async ({ fetchUnescoSites, fetchUnescoGeoparks }) => {
        const [whs, geoparks] = await Promise.all([
          fetchUnescoSites().catch(() => []),
          fetchUnescoGeoparks().catch(() => []),
        ]);
        const items = [...whs, ...geoparks];
        unescoIndex.current.clear();
        items.forEach(u => unescoIndex.current.set(u.id, u));
        setUnescoFeatures(toSimpleGeoJSON(items, "unesco").features);
        setUnescoCount(items.length);
        removeLoading("unesco");
      }).catch((e) => { console.warn("Failed to load UNESCO sites:", e); removeLoading("unesco"); });
    }
  }, [showUnesco, unescoLoaded, viewKey]);

  useEffect(() => {
    if (showMegalith && !megalithsLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setMegalithsLoaded(true);
      addLoading("megalith");
      import("./api/megalith.ts").then(({ fetchMegaliths }) => fetchMegaliths()).then((items) => {
        // Don't reset megalithsLoaded on empty/error — Wikidata rate-limits
        // (429) and SPARQL outages (502) returned [] before, which flipped
        // the flag back to false and triggered an immediate retry loop on
        // every map state change. Treat the first attempt as final for
        // this session; user gets megaliths from localStorage cache on
        // next page load if SPARQL recovers.
        if (items.length > 0) {
          megalithIndex.current.clear();
          items.forEach(s => megalithIndex.current.set(s.id, s));
          setMegalithFeatures(toSimpleGeoJSON(items.map((m) => ({ ...m, period: m.period || "Stenalder", years: megalithYears(m.type, m.period, m.country) })), "megalith").features);
          setMegalithCount(items.length);
        }
        removeLoading("megalith");
      }).catch((e) => { console.warn("Failed to load megaliths:", e); removeLoading("megalith"); });
    }
  }, [showMegalith, megalithsLoaded, viewKey]);

  /**
   * Townscapes, one country's file at a time.
   *
   * There are twelve files, 3.6 MB in all, and the layer used to load every
   * one of them on the first pan wherever that pan was: looking at Berlin
   * fetched the Danish 590 kB, the Norwegian and the two British sets. Now
   * api/townscape.ts takes the viewport and answers with the files it
   * overlaps, memoised per file, and this effect runs again as the map moves
   * and merges what comes back. `townscapeIndex` is shared with the
   * viewport-driven English fetcher in useFetchLayers, which rebuilds the
   * features the same way.
   */
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // The twelve files are country-sized, the Danish one 590 kB, and a view of
    // northern Europe touches all of them at once (utils/geo.ts).
    if (!showTownscape || !bbox || !wantsCountryFiles(bbox)) return;
    let cancelled = false;
    // 300 ms, for the reason useFetchLayers debounces its eight: a fly-through
    // fires several viewports and only the last matters, and the burst this
    // makes on mount competes with the deep-link path. Without it the
    // timeline's ghost tests, which poll for pins from a mocked register,
    // timed out about one e2e run in three.
    const timer = setTimeout(() => {
    addLoading("townscape");
    import("./api/townscape.ts").then(({ fetchTownscapes }) => fetchTownscapes(bbox)).then((items) => {
      if (cancelled) return;
      let added = 0;
      for (const s2 of items) if (!townscapeIndex.current.has(s2.id)) { townscapeIndex.current.set(s2.id, s2); added++; }
      if (!added) return;
      const all = Array.from(townscapeIndex.current.values());
      const features = toSimpleGeoJSON(all, "townscape").features;
      // Mark curated DK Nationale Kulturmiljøer for special map styling
      const curatedIds = new Set(all.filter(s2 => !!s2.values).map(s2 => s2.id));
      for (const f of features) { if (curatedIds.has(f.properties?.id) && f.properties) (f.properties as { curated?: boolean }).curated = true; }
      setTownscapeFeatures(features);
      setTownscapeCount(all.length);
    }).catch((e) => { console.warn("Failed to load townscapes:", e); })
      .finally(() => { if (!cancelled) removeLoading("townscape"); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showTownscape, viewKey, addLoading, removeLoading]);

  // Load plaques (one-time fetch)
  useEffect(() => {
    const bbox = viewBboxRef.current;
    /**
     * Den eneste af de udsnitsdrevne effekter der manglede arealporten, og
     * den dyreste at mangle den: `fetchPlaques` fyrer en nordisk
     * Overpass-forespørgsel helt uden bbox, og plaketter ligger i
     * GROUP_TABS.events, så den kørte på standardforsiden. Målt 19/9-2026 på
     * produktion: ét plakette-kald til Overpass pr. besøgende på
     * landingsudsnittet, uge efter uge når cachen udløb.
     *
     * At give den nordiske halvdel en bbox i stedet er ikke en linjes
     * arbejde: `fetchFromOverpass` har én cache-nøgle pr. forespørgsel, så en
     * bbox-forespørgsel under den faste nøgle ville servere Københavns
     * plaketter til en læser i Helsinki.
     */
    if (!showPlaque || !bbox || !wantsCountryFiles(bbox)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
    addLoading("plaque");
    // Open Plaques is tiled and follows the viewport; the Nordic half comes
    // from Overpass and is cached under its own key, so calling this again on
    // a pan costs one tile at most. Records accumulate, as they do for every
    // other viewport-driven layer.
    import("./api/plaques.ts").then(({ fetchPlaques }) => fetchPlaques(bbox)).then((items) => {
      if (cancelled) return;
      let added = 0;
      for (const s2 of items) if (!plaqueIndex.current.has(s2.id)) { plaqueIndex.current.set(s2.id, s2); added++; }
      if (!added) return;
      const all = Array.from(plaqueIndex.current.values());
      setPlaqueFeatures(toSimpleGeoJSON(all, "plaque").features);
      setPlaqueCount(all.length);
    }).catch((e) => { console.warn("Failed to load plaques:", e); })
      .finally(() => { if (!cancelled) removeLoading("plaque"); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showPlaque, viewKey, addLoading, removeLoading]);

  /**
   * Germany's castles, palaces, monasteries, forts and museums.
   *
   * Both layers reach the rest of Europe through one cross-border query and
   * neither can take Germany: see api/germany.ts::fetchGermanMonuments. The
   * German records are tiles, merged into the same indexes the global
   * fetchers fill, so the panel, the search and the counts see one layer.
   * `monumentsRawRef` is what the monument layer rebuilds from when its
   * second phase lands, so the German records go in there rather than
   * straight into the index, which that phase clears.
   */
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // viewport-gate-exempt: flisesættet bærer sin egen port. loadPointTiles
    // tjekker spændet (2°) *før* den henter indekset, så et bredt udsnit
    // svarer med [] og nul netværk — en arealport her ville sige det samme
    // to gange (api/pointTiles.ts:89).
    if (!showMonument || !bbox || !bboxOverlaps(bbox, DE_BOUNDS)) return;
    let cancelled = false;
    // 300 ms, for the reason useFetchLayers debounces its eight: a fly-through
    // fires several viewports and only the last matters, and the burst this
    // makes on mount competes with the deep-link path. Without it the
    // timeline's ghost tests, which poll for pins from a mocked register,
    // timed out about one e2e run in three.
    const timer = setTimeout(() => {
    import("./api/germany.ts").then(({ fetchGermanMonuments }) => fetchGermanMonuments(bbox)).then((items) => {
      if (cancelled || !items.length) return;
      const seen = new Set(germanExtrasRef.current.monuments.map((m) => m.id));
      const fresh = items.filter((m) => !seen.has(m.id));
      if (!fresh.length) return;
      germanExtrasRef.current.monuments = [...germanExtrasRef.current.monuments, ...fresh];
      for (const m of fresh) monumentIndex.current.set(m.id, m);
      const all = Array.from(monumentIndex.current.values());
      setMonumentFeatures(toSimpleGeoJSON(all.map((m) => ({ ...m, dating: m.startDate })), "monument").features);
      setMonumentCount(all.length);
    }).catch((e) => warnFetch("German monuments failed:", e));
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showMonument, viewKey]);

  useEffect(() => {
    const bbox = viewBboxRef.current;
    // viewport-gate-exempt: flisesættet bærer sin egen port. loadPointTiles
    // tjekker spændet (2°) *før* den henter indekset, så et bredt udsnit
    // svarer med [] og nul netværk — en arealport her ville sige det samme
    // to gange (api/pointTiles.ts:89).
    if (!showMuseum || !bbox || !bboxOverlaps(bbox, DE_BOUNDS)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
    import("./api/germany.ts").then(({ fetchGermanMuseums }) => fetchGermanMuseums(bbox)).then((items) => {
      if (cancelled || !items.length) return;
      const seen = new Set(germanExtrasRef.current.museums.map((m) => m.id));
      const fresh = items.filter((m) => !seen.has(m.id));
      if (!fresh.length) return;
      germanExtrasRef.current.museums = [...germanExtrasRef.current.museums, ...fresh];
      for (const m of fresh) museumIndex.current.set(m.id, m);
      const all = Array.from(museumIndex.current.values());
      setMuseumFeatures(toSimpleGeoJSON(all, "museum").features);
      setMuseumCount(all.length);
    }).catch((e) => warnFetch("German museums failed:", e));
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showMuseum, viewKey]);

  /**
   * Germany's Stolpersteine, one viewport at a time.
   *
   * The layer's own file holds 919 Nordic stones with hand-written
   * biographies and is loaded whole; Germany's 47 238 come from Wikidata as
   * tiles, because whole they are 3.6 MB for a layer that is off by default
   * and shows one city at a time. Both end up in `stolpersteinIndex`, so the
   * panel, the search index and the timeline see one layer. Records are only
   * added, never removed, so panning back to a visited city costs nothing.
   */
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // viewport-gate-exempt: flisesættet bærer sin egen port. loadPointTiles
    // tjekker spændet (2°) *før* den henter indekset, så et bredt udsnit
    // svarer med [] og nul netværk — en arealport her ville sige det samme
    // to gange (api/pointTiles.ts:89).
    if (!showStolperstein || !bbox || !bboxOverlaps(bbox, DE_BOUNDS)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
    addLoading("stolperstein");
    import("./api/germany.ts")
      .then(({ fetchGermanStolpersteine }) => fetchGermanStolpersteine(bbox))
      .then((items) => {
        if (cancelled) return;
        let added = 0;
        for (const st of items) if (!stolpersteinIndex.current.has(st.id)) { stolpersteinIndex.current.set(st.id, st); added++; }
        if (!added) return;
        const all = Array.from(stolpersteinIndex.current.values());
        setStolpersteinFeatures(toSimpleGeoJSON(all.map((st) => ({ ...st, years: stolpersteinYears(st.deportDate, st.deathYear) })), "stolperstein").features);
        setStolpersteinCount(all.length);
      })
      .catch((e) => warnFetch("German Stolpersteine failed:", e))
      .finally(() => { if (!cancelled) removeLoading("stolperstein"); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showStolperstein, viewKey, addLoading, removeLoading]);

  // Load stolpersteine (one-time fetch)
  useEffect(() => {
    const bbox = viewBboxRef.current;
    // Tiles now, so this runs on every viewport rather than once: the index
    // accumulates, the way every tiled layer in the app does, and the count is
    // what has been seen so far and not what the file holds.
    if (showStolperstein && bbox && wantsCountryFiles(bbox)) {
      addLoading("stolperstein");
      import("./api/stolpersteine.ts").then(({ fetchStolpersteine }) => fetchStolpersteine(bbox)).then((items) => {
        const fresh = items.filter((s) => !stolpersteinIndex.current.has(s.id));
        if (fresh.length) {
          fresh.forEach(s => stolpersteinIndex.current.set(s.id, s));
          const all = Array.from(stolpersteinIndex.current.values());
          setStolpersteinFeatures(toSimpleGeoJSON(all.map((st) => ({ ...st, years: stolpersteinYears(st.deportDate, st.deathYear) })), "stolperstein").features);
          setStolpersteinCount(all.length);
        }
        removeLoading("stolperstein");
      }).catch((e) => { console.warn("Failed to load stolpersteine:", e); removeLoading("stolperstein"); });
    }
  }, [showStolperstein, viewKey]);

  // Load lighthouses (one-time fetch)
  useEffect(() => {
    if (showLighthouse && !lighthousesLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setLighthousesLoaded(true);
      addLoading("lighthouse");
      import("./api/lighthouses.ts").then(({ fetchLighthouses }) => fetchLighthouses()).then((items) => {
        if (items.length === 0) { setLighthousesLoaded(false); removeLoading("lighthouse"); return; }
        lighthouseIndex.current.clear();
        items.forEach(s => lighthouseIndex.current.set(s.id, s));
        setLighthouseFeatures(toSimpleGeoJSON(items, "lighthouse").features);
        setLighthouseCount(items.length);
        removeLoading("lighthouse");
      }).catch((e) => { console.warn("Failed to load lighthouses:", e); setLighthousesLoaded(false); removeLoading("lighthouse"); });
    }
  }, [showLighthouse, lighthousesLoaded, viewKey]);

  // Load Oslo Byleksikon sites (viewport-gated to Oslo area)
  useEffect(() => {
    /**
     * 1.6 MB about one city, and a visitor in Berlin was downloading it.
     *
     * The guard was here already, but it read the map through `mapRef` and
     * skipped itself when there was no map yet — which is the state the
     * effect runs in on a first load, so the file was fetched and the check
     * never happened. `viewBboxRef` is filled by onLoad and onMoveEnd
     * instead, and `viewKey` is in the deps so panning into Oslo still loads
     * it.
     */
    if (showByleksikon && !byleksikonLoaded && viewBboxRef.current && bboxOverlaps(viewBboxRef.current, OSLO_BOUNDS)
      && viewportFitsACountryFile(viewBboxRef.current)) {
      setByleksikonLoaded(true);
      addLoading("byleksikon");
      import("./api/osloByleksikon.ts").then(async ({ loadOsloByleksikon, getAllOBLWithCoords }) => {
        await loadOsloByleksikon();
        const all = getAllOBLWithCoords();
        if (all.length === 0) { setByleksikonLoaded(false); removeLoading("byleksikon"); return; }
        // Deduplicate against existing heritage/monument/building pins
        const existingCoords = new Set<string>();
        for (const f of heritageFeatures) {
          if (f.geometry.type === "Point") {
            const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
            existingCoords.add(`${lat.toFixed(3)},${lng.toFixed(3)}`);
          }
        }
        for (const [, pin] of monumentIndex.current) existingCoords.add(`${pin.lat.toFixed(3)},${pin.lng.toFixed(3)}`);
        for (const [, pin] of buildingIndex.current) existingCoords.add(`${pin.lat.toFixed(3)},${pin.lng.toFixed(3)}`);

        /**
         * Dubletten undertrykker pinnen, ikke posten.
         *
         * `continue` stod her, saa en artikel hvis koordinat allerede baerer
         * en bygnings- eller monument-pin forsvandt helt — ogsaa ud af
         * indekset. Sitemappen udgiver den alligevel, for generatoren kender
         * ikke de pins der tilfaeldigvis er hentet i browseren, og saa kunne
         * panelet aldrig aabne paa dens adresse. Maalt 24/9-2026:
         * /s/byleksikon/obl-hoeybraatenveien og obl-furuset-kulturpark
         * aabnede aldrig, mens obl-abbediengen-gaard aabnede paa 3,1 s.
         *
         * Nu gaar hver artikel i indekset, og kun de udeduplikerede tegnes.
         * Kortet viser stadig ét punkt pr. sted, og adressen virker.
         */
        const sites: ByleksikonSite[] = [];
        const tegnes: ByleksikonSite[] = [];
        for (const a of all) {
          const ck = `${a.lat.toFixed(3)},${a.lng.toFixed(3)}`;
          const sanitized = a.title.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, "-").toLowerCase();
          const site: ByleksikonSite = {
            id: `obl-${sanitized}`,
            name: a.title,
            type: a.type || "annet",
            country: "NO",
            lng: a.lng,
            lat: a.lat,
            extract: a.e,
            url: a.u,
            img: a.img,
            imgCredit: a.imgC,
            imgLicense: a.imgL,
            imgCaption: a.imgCaption,
            imgs: a.imgs,
            cats: a.cats,
            addr: a.addr,
            sourceUrl: a.u,
          };
          sites.push(site);
          if (!existingCoords.has(ck)) tegnes.push(site);
        }
        byleksikonIndex.current.clear();
        sites.forEach(s => byleksikonIndex.current.set(s.id, s));
        const features: GeoJSON.Feature[] = tegnes.map(s => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [s.lng, s.lat] },
          properties: { id: s.id, name: s.name, layerType: "byleksikon", oblType: s.type, country: "NO" },
        }));
        setByleksikonFeatures(features);
        // Tallet er de tegnede punkter og ikke posterne, som foer.
        setByleksikonCount(tegnes.length);
        removeLoading("byleksikon");
      }).catch((e) => { console.warn("Failed to load Oslo Byleksikon:", e); setByleksikonLoaded(false); removeLoading("byleksikon"); });
    }
  }, [showByleksikon, byleksikonLoaded, viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wikidata archaeological supplement — one-time fetch when heritage tab is active.
  // Don't reset wikidataArchLoaded on empty/error: SPARQL hits 429/502 routinely
  // and resetting created an infinite retry loop on every map state change.
  useEffect(() => {
    if (showHeritage && !wikidataArchLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setWikidataArchLoaded(true);
      import("./api/archaeological.ts").then(({ fetchWikidataArchaeological }) => fetchWikidataArchaeological()).then((items) => {
        if (items.length > 0) {
          wikidataArchRef.current = items;
          mergeStaticHeritageRef.current();
        }
      }).catch((e) => {
        console.warn("Failed to load Wikidata archaeological:", e);
      });
    }
  }, [showHeritage, wikidataArchLoaded, viewKey]);

  useEffect(() => {
    if (showThingSite && !thingsitesLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setThingsitesLoaded(true);
      addLoading("thingsite");
      import("./api/static.ts").then(({ fetchThingSites }) => fetchThingSites()).then((sites) => {
        sites.forEach(s => thingsiteIndex.current.set(s.id, s));
        setThingsiteFeatures(toSimpleGeoJSON(sites, "thingsite").features);
        setThingsiteCount(sites.length);
        removeLoading("thingsite");
      }).catch((e) => { console.warn("Failed to load thing sites:", e); removeLoading("thingsite"); });
    }
  }, [showThingSite, thingsitesLoaded, viewKey]);

  useEffect(() => {
    if (showRoute && !routesLoaded && wantsCountryFiles(viewBboxRef.current)) {
      setRoutesLoaded(true);
      addLoading("route");
      Promise.all([
        import("./api/static.ts").then(({ fetchHistoricRoutes }) => fetchHistoricRoutes(viewBboxRef.current!)),
        // The Roman roads stop at the Welsh coast and 55,6° N, so they wait
        // for a map over England and Wales rather than over Britain.
        bboxOverlapsAny(viewBboxRef.current!, EN_BOUNDS)
          ? fetch("/data/roman-roads-gb.json").then(r => r.json()).catch(() => ({ routes: [], geojson: { type: "FeatureCollection", features: [] } }))
          : Promise.resolve({ routes: [], geojson: { type: "FeatureCollection", features: [] } }),
        bboxOverlapsAny(viewBboxRef.current!, EN_BOUNDS)
          ? fetch("/data/roman-roads-curated.json").then(r => r.json()).catch(() => ({ roads: {} }))
          : Promise.resolve({ roads: {} }),
      ]).then(([historic, roman, curated]) => {
        // Match Roman road segments to curated road descriptions
        interface CuratedRoad { description?: { en?: string }; wikipediaUrl?: string; wikidataId?: string; distance?: string; from?: string; to?: string; matchPatterns?: string[]; modernRoads?: string }
        const roads = (curated.roads ?? {}) as Record<string, CuratedRoad>;
        const patterns: { name: string; regex: RegExp; road: CuratedRoad }[] = [];
        for (const [roadName, road] of Object.entries(roads)) {
          for (const p of (road.matchPatterns ?? [])) {
            try { patterns.push({ name: roadName, regex: new RegExp(p, "i"), road }); } catch { /* skip bad regex */ }
          }
        }
        for (const route of roman.routes) {
          const segName = (route.name || "").toLowerCase();
          for (const { name: roadName, regex, road } of patterns) {
            if (regex.test(segName)) {
              route.name = roadName + (route.name ? ` (${route.name})` : "");
              route.description = road.description?.en;
              route.wikipediaUrl = road.wikipediaUrl;
              route.wikidataId = road.wikidataId;
              route.distanceInfo = road.distance;
              route.fromTo = `${road.from} → ${road.to}`;
              route.modernRoads = road.modernRoads;
              break;
            }
          }
        }
        const allRoutes = [...historic.routes, ...roman.routes];
        const allFeatures = [...historic.geojson.features, ...roman.geojson.features];
        routeIndex.current.clear();
        allRoutes.forEach(r => routeIndex.current.set(r.id, r));
        setRouteGeoJSON({ type: "FeatureCollection", features: allFeatures });
        setRouteCount(allRoutes.length);
        removeLoading("route");
      }).catch((e) => { console.warn("Failed to load routes:", e); removeLoading("route"); });
    }
  }, [showRoute, routesLoaded, viewKey]);

  /**
   * Curated walks. Two static files, so no bbox gate: fetched the first time
   * anything asks and never again. The loader already drops a walk that lost
   * every stop, so anything reaching here has somewhere to go.
   *
   * A function and not just an effect, because the `#walk=` deep link cannot
   * afford to wait for the effect. It used to: the branch set activeTab and
   * then polled walkIndex every 500 ms until the layer gate had turned the
   * effect on. Measured on a cold dev server 24 Aug 2026, a shared walk link
   * opened its panel 4,2 / 5,1 / 8,3 s after the map was ready, because the
   * effect needs a React commit first and the file then queues behind the
   * sixty other /data/*.json the initial all-layers load has in flight. The
   * deep link now calls this directly. The promise ref makes the second
   * caller wait on the first request rather than issue a second.
   */
  const walksPromise = useRef<Promise<CuratedWalk[]> | null>(null);
  // The walk pins, named in the language each walk will be read in, and
  // renamed when the reader switches language (walkReading.ts).
  useEffect(() => {
    if (!walkCount) return;
    const walks = [...walkIndex.current.values()];
    setWalkGeoJSON(toSimpleGeoJSON(walks.map(w => ({ id: w.id, lng: w.lng, lat: w.lat, name: walkIn(w, readingLang(w, lang)).title, country: w.country, type: w.city })), "walk"));
  }, [walkCount, lang]);
  const loadWalks = useCallback((): Promise<CuratedWalk[]> => {
    if (!walksPromise.current) {
      addLoading("walk");
      walksPromise.current = import("./api/curatedWalks.ts")
        .then(({ fetchCuratedWalks }) => fetchCuratedWalks())
        .then(walks => {
          walkIndex.current.clear();
          walks.forEach(w => walkIndex.current.set(w.id, w));
          setWalkCount(walks.length);
          removeLoading("walk");
          return walks;
        })
        .catch(e => {
          warnFetch("curated walks", e);
          removeLoading("walk");
          // Cleared, so a later caller retries rather than inheriting the
          // failure of whoever happened to ask first.
          walksPromise.current = null;
          return [] as CuratedWalk[];
        });
    }
    return walksPromise.current;
  }, [addLoading, removeLoading]);

  useEffect(() => {
    if (showWalk) void loadWalks();
  }, [showWalk, loadWalks]);

  /**
   * The stops of the walk currently open, as their own source.
   *
   * Not a layer: it is empty unless a walk is selected, and it carries no
   * index, because a stop is only ever reached through the walk that owns it.
   * `n` is the number the panel prints, so the map and the list count alike.
   */
  const walkStopGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    const walk = selectedType === "walk" ? (selected as CuratedWalk | null) : null;
    // `stops` is checked, not assumed: a search hit reached this as a bare
    // record once and took the whole app down (found 26 Sep 2026).
    if (!walk?.stops) return { type: "FeatureCollection", features: [] };
    return {
      type: "FeatureCollection",
      features: walk.stops.map((st, i) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [st.lng, st.lat] },
        properties: { id: st.id, walkId: walk.id, name: st.name, n: i + 1, layerType: "walk-stop" },
      })),
    };
  }, [selected, selectedType]);
  const walkStopsVisible = walkStopGeoJSON.features.length > 0;
  const openWalkId = selectedType === "walk" && (selected as CuratedWalk | null)?.stops ? ((selected as CuratedWalk).id ?? "") : "";
  /**
   * The open walk's walking line, baked at build time (scripts/walkRoute.mjs).
   * Empty when no walk is open or the walk has no line, like the stops above.
   */
  const walkRouteGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    const walk = selectedType === "walk" ? (selected as CuratedWalk | null) : null;
    if (!walk?.path?.length) return { type: "FeatureCollection", features: [] };
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "LineString", coordinates: walk.path }, properties: {} }],
    };
  }, [selected, selectedType]);
  /**
   * The other places a Danish legend touches, published by its own panel.
   *
   * The pin is one point and the source often names several. Where it names two
   * or more we do not choose between them, and that refusal used to be
   * invisible: a single pin, and no sign that three places were in play. These
   * points say it out loud. They also carry the recording place for the 308
   * legends whose pin was moved off it, which answers the reader's other
   * question, where was this actually told.
   *
   * The coordinates ride in the lazily fetched text row, so FolkloreDetail is
   * the only thing that has them and it hands them up. Dropped when its id is
   * not the record on screen: the fetch is asynchronous and the panel's own
   * carousel can move on while it is in flight.
   */
  const [folkloreContext, setFolkloreContext] = useState<FolkloreContext | null>(null);
  const folkloreContextGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    const empty = { type: "FeatureCollection" as const, features: [] };
    if (!folkloreContext || selectedType !== "folklore") return empty;
    const recs = selected as FolkloreRecord[] | null;
    if (!recs?.some((r) => r.id === folkloreContext.id)) return empty;
    const { origin, mentioned, recorded } = folkloreContext;
    const near = (p: { lat: number; lng: number }) =>
      Math.abs(p.lat - origin.lat) < 1e-4 && Math.abs(p.lng - origin.lng) < 1e-4;
    const points = [
      ...mentioned.map((p) => ({ ...p, kind: "mentioned" as const })),
      ...(recorded ? [{ ...recorded, kind: "recorded" as const }] : []),
    // One of the mentioned places is very often the recording place itself, and
    // drawing a second circle on top of the pin reads as a rendering fault.
    ].filter((p) => !near(p));
    return {
      type: "FeatureCollection",
      features: points.flatMap((p) => [
        {
          type: "Feature" as const,
          geometry: { type: "LineString" as const, coordinates: [[origin.lng, origin.lat], [p.lng, p.lat]] },
          properties: { kind: p.kind },
        },
        {
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
          properties: { kind: p.kind, name: p.name },
        },
      ]),
    };
  }, [folkloreContext, selected, selectedType]);

  // 1-based position of the stop being read, 0 when the walk is unstarted.
  // The map fades stops with n below this, so the pins say how far along the
  // walk the reader is — colour as data, in this case the data "behind you".
  const activeStopN = useMemo(() => {
    const walk = selectedType === "walk" ? (selected as CuratedWalk | null) : null;
    if (!walk || !activeStopId) return 0;
    return walk.stops.findIndex(st => st.id === activeStopId) + 1;
  }, [selected, selectedType, activeStopId]);

  /**
   * Warm the cache for the place the reader is actually looking at.
   *
   * This ran on mount with no conditions at all and fetched every layer for
   * every visitor: the British monuments (411 kB), the runestones, the
   * battlefields, the Wikidata and OSM museums, UNESCO, and then churches for
   * fourteen countries one after another, each its own Overpass query. A
   * reader who opened the front page and left downloaded Britain and rang
   * Overpass fourteen times. Measured 12 Sep 2026, it was most of what the
   * front page cost and a good deal of what the CDN was serving.
   *
   * It keeps the point it was written for, which is that switching a tab
   * should feel instant, and drops the part that was never true, which is
   * that everyone needs everything. It waits for a viewport worth a country
   * file, runs once a session, and asks for churches only where the map is.
   */

  // Whether the landed-on record has had its turn: selected, or 12 s gone.
  const [landingSettled, setLandingSettled] = useState(!LANDING_LAYER);
  useEffect(() => {
    if (landingSettled) return;
    if (selected) { const t = window.setTimeout(() => setLandingSettled(true), 1500); return () => window.clearTimeout(t); }
    const t = window.setTimeout(() => setLandingSettled(true), 12000);
    return () => window.clearTimeout(t);
  }, [selected, landingSettled]);
  const preloadedRef = useRef(false);
  useEffect(() => {
    const bbox = viewBboxRef.current;
    /**
     * Arealporten står her for sig selv, fordi `wantsCountryFiles` ikke bider
     * på en valgt fane: den er `!browsingEverything || fits`, og en
     * explore-side har netop valgt en fane, så porten falder sammen til
     * `!!bbox`. Målt 19/9-2026 kostede /explore/runestones 2 564 KiB, hvoraf
     * 1 329 var Storbritanniens fortidsminder og museer, forhåndshentet ved
     * kontinent-zoom til faner læseren ikke havde bedt om.
     *
     * Linjen er: det lag læseren har valgt er ikke gated — det får hun i
     * enhver zoom gennem lagets egen effekt. Det er den spekulative
     * forhåndshentning af de *andre* lag der er det.
     */
    if (preloadedRef.current || !bbox || !wantsCountryFiles(bbox)) return;
    if (!viewportFitsACountryFile(bbox)) return;
    // A reader who came for one record gets it first. The other layers
    // shared the line with its panel and held it back 12 s on 4G.
    if (LANDING_LAYER && !landingSettled) return;
    preloadedRef.current = true;
    const idleCb = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 2500));
    idleCb(() => {
      // One Overpass query per country, so the list is the countries in view
      // and not the fourteen the app knows.
      const churchCountries = CHURCH_COUNTRIES.filter(([, , boxes]) => bboxOverlapsAny(bbox, boxes));
      loadOverpass().then(({ fetchRunestones, fetchBattlefields, fetchWikidataBattles, fetchHistoricMonuments, fetchChurchesByCountry }) => {
        fetchRunestones().catch(() => {});
        fetchBattlefields().then(() => fetchWikidataBattles().catch(() => {})).catch(() => {});
        // Sequentially, as before: Overpass has a concurrency limit and a
        // burst from one visitor gets the whole machine refused for an hour.
        let chain = fetchHistoricMonuments({ gb: bboxOverlaps(bbox, GB_BOUNDS) }).catch(() => {}) as Promise<unknown>;
        for (const [code, key] of churchCountries) {
          chain = chain.then(() => fetchChurchesByCountry(code, key)).catch(() => {});
        }
      });
      import("./api/wikidata.ts").then(({ fetchMuseums, fetchOsmMuseums, fetchUnescoSites, fetchUnescoGeoparks }) => {
        fetchMuseums().catch(() => {});
        if (bboxOverlaps(bbox, GB_BOUNDS)) fetchOsmMuseums().catch(() => {});
        fetchUnescoSites().catch(() => {});
        fetchUnescoGeoparks().catch(() => {});
      });
    });
  }, [viewKey, landingSettled]);

  // --- VIEWPORT-BASED FETCH (delegated to useFetchLayers hook) ---
  const { fetchForLayers, getVisibleLayers, fetchHeritageSites: _fetchHeritageSites, mergeStaticHeritage } = useFetchLayers({
    debounceRef, folkDebounceRef, bldDebounceRef, wreckDebounceRef,
    rockDebounceRef, indDebounceRef, barDebounceRef, enTownDebounceRef,
    lastBboxRef, folkLastBboxRef, bldLastBboxRef, wreckLastBboxRef,
    rockLastBboxRef, indLastBboxRef, barLastBboxRef, enTownLastBboxRef,
    folkloreIndex, folkloreByCoord,
    buildingIndex, shipwreckIndex, rockArtIndex, industrialIndex, barrowIndex, townscapeIndex,
    wikidataArchRef, doggerHeritageCacheRef, romanCacheRef, hillfortCacheRef, hoardCacheRef, wdWreckCacheRef,
    heritageCoordSet,
    setHeritageFeatures, setFolkloreFeatures, setBuildingFeatures,
    setShipwreckFeatures, setRockArtFeatures, setIndustrialFeatures,
    setBarrowFeatures, setTownscapeFeatures,
    setCounts, setFolkloreCounts, setBuildingCount,
    setShipwreckCount, setRockArtCount, setIndustrialCount,
    setBarrowCount, setTownscapeCount,
    setLoading, setError,
    addLoading, removeLoading,
    lang,
    browsingEverythingRef,
  });
  mergeStaticHeritageRef.current = mergeStaticHeritage;

  // Keep refs in sync so the direct MapLibre moveend listener always reads fresh values
  fetchForLayersRef.current = fetchForLayers;
  getVisibleLayersRef.current = getVisibleLayers;
  activeGroupRef.current = activeGroup;
  activeTabRef.current = activeTab;

  // --- MAP EVENTS ---
  const onMoveEnd = useCallback((e: ViewStateChangeEvent) => {
    const bbox = bboxOfBounds(e.target.getBounds());
    const c = e.target.getCenter();
    setLensSheet(prev => { const next = pastSheetAt(c.lng, c.lat); return next?.id === prev?.id ? prev : next; });
    setViewCountry(determineCountry(c.lat, c.lng));
    // Read the tab through the refs, as the raw moveend listener in onLoad
    // does, not through this closure. A prop-driven camera change fires
    // moveend synchronously inside react-map-gl's effect, one render before
    // it has swapped this callback for the current one: the campaign link
    // (#hist=1) sets maxBounds and a tab in the same commit, and the closure
    // from the first render fetched the "all" set for Denmark while the tab
    // was folklore, measured 2 Sep 2026 as the shipwreck registers of two
    // countries answering a link named 'sagnene'. The refs are assigned
    // during render, so they are already current when that moveend fires.
    fetchForLayers(getVisibleLayers(activeGroupRef.current, activeTabRef.current), bbox);
    noteCamera(e.target, bbox);
    try {
      const { longitude, latitude, zoom } = e.viewState;
      localStorage.setItem("fornland-map-view", JSON.stringify({ lng: +longitude.toFixed(4), lat: +latitude.toFixed(4), zoom: +zoom.toFixed(2) }));
    } catch { /* ignore */ }
    // Auto-refresh local search results when query is active
    if (activeSearchQueryRef.current) {
      clearTimeout(searchPanTimerRef.current);
      searchPanTimerRef.current = setTimeout(() => setSearchTrigger(c => c + 1), 500);
    }
  }, [fetchForLayers, getVisibleLayers, activeGroup, activeTab]);

  const onLoad = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    // The lens control follows the map's centre; a deep link mounts on its
    // target without a moveend, so the first reading happens here.
    { const c = m.getMap().getCenter(); setLensSheet(pastSheetAt(c.lng, c.lat)); setViewCountry(determineCountry(c.lat, c.lng)); }

    // Register a direct moveend listener on the underlying MapLibre map.
    // This is a safety net: react-map-gl's onMoveEnd prop may occasionally miss
    // events after flyTo animations, programmatic zoom changes, or on initial load.
    // The per-layer bbox guards prevent redundant API calls, so double-firing is
    // harmless — but a guarded re-mount or style change can fire `load` again,
    // which would stack additional listeners. We track whether the listener is
    // already attached via a ref so we never duplicate.
    const rawMap = m.getMap();
    if (!moveendListenerAttachedRef.current) {
      const moveendHandler = () => {
        const fn = fetchForLayersRef.current;
        const gv = getVisibleLayersRef.current;
        if (!fn || !gv) return;
        const box = bboxOfBounds(rawMap.getBounds());
        fn(gv(activeGroupRef.current, activeTabRef.current), box);
        // Lytteren findes fordi proppen kan misse et event. Den skal derfor
        // notere udsnittet af samme grund som den henter lag: ellers står
        // `viewKey` forældet netop når proppen svigtede.
        noteCamera(rawMap, box);
      };
      rawMap.on("moveend", moveendHandler);
      moveendListenerAttachedRef.current = true;
    }

    // Deep-link: the map has already mounted on the linked view, because
    // initialViewState reads the hash through the same parser. There is no
    // flight left to make: the old flyTo from the visitor's stored view
    // covered no distance once the two agreed, cost two seconds before the
    // moveend that fetched the layers, and under OS reduce-motion became a
    // synchronous jumpTo whose moveend fired before the linked tab was set.
    // So this handler applies the tab and fetches for the mounted bounds
    // itself, then returns; the mount fetch below is for a plain visit.
    const hash = window.location.hash;
    if (hash) {
      const p = new URLSearchParams(hash.slice(1));
      const siteId = p.get("site");
      const layer = p.get("layer");
      const walkId = p.get("walk");
      const stopId = p.get("stop");
      const target = parseDeepLinkTarget(hash);
      // #hist=1 opens the map on the 1842-1899 survey sheets. Read-only, like
      // every deep link except the walk: the campaign page links in with it,
      // and the toggle itself never writes it back. Without a coordinate the
      // parser has already put the map on SURVEY_HOME.
      if (p.get("hist") === "1") setHistMap(true);
      // The initial tab state already read this; setting it again is a no-op
      // on a fresh load and the right thing when the hash changed underneath.
      const linkedTab = parseDeepLinkTab(hash);
      if (linkedTab) { setActiveGroup(linkedTab.group); setActiveTab(linkedTab.tab); }
      // A walk is resumed, not visited. Every other deep link is a one-shot
      // jump and ends by wiping the hash, so a reload lands you at the front
      // page. That is wrong for a walk you are halfway through: the hash is the
      // only record of where you got to, and closing the tab on stop nine is
      // exactly when you want it kept. So this branch never touches the hash.
      // It returns without the mount fetch: the frame it ends with fires the
      // moveend that fetches, and by then the walk tab has rendered, so the
      // fetch is for the walk's layers and not the stored tab's.
      //
      // Og derfor er der heller ingen `noteCamera` her, selv om det er den
      // fjerde sti ud af onLoad. En walk-deeplink sætter activeTab til "walk",
      // så `browsingEverything` er falsk og `wantsCountryFiles` falder sammen
      // til `!!bbox`: 40-graders-porten gælder ikke. At notere udsnittet her
      // ville derfor koste doggerland, hoards og hele forhåndshenterens byge
      // for en der bad om én byvandring i Oslo — og give hende intet synligt,
      // for vandringerne kommer fra loadWalks() og ikke fra nogen
      // viewKey-effekt. frameWalk's fitBounds noterer det et øjeblik efter.
      if (walkId) {
        setShowWelcome(false);
        setActiveGroup("explore");
        setActiveTab("walk");
        const myToken = ++deepLinkSelectTokenRef.current;
        // Ask for the file rather than waiting for the layer effect to notice
        // the tab change, and take the answer from the promise rather than
        // from walkIndex, so there is nothing left to poll. No early return
        // either, unlike the branch below: a `#walk=` naming a walk that has
        // since been renamed would otherwise leave a map with no data in it.
        void loadWalks().then(walks => {
          if (deepLinkSelectTokenRef.current !== myToken) return;
          const walk = walks.find(w => w.id === walkId);
          if (!walk) return;
          const stop = stopId ? walk.stops.find(st => st.id === stopId) : undefined;
          setSelected(walk);
          setSelectedType("walk");
          setActiveStopId(stop?.id ?? null);
          // Resuming mid-walk goes to the stop; opening fresh frames the walk.
          if (stop) m.flyTo({ center: [stop.lng, stop.lat], zoom: 17, duration: 2000 });
          else frameWalk(walk, true);
        });
        if (import.meta.env.DEV) (window as unknown as { __mapReady: boolean }).__mapReady = true;
        return;
      }
      if (target) {
        setShowWelcome(false);
        // Fetch now, for the bounds the map mounted on and the tab the link
        // named. The tab state set above has not rendered yet, so the visible
        // layers are derived from the link rather than read back from state.
        const mountBbox = bboxOfBounds(m.getBounds());
        fetchForLayers(
          getVisibleLayers(linkedTab?.group ?? activeGroup, linkedTab?.tab ?? activeTab),
          mountBbox,
        );
        // A page that opens on a place and never moves fires no moveend, and
        // the viewport-driven layers would wait forever for one.
        noteCamera(m.getMap(), mountBbox);
        // If a specific site ID is in the hash, select it after data loads.
        // The retry loop fires every 500 ms for up to 7.5 s; if the user
        // clears the hash by selecting a different pin (which writes to
        // hash) or via another deep-link, deepLinkSelectTokenRef gets
        // bumped and the old retry chain self-cancels.
        if (siteId && layer) {
          const myToken = ++deepLinkSelectTokenRef.current;
          const trySelect = (attempts = 0) => {
            if (deepLinkSelectTokenRef.current !== myToken) return; // newer deep link won; abandon
            // Layer detail records are heterogeneous; the value is fed straight back
            // into setSelected and narrowed downstream via selectedType.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let record: any = null;
            if (layer === "heritage") {
              // Heritage has no named index; search features by properties.id.
              // Read via ref to pick up state updates that arrive AFTER this
              // callback was created at first render.
              const feat = heritageFeaturesRef.current.find(f => f.properties?.id === siteId);
              if (feat?.properties) record = { ...feat.properties, lng: (feat.geometry as GeoJSON.Point).coordinates[0], lat: (feat.geometry as GeoJSON.Point).coordinates[1] };
            } else {
              const INDEX_MAP: Record<string, React.MutableRefObject<Map<string, unknown>>> = {
                runestone: runestoneIndex, battlefield: battlefieldIndex,
                monument: monumentIndex, building: buildingIndex, shipwreck: shipwreckIndex,
                museum: museumIndex, rockart: rockArtIndex, unesco: unescoIndex,
                industrial: industrialIndex, barrow: barrowIndex, megalith: megalithIndex,
                townscape: townscapeIndex, plaque: plaqueIndex, stolperstein: stolpersteinIndex,
                lighthouse: lighthouseIndex, thingsite: thingsiteIndex, route: routeIndex,
                // A shared walk arrives here rather than through the `#walk=`
                // branch above, because the share card writes `site=&layer=`
                // like every other pin. It opens the walk at the top, which is
                // right: sharing a walk is not the same as resuming one.
                walk: walkIndex,
                byleksikon: byleksikonIndex, folklore: folkloreIndex,
              };
              record = INDEX_MAP[layer]?.current?.get(siteId);
            }
            if (record) {
              /**
               * Sagnene vaelges som en liste, ikke som ét sted.
               *
               * FolkloreDetail tager `records: FolkloreRecord[]`, fordi flere
               * sagn kan hoere til samme punkt, og et klik paa en pin sender
               * hele gruppen fra folkloreByCoord. Deeplinket sendte ét objekt,
               * som blev castet til en liste, saa `records[0]` var undefined
               * og panelet kastede paa `record.folkloreCatKey`. Maalt i
               * produktion 21/9-2026: hver eneste af de 10.111
               * folklore-adresser viste «COULD NOT LOAD», ogsaa dem hvor alt
               * andet virkede. Det ramte ikke kun Google — en laeser der
               * fulgte et delt sagnlink saa det samme.
               */
              if (layer === "folklore") {
                const r = record as { lat: number; lng: number };
                const gruppe = folkloreByCoord.current.get(`${r.lat},${r.lng}`);
                setSelected(gruppe?.length ? gruppe : [record]);
              } else {
                setSelected(record);
              }
              setSelectedType(layer as LayerType);
            } else if (attempts < 50) {
              // Var 15, altsaa 9 sekunder i alt. Et lag der ligger bag
              // Overpass eller WDQS er sjaeldent i indekset saa hurtigt, og
              // naar loekken har opgivet, aabner panelet aldrig uanset hvor
              // laenge siden staar aaben. 50 forsoeg er 26,5 sekunder.
              setTimeout(() => trySelect(attempts + 1), 500);
            }
          };
          setTimeout(() => trySelect(), 1500);
        }
        // A coordinate link is a one-shot jump and ends by wiping the hash,
        // so a reload lands on the front page. The campaign link, #hist=1
        // with no coordinate, keeps its hash and reopens the sheets.
        if (p.has("lat")) window.location.hash = "";
        // Dev-only signal for E2E tests: even though we returned early via
        // the deep-link path, the map is now ready to be asserted against.
        if (import.meta.env.DEV) (window as unknown as { __mapReady: boolean }).__mapReady = true;
        return;
      }
    }
    const bbox = bboxOfBounds(m.getBounds());
    fetchForLayers(getVisibleLayers(activeGroup, activeTab), bbox);
    // Forsiden uden deeplink fyrer intet moveend når kortet lander på det
    // gemte udsnit og aldrig flytter sig. Det er den sti flest kommer ind ad.
    noteCamera(m.getMap(), bbox);

    // Dev-only signal for E2E tests: deep-link handling is finished and map
    // is ready to be inspected/asserted against. Tests poll window.__mapReady.
    if (import.meta.env.DEV) {
      (window as unknown as { __mapReady: boolean }).__mapReady = true;
    }
  }, [fetchForLayers, getVisibleLayers, activeGroup, activeTab, loadWalks]);

  /**
   * Move the reader to a stop, whether they picked it in the list or on the map.
   *
   * This is the only place in Fornland that writes to the URL. It uses
   * replaceState rather than assigning to location.hash, because a sixteen-stop
   * walk would otherwise bury the page the reader came from under sixteen back
   * presses.
   */
  const selectWalkStop = useCallback((walkId: string, stop: WalkStop) => {
    setActiveStopId(stop.id);
    history.replaceState(null, "", `#walk=${encodeURIComponent(walkId)}&stop=${encodeURIComponent(stop.id)}`);
    mapRef.current?.flyTo({ center: [stop.lng, stop.lat], zoom: 17, duration: 1000 });
  }, []);

  /**
   * Frame a walk's whole extent. A fixed zoom frames one walk by luck and the
   * next by accident — the two Oslo walks already differ by a factor of two
   * (0.49 km against 0.71 km across), so the stops decide the camera instead.
   * `panelOpen` widens the right padding on desktop, where the 420px sheet
   * would otherwise cover the eastern stops.
   */
  const frameWalk = useCallback((walk: CuratedWalk, panelOpen: boolean) => {
    const b = boundsOf(walk.stops);
    if (!b) return;
    const wide = window.innerWidth >= 768;
    mapRef.current?.fitBounds(b, {
      padding: wide
        ? { top: 80, bottom: 80, left: 80, right: panelOpen ? 500 : 80 }
        : { top: 110, bottom: 140, left: 48, right: 48 },
      maxZoom: 16.5,
      duration: 1500,
    });
  }, []);

  const onSelectWalkStop = useCallback((stop: WalkStop) => {
    const walk = selectedType === "walk" ? (selected as CuratedWalk | null) : null;
    if (walk) selectWalkStop(walk.id, stop);
  }, [selected, selectedType, selectWalkStop]);

  // "View the map" in the walk panel: swap the sheet for WalkModeBar and
  // frame the walk, now with no panel to pad around.
  const handleShowWalkMap = useCallback(() => {
    const walk = selectedType === "walk" ? (selected as CuratedWalk | null) : null;
    if (!walk) return;
    setWalkMapView(true);
    frameWalk(walk, false);
  }, [selected, selectedType, frameWalk]);

  // One stop on the oldest map the past lens holds for it: the walk collapses
  // to its bar as for the map button, selecting the stop flies to it at zoom
  // 17, and the lens comes on. moveend picks the sheet under the stop.
  //
  // The zoom follows the sheet, not the stop. Measured 30 Sep 2026: the Danish
  // målebordsblade (1:20 000) are sharp at 14 and a grey blur at 17, and the
  // Norwegian town plans read best at 16. Only London's five-foot plan holds
  // up closer than that.
  //
  // The stop is chosen after the sheet has closed, not before: closing it
  // steps history back past the sheet's own entry (useBackDismiss), and that
  // entry is `#walk=` without the stop, so a stop written first was undone.
  const handleShowWalkPast = useCallback((stop: WalkStop) => {
    setWalkMapView(true);
    setLensOn(true);
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      onSelectWalkStop(stop);
      const sheet = pastSheetAt(stop.lng, stop.lat);
      const zoom = sheet?.id === "dk-hmb" ? 14.5 : sheet?.id === "gb-os" ? 17 : 16;
      mapRef.current?.flyTo({ center: [stop.lng, stop.lat], zoom, duration: 1000 });
    };
    // The step back can land after the fallback below (it did in production
    // on 30 Sep 2026, though not in e2e), so whenever it comes it puts the
    // stop back in the address, without flying a second time.
    const walkId = selectedType === "walk" ? (selected as CuratedWalk | null)?.id : undefined;
    window.addEventListener("popstate", () => setTimeout(() => {
      if (!done) { go(); return; }
      if (walkId) history.replaceState(null, "", `#walk=${encodeURIComponent(walkId)}&stop=${encodeURIComponent(stop.id)}`);
    }, 0), { once: true });
    setTimeout(go, 300);
  }, [onSelectWalkStop, selected, selectedType]);

  // Closing the walk drops the resume point. Left behind, `#walk=` would reopen
  // on the next load a walk the reader had just shut. The ref is what keeps
  // this off the deep-link's back: on first render nothing is selected yet, and
  // without it the effect would wipe the incoming hash before it was read.
  const walkWasOpenRef = useRef(false);
  useEffect(() => {
    if (selectedType === "walk" && selected) { walkWasOpenRef.current = true; return; }
    if (!walkWasOpenRef.current) return;
    walkWasOpenRef.current = false;
    setActiveStopId(null);
    setWalkMapView(false);
    if (window.location.hash.startsWith("#walk=")) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, [selected, selectedType]);

  /**
   * The address follows the open record, so a teacher can paste what is on
   * the board into the class chat (reviewed 26 Sep 2026: "the address bar
   * stays at # when a place or walk is open"). `#site=&layer=&lat&lng` is the
   * deep link App.tsx already reads on load; a walk opened without a stop gets
   * `#walk=`. Only this effect's own keys are touched, so the timeline's
   * `year=` and the rest survive, and it only clears what it wrote. A /s/ path
   * stays when it names the open record and gives way to / when it does not,
   * because api/og.ts would otherwise rewrite the hash back to its own record.
   */
  /**
   * A collection someone sent as `#list=` (utils/listLink.ts). Read once at
   * mount; the list stays open behind any place opened from it, and closing
   * it takes `list=` off the address.
   */
  const [sharedList, setSharedList] = useState<import("./utils/listLink.ts").SharedList | null>(null);
  useEffect(() => {
    const data = /[#&]list=([A-Za-z0-9_-]+)/.exec(window.location.hash)?.[1];
    if (!data) return;
    void import("./utils/listLink.ts").then(({ decodeList }) => decodeList(data)).then((list) => {
      if (!list) return;
      setShowWelcome(false);
      setSharedList(list);
    });
  }, []);
  const closeSharedList = useCallback(() => {
    setSharedList(null);
    const strip = () => {
      const rest = window.location.hash.replace(/^#/, "").split("&").filter((p) => p && !/^list=/.test(p));
      history.replaceState(history.state, "", window.location.pathname + window.location.search + (rest.length ? `#${rest.join("&")}` : ""));
    };
    // Again after useBackDismiss has stepped back past the sheet's own history
    // entry: the entry it lands on is the one the link arrived with, list= and
    // all, so stripping only now would be undone a moment later.
    strip();
    window.addEventListener("popstate", () => setTimeout(strip, 0), { once: true });
    setTimeout(strip, 300);
  }, []);

  const siteHashRef = useRef(false);
  useEffect(() => {
    const OWN = /^(site|layer|lat|lng)=/;
    const rest = () => window.location.hash.replace(/^#/, "").split("&").filter((p) => p && !OWN.test(p));
    const rec = (Array.isArray(selected) ? selected[0] : selected) as { id?: string; lat?: number; lng?: number } | null;
    if (selectedType === "walk") {
      if (rec?.id && !window.location.hash.includes("walk=")) {
        history.replaceState(null, "", `/#${[`walk=${encodeURIComponent(rec.id)}`, ...rest()].join("&")}`);
      }
      siteHashRef.current = false;
      return;
    }
    if (rec?.id && selectedType) {
      const path = `/s/${selectedType}/${encodeURIComponent(rec.id)}`;
      const keepPath = window.location.pathname === path;
      const own = [`site=${encodeURIComponent(rec.id)}`, `layer=${selectedType}`];
      if (typeof rec.lat === "number" && typeof rec.lng === "number") own.push(`lat=${rec.lat.toFixed(5)}`, `lng=${rec.lng.toFixed(5)}`);
      history.replaceState(null, "", `${keepPath ? path + window.location.search : "/"}#${[...own, ...rest()].join("&")}`);
      siteHashRef.current = true;
    } else if (!rec && siteHashRef.current) {
      siteHashRef.current = false;
      const left = rest();
      history.replaceState(null, "", `${window.location.pathname.startsWith("/s/") ? "/" : window.location.pathname + window.location.search}${left.length ? `#${left.join("&")}` : ""}`);
    }
  }, [selected, selectedType]);

  const onClick = useCallback((e: MapLayerMouseEvent) => {
    const f = e.features?.[0];
    // Walk map view: the map is for orientation along the open walk. A stop
    // pin moves the walk onward; every other tap is ignored rather than let
    // replace the selection, because replacing it closes the walk and wipes
    // the `#walk=` resume point — a stray tap in the street must not cost
    // the reader their place. The bar's centre button is the way back.
    if (walkMapView && selectedType === "walk" && selected) {
      if ((f?.properties?.layerType as string) === "walk-stop") {
        const walk = walkIndex.current.get(f!.properties?.walkId);
        const stop = walk?.stops.find(st => st.id === f!.properties?.id);
        if (walk && stop) selectWalkStop(walk.id, stop);
      }
      return;
    }
    if (!f) { setSelected(null); return; }
    const layerId = (f.layer as { id?: string } | null)?.id ?? "";
    // Handle cluster expansion
    if (f.properties?.cluster) {
      const sourceMap: Record<string, string> = { runestone: "runestone-sites", battlefield: "battlefield-sites", monument: "monument-sites", building: "building-sites", folklore: "folklore-sites", shipwreck: "shipwreck-sites", museum: "museum-sites", rockart: "rockart-sites", unesco: "unesco-sites", industrial: "industrial-sites", thingsite: "thingsite-sites", barrow: "barrow-sites", megalith: "megalith-sites", townscape: "townscape-sites", plaque: "plaque-sites", stolperstein: "stolperstein-sites", lighthouse: "lighthouse-sites", byleksikon: "byleksikon-sites" };
      const sourceId = Object.entries(sourceMap).find(([k]) => layerId.includes(k))?.[1] ?? "heritage-sites";
      const src = mapRef.current?.getSource(sourceId) as GeoJSONSource;
      if (!src) return;
      src.getClusterExpansionZoom(f.properties.cluster_id).then((zoom) => {
        if (f.geometry.type === "Point")
          mapRef.current?.easeTo({ center: [f.geometry.coordinates[0], f.geometry.coordinates[1]], zoom, duration: 500 });
      }).catch(() => {});
      return;
    }
    if (f.geometry.type !== "Point" && f.properties?.layerType !== "route") return;
    const lt = f.properties?.layerType as LayerType;
    if (lt === "folklore") {
      const coordKey = f.properties?.coordKey as string;
      let records = folkloreByCoord.current.get(coordKey) ?? [];
      // Fallback: if coordKey lookup fails, try reconstructing from geometry
      if (records.length === 0 && f.geometry.type === "Point") {
        const [lng, lat] = f.geometry.coordinates;
        const fallbackKey = `${lat},${lng}`;
        records = folkloreByCoord.current.get(fallbackKey) ?? [];
        // Last resort: scan for nearest coord within ~10m
        if (records.length === 0) {
          for (const [ck, recs] of folkloreByCoord.current) {
            const [rlat, rlng] = ck.split(",").map(Number);
            if (Math.abs(rlat - lat) < 0.0002 && Math.abs(rlng - lng) < 0.0002) { records = recs; break; }
          }
        }
      }
      if (activeFolkloreCat !== "all") records = records.filter(r => r.category === activeFolkloreCat);
      if (records.length > 0) { setSelected(records); setSelectedType("folklore"); }
    } else if (lt === "runestone") {
      const stone = runestoneIndex.current.get(f.properties?.id);
      if (stone) { setSelected(stone); setSelectedType("runestone"); }
    } else if (lt === "battlefield") {
      const field = battlefieldIndex.current.get(f.properties?.id);
      if (field) { setSelected(field); setSelectedType("battlefield"); }
    } else if (lt === "monument") {
      const mon = monumentIndex.current.get(f.properties?.id);
      if (mon) { setSelected(mon); setSelectedType("monument"); }
    } else if (lt === "building") {
      const bld = buildingIndex.current.get(f.properties?.id);
      if (bld) { setSelected(bld); setSelectedType("building"); }
    } else if (lt === "shipwreck") {
      const wreck = shipwreckIndex.current.get(f.properties?.id);
      if (wreck) { setSelected(wreck); setSelectedType("shipwreck"); }
    } else if (lt === "museum") {
      const mus = museumIndex.current.get(f.properties?.id);
      if (mus) { setSelected(mus); setSelectedType("museum"); }
    } else if (lt === "rockart") {
      const rock = rockArtIndex.current.get(f.properties?.id);
      if (rock) { setSelected(rock); setSelectedType("rockart"); }
    } else if (lt === "unesco") {
      const site = unescoIndex.current.get(f.properties?.id);
      if (site) { setSelected(site); setSelectedType("unesco"); }
    } else if (lt === "industrial") {
      const ind = industrialIndex.current.get(f.properties?.id);
      if (ind) { setSelected(ind); setSelectedType("industrial"); }
    } else if (lt === "barrow") {
      const bar = barrowIndex.current.get(f.properties?.id);
      if (bar) { setSelected(bar); setSelectedType("barrow"); }
    } else if (lt === "megalith") {
      const meg = megalithIndex.current.get(f.properties?.id);
      if (meg) { setSelected(meg); setSelectedType("megalith"); }
    } else if (lt === "townscape") {
      const ts = townscapeIndex.current.get(f.properties?.id);
      if (ts) { setSelected(ts); setSelectedType("townscape"); }
    } else if (lt === "plaque") {
      const plq = plaqueIndex.current.get(f.properties?.id);
      if (plq) { setSelected(plq); setSelectedType("plaque"); }
    } else if (lt === "stolperstein") {
      const stn = stolpersteinIndex.current.get(f.properties?.id);
      if (stn) { setSelected(stn); setSelectedType("stolperstein"); }
    } else if (lt === "lighthouse") {
      const lh = lighthouseIndex.current.get(f.properties?.id);
      if (lh) { setSelected(lh); setSelectedType("lighthouse"); }
    } else if (lt === "thingsite") {
      const site = thingsiteIndex.current.get(f.properties?.id);
      if (site) { setSelected(site); setSelectedType("thingsite"); }
    } else if (lt === "byleksikon") {
      const obl = byleksikonIndex.current.get(f.properties?.id);
      if (obl) { setSelected(obl); setSelectedType("byleksikon"); }
    } else if (lt === "route") {
      const route = routeIndex.current.get(f.properties?.id);
      if (route) { setSelected(route); setSelectedType("route"); mapRef.current?.flyTo({ center: [route.lng, route.lat], zoom: 8, duration: 1000 }); }
    } else if (lt === "walk") {
      const walk = walkIndex.current.get(f.properties?.id);
      // Opened, not started. The panel offers the first stop and the reader
      // decides; the camera frames the walk's whole extent, which keeps the
      // pin they aimed at in view and shows what the walk covers.
      if (walk) { setSelected(walk); setSelectedType("walk"); setActiveStopId(null); frameWalk(walk, true); }
    } else if ((f.properties?.layerType as string) === "walk-stop") {
      // Stop pins only exist while their walk is open, and an open walk means
      // either the sheet's backdrop is eating map clicks or walk map view
      // returned above — so today this branch is belt-and-braces for a state
      // that should not occur, not a path with traffic. It stays because it
      // is what "a stop pin was clicked" should do if the chrome ever changes.
      const walk = walkIndex.current.get(f.properties?.walkId);
      const stop = walk?.stops.find(st => st.id === f.properties?.id);
      if (walk && stop) { setSelected(walk); setSelectedType("walk"); selectWalkStop(walk.id, stop); }
    } else if (f.geometry.type === "Point") {
      setSelected({ ...f.properties, lng: (f.geometry as GeoJSON.Point).coordinates[0], lat: (f.geometry as GeoJSON.Point).coordinates[1] });
      setSelectedType("heritage");
    }
  }, [activeFolkloreCat, selectWalkStop, walkMapView, selectedType, selected]);

  const handleLocate = useCallback(() => {
    setShowWelcome(false);
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        const loc: [number, number] = [pos.coords.longitude, pos.coords.latitude];
        setUserLocation(loc);
        mapRef.current?.flyTo({ center: loc, zoom: 12, duration: 2000 });
      },
      () => { setLoading(false); setError(t("error.noLocation", lang)); },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
    );
  }, []);

  // --- COMPASS (extracted to useCompass hook) ---
  // Mid-walk the compass points at the stop being read, not at the walk's
  // anchor coordinate — an arrow toward the centroid of a 700-metre walk is
  // an arrow toward nothing the reader can stand on.
  const activeWalkStop = useMemo(() => {
    if (selectedType !== "walk" || !selected || !activeStopId) return null;
    return (selected as CuratedWalk).stops.find(st => st.id === activeStopId) ?? null;
  }, [selected, selectedType, activeStopId]);
  const {
    compassActive, compassHeading, compassUserPos, compassTarget,
    handleCompassActivate, closeCompass,
  } = useCompass({ selected: activeWalkStop ?? selected, selectedType, lang, userLocation, setUserLocation, handleLocate });

  const handleBadgeEarned = useCallback((badge: BadgeDef) => {
    if (badgeToastTimer.current) clearTimeout(badgeToastTimer.current);
    setBadgeToast(badge);
    badgeToastTimer.current = setTimeout(() => setBadgeToast(null), 4000);
  }, []);

  const switchGroup = useCallback((group: CategoryGroup) => {
    setActiveGroup(group);
    // If group has only 1 sub-tab, select it directly (skip redundant "all" → sub-tab click)
    const tabs = GROUP_TABS[group];
    setActiveTab(tabs.length === 1 ? tabs[0] : "all");
    const m = mapRef.current;
    if (!m) return;
    const b = m.getBounds();
    const bbox: BBox = { minLng: b.getWest(), minLat: b.getSouth(), maxLng: b.getEast(), maxLat: b.getNorth() };
    fetchForLayers(getVisibleLayers(group, "all"), bbox);
  }, [fetchForLayers, getVisibleLayers]);

  const switchSubTab = useCallback((tab: ActiveTab) => {
    setActiveTab(tab);
    const m = mapRef.current;
    if (!m) return;
    const b = m.getBounds();
    const bbox: BBox = { minLng: b.getWest(), minLat: b.getSouth(), maxLng: b.getEast(), maxLat: b.getNorth() };
    fetchForLayers(getVisibleLayers(activeGroup, tab), bbox);
  }, [activeGroup, fetchForLayers, getVisibleLayers]);

  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    if (showHeritage) ids.push("clusters", "unclustered-point");
    // Ghosts resolve to their own layer's index through properties.layerType.
    if (timelineYear != null) ids.push("timeline-ghost-point");
    if (showFolklore) ids.push("folklore-clusters", "folklore-point", "folklore-story-count");
    if (showRunestone) ids.push("runestone-clusters", "runestone-point");
    if (showBattlefield) ids.push("battlefield-clusters", "battlefield-point");
    if (showMonument) ids.push("monument-clusters", "monument-point");
    if (showBuildingPoints) ids.push("building-clusters", "building-point");
    if (showShipwreck) ids.push("shipwreck-clusters", "shipwreck-point");
    if (showMuseum) ids.push("museum-clusters", "museum-point");
    if (showRockArt) ids.push("rockart-clusters", "rockart-point");
    if (showUnesco) ids.push("unesco-clusters", "unesco-point");
    if (showIndustrial) ids.push("industrial-clusters", "industrial-point");
    if (showBarrow) ids.push("barrow-clusters", "barrow-point");
    if (showMegalith) ids.push("megalith-clusters", "megalith-point");
    if (showTownscape) ids.push("townscape-clusters", "townscape-point", "townscape-curated-point");
    if (showPlaque) ids.push("plaque-clusters", "plaque-point");
    if (showStolperstein) ids.push("stolperstein-clusters", "stolperstein-point");
    if (showLighthouse) ids.push("lighthouse-clusters", "lighthouse-point");
    if (showThingSite) ids.push("thingsite-clusters", "thingsite-point");
    if (showByleksikon) ids.push("byleksikon-clusters", "byleksikon-point");
    if (showRoute) ids.push("route-line");
    if (showWalk) ids.push("walk-point");
    // Not gated on showWalk. A walk stays open while the reader switches to
    // another tab to see what else is on the street, and its stops have to stay
    // clickable through that.
    if (walkStopsVisible) ids.push("walk-stop-point", "walk-stop-number");
    return ids;
  }, [showHeritage, showFolklore, showRunestone, showBattlefield, showMonument, showBuildingPoints, showShipwreck, showMuseum, showRockArt, showUnesco, showIndustrial, showBarrow, showMegalith, showPlaque, showStolperstein, showLighthouse, showThingSite, showByleksikon, showRoute, showWalk, walkStopsVisible, timelineYear]);

  // ============================================================
  // NEARBY PINS — computed from all index refs
  // ============================================================

  const nearbyPins = useMemo((): NearbyPinItem[] => {
    if (!selected) return [];
    // Get current pin lat/lng
    let lat: number, lng: number, currentId: string;
    if (selectedType === "folklore") {
      const recs = selected as FolkloreRecord[];
      if (!recs.length) return [];
      lat = recs[0].lat; lng = recs[0].lng; currentId = recs[0].id;
    } else {
      lat = selected.lat; lng = selected.lng; currentId = selected.id;
    }
    if (!lat || !lng) return [];

    const RADIUS_KM = 5;
    const DEG_OFFSET = 0.05; // ~5km bounding box pre-filter
    const candidates: NearbyPinItem[] = [];

    // Deduplicate nearby pins by coordinate key — prevent showing the same physical location from different layers
    const seenCoords = new Set<string>();
    // Mark current pin's coordinate as seen
    seenCoords.add(`${lat.toFixed(3)},${lng.toFixed(3)}`);

    const addFromIndex = (index: Map<string, { id: string; name: string; lat: number; lng: number }>, layerType: LayerType) => {
      for (const [, pin] of index) {
        if (pin.id === currentId) continue;
        // Bounding box pre-filter
        if (Math.abs(pin.lat - lat) > DEG_OFFSET || Math.abs(pin.lng - lng) > DEG_OFFSET) continue;
        const dist = haversineKm(lat, lng, pin.lat, pin.lng);
        if (dist <= RADIUS_KM && dist > 0.05) {
          const ck = `${pin.lat.toFixed(3)},${pin.lng.toFixed(3)}`;
          if (seenCoords.has(ck)) continue;
          seenCoords.add(ck);
          candidates.push({ id: pin.id, name: pin.name, layerType, distance: dist, lng: pin.lng, lat: pin.lat });
        }
      }
    };

    // Heritage: from features, not an index map
    for (const f of heritageFeatures) {
      if (f.geometry.type !== "Point" || !f.properties) continue;
      const fId = f.properties.id as string;
      if (fId === currentId) continue;
      const [fLng, fLat] = (f.geometry as GeoJSON.Point).coordinates;
      if (Math.abs(fLat - lat) > DEG_OFFSET || Math.abs(fLng - lng) > DEG_OFFSET) continue;
      const dist = haversineKm(lat, lng, fLat, fLng);
      if (dist <= RADIUS_KM && dist > 0.05) {
        const ck = `${fLat.toFixed(3)},${fLng.toFixed(3)}`;
        if (seenCoords.has(ck)) continue;
        seenCoords.add(ck);
        candidates.push({ id: fId, name: f.properties.name as string || "?", layerType: "heritage", distance: dist, lng: fLng, lat: fLat });
      }
    }

    addFromIndex(folkloreIndex.current, "folklore");
    addFromIndex(runestoneIndex.current, "runestone");
    addFromIndex(battlefieldIndex.current, "battlefield");
    addFromIndex(monumentIndex.current, "monument");
    addFromIndex(buildingIndex.current, "building");
    addFromIndex(shipwreckIndex.current, "shipwreck");
    addFromIndex(museumIndex.current, "museum");
    addFromIndex(rockArtIndex.current, "rockart");
    addFromIndex(unescoIndex.current, "unesco");
    addFromIndex(industrialIndex.current, "industrial");
    addFromIndex(barrowIndex.current, "barrow");
    addFromIndex(megalithIndex.current, "megalith");
    addFromIndex(townscapeIndex.current, "townscape");
    addFromIndex(plaqueIndex.current, "plaque");
    addFromIndex(stolpersteinIndex.current, "stolperstein");
    addFromIndex(lighthouseIndex.current, "lighthouse");
    addFromIndex(thingsiteIndex.current, "thingsite");
    addFromIndex(byleksikonIndex.current, "byleksikon");

    candidates.sort((a, b) => a.distance - b.distance);
    // The bearing from this pin, so the list can say which way. Two nearby
    // pins are often named the same ("Rundhøj", "Rundhøj" at Jelling), and a
    // direction is what tells them apart without another word to translate.
    const toRad = Math.PI / 180;
    return candidates.slice(0, 4).map((c) => {
      if (c.lat == null || c.lng == null) return c;
      const y = Math.sin((c.lng - lng) * toRad) * Math.cos(c.lat * toRad);
      const x = Math.cos(lat * toRad) * Math.sin(c.lat * toRad) - Math.sin(lat * toRad) * Math.cos(c.lat * toRad) * Math.cos((c.lng - lng) * toRad);
      return { ...c, bearing: (Math.atan2(y, x) / toRad + 360) % 360 };
    });
  }, [selected, selectedType, heritageFeatures]);

  const handleSelectNearby = useCallback((item: NearbyPinItem) => {
    // Look up the pin from the appropriate index and select it
    const lt = item.layerType;
    // A walk opens whole, from the walk files, as the search does. The item
    // here is a name and a position; the panel read `walk.stops` off it and
    // threw, so a walk opened from the profile, a list or explore mode showed
    // «Could not load» (the Old Edinburgh Club's secretary, 4 Oct 2026).
    if (lt === "walk") {
      void loadWalks().then(walks => {
        const walk = walks.find(w => w.id === item.id);
        if (!walk) return;
        setSelected(walk);
        setSelectedType("walk");
        setActiveStopId(null);
        frameWalk(walk, true);
      });
      return;
    }
    let found = false;
    if (lt === "heritage") {
      // Through the ref, not the state: the search's retry calls this from a
      // closure made at the click, and the state it captured then did not yet
      // hold the pin it is now asked to open, so the panel got the name alone.
      const f = heritageFeaturesRef.current.find(f => f.properties?.id === item.id);
      if (f && f.geometry.type === "Point") {
        setSelected({ ...f.properties, lng: (f.geometry as GeoJSON.Point).coordinates[0], lat: (f.geometry as GeoJSON.Point).coordinates[1] });
        setSelectedType("heritage");
        mapRef.current?.flyTo({ center: [(f.geometry as GeoJSON.Point).coordinates[0], (f.geometry as GeoJSON.Point).coordinates[1]], zoom: 14, duration: 1000 });
        found = true;
      }
    } else if (lt === "folklore") {
      const rec = folkloreIndex.current.get(item.id);
      if (rec) {
        const coordKey = `${rec.lng.toFixed(5)},${rec.lat.toFixed(5)}`;
        const records = folkloreByCoord.current.get(coordKey) ?? [rec];
        setSelected(records); setSelectedType("folklore");
        mapRef.current?.flyTo({ center: [rec.lng, rec.lat], zoom: 14, duration: 1000 });
        found = true;
      }
    } else {
      const indexes: Record<string, React.MutableRefObject<Map<string, { id: string; name: string; lat: number; lng: number }>>> = {
        runestone: runestoneIndex, battlefield: battlefieldIndex, monument: monumentIndex,
        building: buildingIndex, shipwreck: shipwreckIndex, museum: museumIndex,
        rockart: rockArtIndex, unesco: unescoIndex, industrial: industrialIndex,
        barrow: barrowIndex, megalith: megalithIndex, townscape: townscapeIndex, plaque: plaqueIndex, stolperstein: stolpersteinIndex, lighthouse: lighthouseIndex,
        thingsite: thingsiteIndex, route: routeIndex, byleksikon: byleksikonIndex,
      };
      const idx = indexes[lt];
      if (idx) {
        const pin = idx.current.get(item.id);
        if (pin) {
          setSelected(pin); setSelectedType(lt);
          mapRef.current?.flyTo({ center: [pin.lng, pin.lat], zoom: 14, duration: 1000 });
          found = true;
        }
      }
    }
    // Fallback: use stored coordinates when the layer index hasn't loaded yet
    if (!found && item.lng != null && item.lat != null) {
      const minPin = { id: item.id, name: item.name, lng: item.lng, lat: item.lat, country: item.country || "DK" };
      if (lt === "folklore") {
        setSelected([{ ...minPin, title: item.name, text: "", type: "", location: item.name }]);
      } else {
        setSelected(minPin);
      }
      setSelectedType(lt);
      mapRef.current?.flyTo({ center: [item.lng, item.lat], zoom: 14, duration: 1000 });
    }
  }, [heritageFeatures, loadWalks, frameWalk]);

  // --- KNOWLEDGE GRAPH NAVIGATION ---
  const handleNavigateRelation = useCallback((rel: WikidataRelation) => {
    // Search all indexes for a matching wikidataId
    interface QidIndexed { id: string; name: string; lat: number; lng: number; wikidataId?: string }
    const allIndexes: { ref: React.MutableRefObject<Map<string, QidIndexed>>; lt: LayerType }[] = [
      { ref: runestoneIndex, lt: "runestone" }, { ref: battlefieldIndex, lt: "battlefield" },
      { ref: monumentIndex, lt: "monument" }, { ref: museumIndex, lt: "museum" },
      { ref: megalithIndex, lt: "megalith" }, { ref: townscapeIndex, lt: "townscape" }, { ref: plaqueIndex, lt: "plaque" }, { ref: stolpersteinIndex, lt: "stolperstein" }, { ref: lighthouseIndex, lt: "lighthouse" }, { ref: thingsiteIndex, lt: "thingsite" },
      { ref: unescoIndex, lt: "unesco" }, { ref: routeIndex, lt: "route" },
    ];
    for (const { ref: idx, lt } of allIndexes) {
      for (const [, pin] of idx.current) {
        if (pin.wikidataId === rel.wikidataId) {
          setSelected(pin); setSelectedType(lt);
          mapRef.current?.flyTo({ center: [pin.lng, pin.lat], zoom: 14, duration: 1000 });
          return;
        }
      }
    }
    // Not found in Fornland — try to open Wikipedia article inline
    if (rel.wikipediaUrl) {
      // Parse Wikipedia URL to extract lang + title: https://XX.wikipedia.org/wiki/TITLE
      const wikiMatch = rel.wikipediaUrl.match(/https?:\/\/(\w+)\.wikipedia\.org\/wiki\/(.+)/);
      if (wikiMatch) {
        setRelationViewer({ lang: wikiMatch[1], title: decodeURIComponent(wikiMatch[2].replace(/_/g, " ")), source: "wikipedia" });
        return;
      }
    }
    // Fallback: try English Wikipedia with the label
    if (rel.label) {
      setRelationViewer({ lang: "en", title: rel.label, source: "wikipedia" });
      return;
    }
    // Last resort: open Wikidata page in new tab
    window.open(`https://www.wikidata.org/wiki/${rel.wikidataId}`, "_blank", "noopener");
  }, []);

  // Wire up search select ref (defined early, implemented after handleSelectNearby)
  handleSearchSelectRef.current = (result: UnifiedSearchResult) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults({ local: [], places: [] });
    activeSearchQueryRef.current = "";
    setShowWelcome(false);
    if (result.layerType === "ask") {
      // Apply what the sentence asked for: the tab through the same switchers
      // the nav uses (so the fetch for the new tab runs), the period filter,
      // and a flight to the place through the geocoder the places section
      // already uses. Each is independent; a sentence may carry one or all.
      const a = result.ask;
      if (!a) return;
      if (a.tab) { const g = LAYER_GROUP[a.tab]; if (g) { switchGroup(g); switchSubTab(a.tab); } }
      // The period filter exists on the heritage tab alone (useGeoJsonLayers
      // filters heritageFeatures by it), so a period with any other tab is
      // dropped rather than left set where nothing shows it.
      if ((a.tab ?? activeTab) === "heritage") setActivePeriod(a.period ?? "all");
      if (a.place) {
        const params = new URLSearchParams({ format: "json", q: a.place, viewbox: "-74,49,32,84", bounded: "1", limit: "1", "accept-language": lang });
        fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { "User-Agent": "Fornland/1.0" } })
          .then(r => r.ok ? r.json() : [])
          .then((hits: { lat: string; lon: string }[]) => {
            const h = hits[0];
            if (h) mapRef.current?.flyTo({ center: [parseFloat(h.lon), parseFloat(h.lat)], zoom: 11, duration: 1500 });
          })
          .catch(() => { /* no place found: the filters still applied */ });
      }
    } else if (result.layerType === "place") {
      const zoom = /city|town|village|county|state|region/.test(result.subtitle || "") ? 12 : 14;
      mapRef.current?.flyTo({ center: [result.lng, result.lat], zoom, duration: 1500 });
    } else if (result.layerType === "walk") {
      // A walk is opened whole, from the walk files, never from the search
      // row: the row has a name and a position and no stops, and the panel
      // and the map both read the stops.
      void loadWalks().then(walks => {
        const walk = walks.find(w => w.id === result.id);
        if (!walk) return;
        setSelected(walk);
        setSelectedType("walk");
        frameWalk(walk, true);
      });
    } else {
      const item = { id: result.id, name: result.name, layerType: result.layerType, distance: 0, lng: result.lng, lat: result.lat, country: result.country as Country };
      // A hit from the corpus search (api/search.ts) is often a record the
      // view had not loaded, and handleSelectNearby then opens it from name
      // and position alone. The flight loads the layer; once the record is in
      // its index, open it again so the panel gets the register's fields.
      const layerIndex = ({
        runestone: runestoneIndex, battlefield: battlefieldIndex, monument: monumentIndex, building: buildingIndex,
        shipwreck: shipwreckIndex, museum: museumIndex, rockart: rockArtIndex, unesco: unescoIndex,
        industrial: industrialIndex, barrow: barrowIndex, megalith: megalithIndex, townscape: townscapeIndex,
        plaque: plaqueIndex, stolperstein: stolpersteinIndex, lighthouse: lighthouseIndex, thingsite: thingsiteIndex,
        byleksikon: byleksikonIndex, folklore: folkloreIndex,
      } as Record<string, React.MutableRefObject<Map<string, unknown>>>)[result.layerType];
      /**
       * The Swedish and Norwegian names come from Wikidata, keyed on an id the
       * live register pin does not always carry (a K-samsök fmi number, not the
       * uuid), so the exact id may never load. The pin itself does: the nearest
       * heritage pin within 150 m whose name matches is the same monument, and
       * it opens with the register's fields instead of the name alone. Found
       * 26 Sep 2026 on Anundshög, Borrehaugene and Jelling.
       */
      const fold = (t: string) => t.toLocaleLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9æøþð]+/g, "");
      const nearbyTwin = (): string | null => {
        if (result.layerType !== "heritage") return null;
        const want = fold(result.name);
        const k = Math.cos((result.lat * Math.PI) / 180);
        let best: { id: string; d: number } | null = null;
        for (const f of heritageFeaturesRef.current) {
          const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
          const d = Math.hypot((lng - result.lng) * k, lat - result.lat) * 111_320;
          if (d > 150) continue;
          const name = fold(String(f.properties?.name ?? ""));
          // Spaces are gone in both: the register writes "Anunds hög", Wikidata
          // "Anundshög".
          if (!name || !want || !(name.includes(want) || want.includes(name))) continue;
          if (!best || d < best.d) best = { id: String(f.properties?.id), d };
        }
        return best?.id ?? null;
      };
      /**
       * The register pin the search row names, by identity rather than by
       * name: a Swedish row carries the lämning's uuid (scripts/build-nordic-
       * search-names.mjs resolves the fmi number) and the pin carries it as
       * raaUuid; a Norwegian row carries the kulturminneId, which the pin's id
       * carries with a "-1" suffix now and then. Name and distance are the
       * last resort.
       */
      const seUuid = /^se-([0-9a-f-]{36})$/.exec(result.id)?.[1];
      const registerTwin = (): string | null => {
        if (result.layerType !== "heritage") return null;
        for (const f of heritageFeaturesRef.current) {
          const id = String(f.properties?.id ?? "");
          if (id === result.id) return id;
          if (seUuid && f.properties?.raaUuid === seUuid) return id;
          if (result.id.startsWith("no-") && id.replace(/-\d+$/, "") === result.id) return id;
        }
        return nearbyTwin();
      };
      const loaded = () => result.layerType === "heritage"
        ? !!registerTwin()
        : !!layerIndex?.current.has(result.id);
      if (loaded()) {
        handleSelectNearby(result.layerType === "heritage" ? { ...item, id: registerTwin() ?? item.id } : item);
      } else {
        handleSelectNearby(item);
        const token = ++searchUpgradeTokenRef.current;
        const retry = (n: number) => {
          // 50 × 500 ms: the Swedish register answers in pages and can take
          // twenty seconds over a wide view.
          if (searchUpgradeTokenRef.current !== token || n > 50) return;
          if (loaded()) {
            handleSelectNearby(result.layerType === "heritage" ? { ...item, id: registerTwin() ?? item.id } : item);
            return;
          }
          setTimeout(() => retry(n + 1), 500);
        };
        setTimeout(() => retry(0), 500);
      }
    }
  };

  // --- 3D TERRAIN TOGGLE (extracted to useTerrain3D hook) ---
  useTerrain3D(mapRef, terrain3d, isMobileOrTablet);

  // --- Havuhyrerne (se constants/seaMonsters.ts og docs/HAVUHYRER.md) ---
  const seaMonsterUrls = useSeaMonsters(resolved, wideView);

  // ============================================================
  // RENDER
  // ============================================================

  /** What the pin colours mean, for the legend in the status bar and the phone's button. */
  const legendSections: LegendSection[] = [
            { title: t("status.sites", lang), items: showHeritage ? periods.filter(p => p.id !== "all").map(p => ({ label: p.label, color: PERIOD_COLORS[p.id] ?? "#9b8ec4" })) : [] },
            { title: t("status.folklore", lang), items: showFolklore ? folkloreCategories.filter(c => c.id !== "all").map(c => ({ label: c.label, color: c.color })) : [] },
            { title: t("status.monuments", lang), items: showMonument ? monumentCategories.filter(c => c.id !== "all").map(c => ({ label: c.label, color: c.color })) : [] },
            // One colour each, the same literals the layers' paint uses.
            { items: ([
              [showRunestone, "status.runestones", "#c47a2e"], [showBattlefield, "status.battlefields", "#c62828"],
              [showShipwreck, "status.shipwrecks", "#0288d1"], [showMuseum, "status.museums", "#2e7d4f"],
              [showRockArt, "status.rockart", "#7b1fa2"], [showUnesco, "status.unesco", "#1565c0"],
              [showIndustrial, "status.industrial", "#607d8b"], [showBarrow, "status.barrows", "#6a1b9a"],
              [showMegalith, "status.megaliths", "#5e35b1"], [showTownscape, "status.townscapes", "#d84315"],
              [showPlaque, "status.plaques", "#8d6e63"], [showStolperstein, "status.stolpersteine", "#a08040"],
              [showLighthouse, "status.lighthouses", "#e68a00"], [showThingSite, "status.thingsites", "#4527a0"],
              [showByleksikon, "status.byleksikon", "#5c8a8a"],
            ] as [boolean, string, string][]).filter(([on]) => on).map(([, key, color]) => ({ label: t(key, lang), color })) },
  ];

  // Memoised because MapGL only reads it at mount; as an inline IIFE it ran
  // localStorage.getItem + JSON.parse on every single render.
  const initialViewState = useMemo(() => {
    // A deep link mounts the map on the linked place, the survey-sheet link
    // on Denmark. Mounting on the stored view first meant one full layer
    // fetch for a bbox nobody asked for; Historic England, Wales, kulturarv.dk
    // and K-samsök answered it on every load, from a Vilnius link as much as
    // from a Danish one. The onLoad handler reads the same parser and fetches
    // for this view without flying.
    const linked = parseDeepLinkTarget(window.location.hash);
    if (linked) return linked;
    // A shared /s/ link has no coordinate in it, on purpose: they were taken
    // out in May 2026 because they are not part of a site's identity and cost
    // 35 000 canonical warnings. The page still carries the coordinate in its
    // own JSON-LD, which api/og.ts prints for the crawler, so read it there.
    // Without this the map opens on the stored view, the layers fetch for that
    // viewport, and the pin the link names is never in the index: measured on
    // 18 Sep 2026, 30 302 of the sitemap's URLs opened an empty map.
    if (window.location.pathname.startsWith("/s/")) {
      const fromPage = coordinateFromPageMetadata(document);
      if (fromPage) return { ...fromPage, zoom: 16 };
    }
    try {
      const s = localStorage.getItem("fornland-map-view");
      if (s) { const v = JSON.parse(s); return { longitude: v.lng, latitude: v.lat, zoom: v.zoom }; }
    } catch { /* ignore */ }
    const langCenter: Record<string, { longitude: number; latitude: number; zoom: number }> = { da: { longitude: 10.4, latitude: 56.0, zoom: 7 }, no: { longitude: 10.0, latitude: 62.0, zoom: 5 }, sv: { longitude: 16.0, latitude: 62.5, zoom: 5 }, fi: { longitude: 25.5, latitude: 63.5, zoom: 5 }, is: { longitude: -19.0, latitude: 65.0, zoom: 6 }, kl: { longitude: -51.7, latitude: 64.2, zoom: 6 }, en: { longitude: -2.0, latitude: 54.0, zoom: 5.5 } };
    return langCenter[lang] || { longitude: 12.0, latitude: 55.8, zoom: 8 };
  }, [lang]);

  return (
    // <main> landmark so screen readers can target the primary content region.
    <main className={appStyles.root}>
      {/* Skip-to-content link — first focusable element on the page.
          Visible only when focused (Tab from address bar). Lands the user
          on the search input, which is the primary keyboard entry point
          since the map canvas itself isn't keyboard-navigable. */}
      <a
        href="#fornland-search"
        className="skip-link"
        onClick={(e) => { e.preventDefault(); searchInputRef.current?.focus(); }}
      >
        {t("a11y.skipToSearch", lang)}
      </a>
      {/* The landing view had no heading at all, so a reader arriving on it
          was told the language and then handed a map canvas. The wordmark
          cannot carry this: it is a button that opens the about modal, and a
          heading that is also a control is neither. Hidden rather than drawn,
          because the wordmark already says it to everyone else. */}
      {/* Naar et panel er aabent, baerer panelet sidens overskrift, og denne
          ville vaere den anden <h1> paa samme side. Maalt 21/9-2026 i det
          renderede DOM: to <h1> paa hver /s/-side hvor panelet aabner, den
          ene «Fornland, kart over kulturminner i Nord- og Sentral-Europa» og
          den anden stedets navn. Paa landingsudsnittet er der ingen anden, og
          da er den her det eneste der fortaeller en skaermlaeser hvad siden
          er. */}
      {!selected && <h1 className="sr-only">{t("a11y.pageTitle", lang)}</h1>}
      <MapGL
        ref={mapRef}
        initialViewState={initialViewState}
        // Read once, when the map is built; the label effect above renames the
        // button on a later language change, and runs too early to do it here.
        locale={{ "AttributionControl.ToggleAttribution": t("map.credits", lang) }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyle}
        maxBounds={
          // The historical map exists over Denmark and nowhere else, so the
          // mode narrows the world to it: panning past the survey's edge
          // would only show bare paper. Released when the mode goes off.
          // The southern edge was 49°N, which was the south of England and the
          // Channel Islands when the map stopped at the British Isles. Germany
          // reaches 47.27 at Oberstdorf, so Munich, Stuttgart, Freiburg and the
          // whole of Bavaria and Baden-Württemberg sat outside it: a deep link
          // to Munich landed the camera at 49.02 and the registers below that
          // line could not be reached at all. Measured and moved 5 Sep 2026.
          histMap ? [[6.0, 53.8], [16.5, 58.5]] : [[-75, 47], [32, 84]]
        }
        bearing={0}
        maxPitch={0}
        dragRotate={false}
        touchZoomRotate={true}
        pitchWithRotate={false}
        onMoveEnd={onMoveEnd}
        onLoad={onLoad}
        onClick={onClick}
        interactiveLayerIds={interactiveLayerIds}
        onMouseEnter={() => setMapCursor("pointer")}
        onMouseLeave={() => setMapCursor("")}
        cursor={mapCursor}
        attributionControl={{ compact: true, customAttribution: "\u00a9 OpenStreetMap contributors" }}
      >
        {/* Zoom/compass controls rendered outside map as custom buttons */}

        {/* === 3D TERRAIN SOURCE === */}
        {terrain3d && !isMobileOrTablet && (
          <Source id="terrain-dem" type="raster-dem"
            tiles={["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"]}
            encoding="terrarium" tileSize={256} maxzoom={15} />
        )}

        {/* === HAVUHYRERNE ===
            Fire træsnit fra 1555-1568, hvert lagt hvor dets egen billedtekst
            sætter det. Rammen regnes i Mercator af seaMonsterCorners, så de
            nordligste ikke trykkes flade. */}
        {SEA_MONSTERS.map((uhyre) => seaMonsterUrls[uhyre.id] && (
          <Source key={uhyre.id} id={`sea-monster-src-${uhyre.id}`} type="image"
            url={seaMonsterUrls[uhyre.id]} coordinates={seaMonsterCorners(uhyre)}>
            <Layer id={`sea-monster-layer-${uhyre.id}`} type="raster"
              paint={{ "raster-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.7, 8, 0], "raster-fade-duration": 0 }} />
          </Source>
        ))}

        {/* === HERITAGE LAYER === */}
        {/* The railway net of the collecting years, drawn under the pins when
            the historical map is on. Fertner (KU), CC BY; opened by 1898 and
            not closed before 1887, so it is the net Tang Kristensen could
            actually ride. Ferries dash. */}
        {histMap && (
          <Source id="etk-railways" type="geojson" data="/data/etk-railways.json">
            <Layer
              id="etk-rail-lines"
              type="line"
              filter={["all",
                ["<=", ["coalesce", ["get", "opened"], 9999], 1898],
                [">", ["coalesce", ["get", "closed"], 9999], 1887],
                ["!", ["get", "ferry"]],
              ]}
              paint={{ "line-color": "#4a3a2a", "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.8, 11, 1.6, 14, 2.4], "line-opacity": 0.75 }}
            />
            <Layer
              id="etk-rail-ferries"
              type="line"
              filter={["all",
                ["<=", ["coalesce", ["get", "opened"], 9999], 1898],
                [">", ["coalesce", ["get", "closed"], 9999], 1887],
                ["get", "ferry"],
              ]}
              paint={{ "line-color": "#4a3a2a", "line-width": 1.2, "line-opacity": 0.5, "line-dasharray": [2, 3] }}
            />
          </Source>
        )}
        <Source id="heritage-sites" type="geojson" data={heritageGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38} clusterProperties={clusterProps}>
          <Layer id="clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": dominantPeriodColor as MapExpression as never, "circle-opacity": showHeritage ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showHeritage ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showHeritage ? 1 : 0 }} />
          <Layer id="unclustered-point" type="circle" filter={noCluster}
            paint={{ "circle-color": periodColorExpr as MapExpression as never, "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 3.5, 10, 6, 14, 10], "circle-stroke-width": (showHeritage ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke, "circle-opacity": showHeritage ? (timelineFade as MapExpression as never) : 0 }} />
        </Source>
        {/* The timeline's ghosts: what the slider's year has not built yet
            (a hollow ring in the period colour) and what it has already lost
            (ash grey). One unclustered source across the period-bearing
            layers. No minzoom: it had one at 8, and at an early year that
            emptied the map at country zoom, because nearly every pin is a
            ghost then and the live clusters are left almost bare — which
            reads as the map breaking rather than as the past being empty.
            Empty while the bar is closed; useGeoJsonLayers::splitByYear
            writes `ts`. */}
        <Source id="timeline-ghosts" type="geojson" data={ghostGeoJSON}>
          <Layer id="timeline-ghost-point" type="circle"
            filter={showUnbuilt ? ["has", "ts"] : ["==", ["get", "ts"], "after"]}
            paint={{
              "circle-color": ["case", ["==", ["get", "ts"], "after"], "#8a8a8a", periodColorExpr] as MapExpression as never,
              "circle-opacity": ["case", ["==", ["get", "ts"], "after"], 0.35, 0] as MapExpression as never,
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 3.5, 10, 6, 14, 10],
              "circle-stroke-width": pinStrokeWidth as MapExpression as never,
              "circle-stroke-color": ["case", ["==", ["get", "ts"], "after"], pinStroke, periodColorExpr] as MapExpression as never,
              "circle-stroke-opacity": ["case", ["==", ["get", "ts"], "after"], 0.45, 0.75] as MapExpression as never,
            }} />
        </Source>

        {/* === FOLKLORE LAYER === */}
        <Source id="folklore-sites" type="geojson" data={folkloreGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="folklore-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#d4a843", "circle-opacity": showFolklore ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showFolklore ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="folklore-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showFolklore ? 1 : 0 }} />
          <Layer id="folklore-glow" type="circle" filter={noCluster}
            paint={{
              "circle-color": folkloreCatColorExpr as MapExpression as never,
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 8, 10, 14, 14, 20],
              "circle-opacity": showFolklore ? 0.15 : 0,
              "circle-stroke-width": 0, "circle-blur": 0.05,
            }} />
          <Layer id="folklore-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": folkloreCatColorExpr as MapExpression as never,
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 12],
              "circle-stroke-width": (showFolklore ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showFolklore ? 1 : 0,
            }} />
          <Layer id="folklore-story-count" type="symbol" filter={["all", noCluster, [">", ["get", "storyCount"], 1]] as FilterSpecification}
            layout={{ "text-field": ["to-string", ["get", "storyCount"]], "text-size": 11, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": resolved === "dark" ? "rgba(0,0,0,0.9)" : "rgba(255,255,255,0.9)", "text-halo-width": 1.5, "text-opacity": showFolklore ? 1 : 0 }} />
        </Source>

        {/* === RUNESTONE LAYER === */}
        <Source id="runestone-sites" type="geojson" data={runestoneGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="runestone-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#c47a2e", "circle-opacity": showRunestone ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showRunestone ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="runestone-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showRunestone ? 1 : 0 }} />
          <Layer id="runestone-point" type="circle" filter={noCluster}
            paint={{ "circle-color": "#c47a2e", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10], "circle-stroke-width": (showRunestone ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke, "circle-opacity": showRunestone ? (timelineFade as MapExpression as never) : 0 }} />
          <Layer id="runestone-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u16b1", "text-size": 8, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showRunestone ? 1 : 0 }} />
        </Source>

        {/* === BATTLEFIELD LAYER === */}
        <Source id="battlefield-sites" type="geojson" data={battlefieldGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="battlefield-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#c62828", "circle-opacity": showBattlefield ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showBattlefield ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="battlefield-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showBattlefield ? 1 : 0 }} />
          <Layer id="battlefield-point" type="circle" filter={noCluster}
            paint={{ "circle-color": "#c62828", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 12], "circle-stroke-width": (showBattlefield ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke, "circle-opacity": showBattlefield ? (timelineFade as MapExpression as never) : 0 }} />
          <Layer id="battlefield-label" type="symbol" minzoom={9} filter={noCluster}
            layout={{ "text-field": "\u2694", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showBattlefield ? 1 : 0 }} />
        </Source>

        {/* === MONUMENT LAYER === */}
        <Source id="monument-sites" type="geojson" data={monumentGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="monument-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#2d8a56", "circle-opacity": showMonument ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showMonument ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="monument-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showMonument ? 1 : 0 }} />
          <Layer id="monument-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": monumentCatColorExpr as MapExpression as never,
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showMonument ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showMonument ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="monument-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u26EB", "text-size": 11, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showMonument ? 1 : 0 }} />
        </Source>

        {/* === BUILDING LAYER (sub-filter under monuments) === */}
        <Source id="building-sites" type="geojson" data={buildingGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="building-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#2d8a56", "circle-opacity": showBuildingPoints ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showBuildingPoints ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="building-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showBuildingPoints ? 1 : 0 }} />
          <Layer id="building-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#2d8a56",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 3.5, 10, 6, 14, 10],
              "circle-stroke-width": (showBuildingPoints ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showBuildingPoints ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="building-label" type="symbol" minzoom={11} filter={noCluster}
            layout={{ "text-field": "\u2302", "text-size": 11, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showBuildingPoints ? 1 : 0 }} />
        </Source>

        {/* === SHIPWRECK LAYER === */}
        <Source id="shipwreck-sites" type="geojson" data={shipwreckGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="shipwreck-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#0288d1", "circle-opacity": showShipwreck ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showShipwreck ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="shipwreck-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showShipwreck ? 1 : 0 }} />
          <Layer id="shipwreck-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#0288d1",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showShipwreck ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showShipwreck ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="shipwreck-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u2693", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showShipwreck ? 1 : 0 }} />
        </Source>

        {/* === MUSEUM LAYER === */}
        <Source id="museum-sites" type="geojson" data={museumGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="museum-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#2e7d4f", "circle-opacity": showMuseum ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showMuseum ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="museum-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showMuseum ? 1 : 0 }} />
          <Layer id="museum-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#2e7d4f",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showMuseum ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showMuseum ? 0.9 : 0,
            }} />
          <Layer id="museum-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "M", "text-size": 11, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showMuseum ? 1 : 0 }} />
        </Source>

        {/* === ROCK ART LAYER === */}
        <Source id="rockart-sites" type="geojson" data={rockArtGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="rockart-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#7b1fa2", "circle-opacity": showRockArt ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showRockArt ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="rockart-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showRockArt ? 1 : 0 }} />
          <Layer id="rockart-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#7b1fa2",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showRockArt ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showRockArt ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="rockart-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\ud83e\udea8", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showRockArt ? 1 : 0 }} />
        </Source>

        {/* === UNESCO LAYER === */}
        <Source id="unesco-sites" type="geojson" data={unescoGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="unesco-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#1565c0", "circle-opacity": showUnesco ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showUnesco ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="unesco-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showUnesco ? 1 : 0 }} />
          <Layer id="unesco-glow" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#1565c0",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 10, 10, 16, 14, 22],
              "circle-opacity": showUnesco ? 0.12 : 0,
              "circle-stroke-width": 0, "circle-blur": 0.05,
            }} />
          <Layer id="unesco-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#1565c0",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 12],
              "circle-stroke-width": (showUnesco ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showUnesco ? 0.95 : 0,
            }} />
          <Layer id="unesco-label" type="symbol" minzoom={8} filter={noCluster}
            layout={{ "text-field": "\u2b50", "text-size": 10, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showUnesco ? 1 : 0 }} />
        </Source>

        {/* === INDUSTRIAL HERITAGE LAYER === */}
        <Source id="industrial-sites" type="geojson" data={industrialGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="industrial-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#607d8b", "circle-opacity": showIndustrial ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showIndustrial ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="industrial-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showIndustrial ? 1 : 0 }} />
          <Layer id="industrial-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#607d8b",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showIndustrial ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showIndustrial ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="industrial-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u2699\ufe0f", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showIndustrial ? 1 : 0 }} />
        </Source>

        {/* === BARROW LAYER === */}
        <Source id="barrow-sites" type="geojson" data={barrowGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="barrow-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#6a1b9a", "circle-opacity": showBarrow ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showBarrow ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="barrow-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showBarrow ? 1 : 0 }} />
          <Layer id="barrow-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#6a1b9a",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showBarrow ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showBarrow ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="barrow-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u25b3", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showBarrow ? 1 : 0 }} />
        </Source>

        {/* === MEGALITH LAYER === */}
        <Source id="megalith-sites" type="geojson" data={megalithGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="megalith-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#5e35b1", "circle-opacity": showMegalith ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showMegalith ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="megalith-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showMegalith ? 1 : 0 }} />
          <Layer id="megalith-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#5e35b1",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showMegalith ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showMegalith ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="megalith-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u25c6", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showMegalith ? 1 : 0 }} />
        </Source>

        {/* === TOWNSCAPE LAYER === */}
        <Source id="townscape-sites" type="geojson" data={townscapeGeoJSON} cluster clusterMaxZoom={10} clusterRadius={38}>
          <Layer id="townscape-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#d84315", "circle-opacity": showTownscape ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showTownscape ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="townscape-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showTownscape ? 1 : 0 }} />
          <Layer id="townscape-glow" type="circle" filter={["all", noCluster, ["!", ["has", "curated"]]] as FilterSpecification}
            paint={{ "circle-color": "#d84315", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 10, 10, 16, 14, 22], "circle-opacity": showTownscape ? 0.12 : 0, "circle-stroke-width": 0, "circle-blur": 0.05 }} />
          <Layer id="townscape-point" type="circle" filter={["all", noCluster, ["!", ["has", "curated"]]] as FilterSpecification}
            paint={{ "circle-color": "#d84315", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 12], "circle-stroke-width": (showTownscape ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke, "circle-opacity": showTownscape ? 0.95 : 0 }} />
          <Layer id="townscape-label" type="symbol" minzoom={8} filter={["all", noCluster, ["!", ["has", "curated"]]] as FilterSpecification}
            layout={{ "text-field": "\u2b21", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true }}
            paint={{ "text-color": mapTextColor, "text-opacity": showTownscape ? 1 : 0 }} />
          <Layer id="townscape-curated-glow" type="circle" filter={["all", noCluster, ["has", "curated"]] as FilterSpecification}
            paint={{ "circle-color": "#d84315", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 14, 10, 22, 14, 30], "circle-opacity": showTownscape ? 0.18 : 0, "circle-stroke-width": 0, "circle-blur": 0.05 }} />
          <Layer id="townscape-curated-point" type="circle" filter={["all", noCluster, ["has", "curated"]] as FilterSpecification}
            paint={{ "circle-color": "#d84315", "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 7, 10, 11, 14, 16], "circle-stroke-width": showTownscape ? 2.5 : 0, "circle-stroke-color": "#e65100", "circle-opacity": showTownscape ? 0.95 : 0 }} />
          <Layer id="townscape-curated-label" type="symbol" minzoom={5} filter={["all", noCluster, ["has", "curated"]] as FilterSpecification}
            layout={{ "text-field": "\u2b22", "text-size": 13, "text-font": ["Open Sans Bold"], "text-allow-overlap": true }}
            paint={{ "text-color": mapTextColor, "text-opacity": showTownscape ? 1 : 0 }} />
        </Source>

        {/* === PLAQUE LAYER === */}
        <Source id="plaque-sites" type="geojson" data={plaqueGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="plaque-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#8d6e63", "circle-opacity": showPlaque ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showPlaque ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="plaque-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showPlaque ? 1 : 0 }} />
          <Layer id="plaque-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#8d6e63",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showPlaque ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showPlaque ? 0.9 : 0,
            }} />
          <Layer id="plaque-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u25C6", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showPlaque ? 1 : 0 }} />
        </Source>

        {/* === STOLPERSTEIN LAYER === */}
        <Source id="stolperstein-sites" type="geojson" data={stolpersteinGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="stolperstein-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#a08040", "circle-opacity": showStolperstein ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showStolperstein ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="stolperstein-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showStolperstein ? 1 : 0 }} />
          <Layer id="stolperstein-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#a08040",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showStolperstein ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showStolperstein ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="stolperstein-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u25A0", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showStolperstein ? 1 : 0 }} />
        </Source>

        {/* === LIGHTHOUSE LAYER === */}
        <Source id="lighthouse-sites" type="geojson" data={lighthouseGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="lighthouse-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#e68a00", "circle-opacity": showLighthouse ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showLighthouse ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="lighthouse-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showLighthouse ? 1 : 0 }} />
          <Layer id="lighthouse-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#e68a00",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showLighthouse ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showLighthouse ? (timelineFade as MapExpression as never) : 0,
            }} />
          <Layer id="lighthouse-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u25C6", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showLighthouse ? 1 : 0 }} />
        </Source>

        {/* === THING SITE LAYER === */}
        <Source id="thingsite-sites" type="geojson" data={thingsiteGeoJSON} cluster clusterMaxZoom={12} clusterRadius={38}>
          <Layer id="thingsite-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#4527a0", "circle-opacity": showThingSite ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showThingSite ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="thingsite-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showThingSite ? 1 : 0 }} />
          <Layer id="thingsite-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": "#4527a0",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 7, 14, 10],
              "circle-stroke-width": (showThingSite ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showThingSite ? 0.9 : 0,
            }} />
          <Layer id="thingsite-label" type="symbol" minzoom={10} filter={noCluster}
            layout={{ "text-field": "\u2696", "text-size": 9, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-offset": [0, 0] }}
            paint={{ "text-color": mapTextColor, "text-opacity": showThingSite ? 1 : 0 }} />
        </Source>

        {/* === BYLEKSIKON LAYER === */}
        <Source id="byleksikon-sites" type="geojson" data={byleksikonGeoJSON} cluster clusterMaxZoom={14} clusterRadius={40}>
          <Layer id="byleksikon-clusters" type="circle" filter={["has", "point_count"]}
            paint={{ "circle-color": "#5c8a8a", "circle-opacity": showByleksikon ? CLUSTER_OPACITY : 0, "circle-radius": clusterRadiusExpr as MapExpression as never, "circle-stroke-width": showByleksikon ? 1 : 0, "circle-stroke-color": clusterStroke, "circle-blur": 0 }} />
          <Layer id="byleksikon-cluster-count" type="symbol" minzoom={6} filter={["has", "point_count"]}
            layout={{ "text-field": "{point_count_abbreviated}", "text-size": 10, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": clusterStroke, "text-halo-width": 1, "text-opacity": showByleksikon ? 1 : 0 }} />
          <Layer id="byleksikon-point" type="circle" filter={noCluster}
            paint={{
              "circle-color": byleksikonColorExpr as MapExpression as never,
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 3.5, 10, 6, 14, 10],
              "circle-stroke-width": (showByleksikon ? pinStrokeWidth : 0) as MapExpression as never, "circle-stroke-color": pinStroke,
              "circle-opacity": showByleksikon ? 0.9 : 0,
            }} />
        </Source>

        {/* === HISTORIC ROUTES LAYER === */}
        <Source id="route-sites" type="geojson" data={routeGeoJSON}>
          <Layer id="route-line-glow" type="line"
            paint={{
              "line-color": "#ad1457",
              "line-width": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 12],
              "line-opacity": showRoute ? (browsingEverything ? ["interpolate", ["linear"], ["zoom"], 6.5, 0, 8, 0.15] : 0.15) : 0,
              "line-blur": 3,
            }} />
          <Layer id="route-line" type="line"
            paint={{
              "line-color": ["match", ["get", "routeType"], "pilgrimage", "#c2185b", "roman", "#8d6e63", "#ad1457"],
              "line-width": ["interpolate", ["linear"], ["zoom"], 5, 2, 10, 3.5, 14, 5],
              // On the default view of everything the roads fade in from zoom
              // 6.5 to 8. Over a whole country they laid a brown net across the
              // pins and the first view read as noise (1 Oct 2026); a reader who
              // picked the routes tab gets them at any zoom.
              "line-opacity": showRoute ? (browsingEverything ? ["interpolate", ["linear"], ["zoom"], 6.5, 0, 8, 0.85] : 0.85) : 0,
              "line-dasharray": [2, 1],
            }} />
          <Layer id="route-label" type="symbol" minzoom={8}
            layout={{ "symbol-placement": "line", "text-field": ["get", "name"], "text-size": 11, "text-offset": [0, -1], "text-max-angle": 30, "text-font": ["Open Sans Bold"] }}
            paint={{ "text-color": mapTextColor, "text-halo-color": resolved === "dark" ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)", "text-halo-width": 1.5, "text-opacity": showRoute ? 1 : 0 }} />
        </Source>

        {/* === CURATED WALKS ===
            Two sources, and only the first is a layer. Walk pins are one per
            walk and behave like every other pin. Stop pins are the walk being
            read: numbered, unclustered, and an empty collection whenever no
            walk is open. Unclustered because a city holds a handful of walks,
            not thousands, and a cluster of two would hide both.
            The open walk's own pin steps aside: its numbered stops and its
            walking line (walk-route, below) say the same thing more exactly,
            and the pin sat on top of stop 1 with its name across the line. */}
        <Source id="walk-sites" type="geojson" data={walkGeoJSON}>
          <Layer id="walk-point" type="circle"
            filter={["!=", ["get", "id"], openWalkId]}
            paint={{
              "circle-color": "#00838f",
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 10, 8, 14, 11],
              "circle-stroke-width": showWalk ? 2 : 0,
              "circle-stroke-color": clusterStroke,
              "circle-opacity": showWalk ? 0.9 : 0,
            }} />
          <Layer id="walk-label" type="symbol" minzoom={11}
            filter={["!=", ["get", "id"], openWalkId]}
            layout={{ "text-field": ["get", "name"], "text-size": 11, "text-offset": [0, 1.2], "text-anchor": "top", "text-font": ["Open Sans Bold"], "text-max-width": 9 }}
            paint={{ "text-color": mapTextColor, "text-halo-color": resolved === "dark" ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)", "text-halo-width": 1.5, "text-opacity": showWalk ? 1 : 0 }} />
        </Source>

        {/* Under the stops, so a number is never crossed out by its own line.
            A casing and a dashed centre, the way a footpath is drawn on paper;
            not clickable, the stops are what a reader taps. */}
        <Source id="walk-route" type="geojson" data={walkRouteGeoJSON}>
          <Layer id="walk-route-casing" type="line"
            layout={{ "line-join": "round", "line-cap": "round" }}
            paint={{ "line-color": resolved === "dark" ? "#1a1a1a" : "#ffffff", "line-width": ["interpolate", ["linear"], ["zoom"], 12, 4, 17, 8], "line-opacity": 0.9 }} />
          <Layer id="walk-route-line" type="line"
            layout={{ "line-join": "round", "line-cap": "round" }}
            paint={{ "line-color": "#00838f", "line-width": ["interpolate", ["linear"], ["zoom"], 12, 2, 17, 4], "line-dasharray": [2, 1.5] }} />
        </Source>

        <Source id="walk-stop-sites" type="geojson" data={walkStopGeoJSON}>
          {/* Three states, one variable: your place in the sequence. Filled is
              where you are, outlined is still ahead, faded is behind you. The
              stop you are on inverts rather than growing, so the numbers stay
              on one baseline and the list and the map agree on size. */}
          <Layer id="walk-stop-point" type="circle"
            paint={{
              "circle-color": ["case", ["==", ["get", "id"], activeStopId ?? ""], "#00838f", resolved === "dark" ? "#1a1a1a" : "#ffffff"],
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 8, 14, 11, 17, 14],
              "circle-stroke-width": 2,
              "circle-stroke-color": "#00838f",
              "circle-opacity": ["case", ["<", ["get", "n"], activeStopN], 0.4, 1],
              "circle-stroke-opacity": ["case", ["<", ["get", "n"], activeStopN], 0.4, 1],
            }} />
          <Layer id="walk-stop-number" type="symbol"
            layout={{ "text-field": ["to-string", ["get", "n"]], "text-size": 11, "text-font": ["Open Sans Bold"], "text-allow-overlap": true, "text-ignore-placement": true }}
            paint={{
              "text-color": ["case", ["==", ["get", "id"], activeStopId ?? ""], "#ffffff", "#00838f"],
              "text-opacity": ["case", ["<", ["get", "n"], activeStopN], 0.5, 1],
            }} />
        </Source>

        {/* The other places the open legend names, and the spot it was told at.
            Drawn only while its panel is up, so there is no show* flag and no
            stroke to gate: the source is empty whenever the panel is closed.
            Deliberately not clickable. An open detail panel already blocks map
            clicks through its backdrop, so a click handler here would be dead
            code that reads as a promise. */}
        <Source id="folklore-context" type="geojson" data={folkloreContextGeoJSON}>
          <Layer id="folklore-context-line" type="line"
            paint={{
              "line-color": resolved === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.35)",
              "line-width": 1,
              "line-dasharray": [2, 3],
            }} />
          <Layer id="folklore-context-point" type="circle"
            filter={["==", ["geometry-type"], "Point"]}
            paint={{
              // Hollow, so it cannot be mistaken for a pin that can be opened.
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 3.5, 14, 6],
              "circle-color": resolved === "dark" ? "#1a1a1a" : "#ffffff",
              "circle-opacity": 0.9,
              "circle-stroke-width": 1.5,
              "circle-stroke-color": pinStroke,
            }} />
          <Layer id="folklore-context-label" type="symbol"
            filter={["==", ["geometry-type"], "Point"]}
            layout={{ "text-field": ["get", "name"], "text-size": 11, "text-offset": [0, 1.1], "text-anchor": "top", "text-font": ["Open Sans Bold"], "text-max-width": 9 }}
            paint={{
              "text-color": mapTextColor,
              "text-halo-color": resolved === "dark" ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)",
              "text-halo-width": 1.5,
            }} />
        </Source>

        {/* === USER LOCATION === */}
        {userLocation && (
          <Source id="user-location" type="geojson" data={{ type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Point", coordinates: userLocation }, properties: {} }] }}>
            <Layer id="user-location-pulse" type="circle" paint={{ "circle-radius": 20, "circle-color": "rgba(66,133,244,0.15)", "circle-stroke-width": 0, "circle-blur": 0.05 }} />
            <Layer id="user-location-dot" type="circle" paint={{ "circle-radius": 7, "circle-color": "#4285F4", "circle-stroke-width": 2.5, "circle-stroke-color": "#fff" }} />
          </Source>
        )}

        {/* === VISITED OVERLAY — subtle badge dot at top-right of pin === */}
        <Source id="visited-overlay" type="geojson" data={visitedOverlayGeoJSON}>
          <Layer id="visited-overlay-dot" type="circle" paint={{ "circle-radius": 4, "circle-color": "#22c55e", "circle-stroke-width": 1.5, "circle-stroke-color": "#fff", "circle-translate": [6, -6] }} />
        </Source>

        {/* === SAVED OVERLAY === */}
        <Source id="saved-overlay" type="geojson" data={savedOverlayGeoJSON}>
          <Layer id="saved-overlay-heart" type="symbol" layout={{ "text-field": "♥", "text-size": 11, "text-offset": [0.9, -0.9], "text-allow-overlap": true, "icon-allow-overlap": true }} paint={{ "text-color": "#f43f5e", "text-halo-color": "#fff", "text-halo-width": 1 }} />
        </Source>
      </MapGL>
      {lensOn && lensOffered && lensSheet && lensView && mapRef.current && (
        <Suspense fallback={null}>
          <PastLens key={lensSheet.id} mainMap={mapRef.current}
            mapStyle={lensView.style} label={lensView.label}
            credit={lensSheet.credit} creditUrl={lensSheet.creditUrl} />
        </Suspense>
      )}
      {timelineYear != null && (
        <Suspense fallback={null}>
          <TimelineBar year={timelineYear} onChange={setTimelineYear} onClose={() => setTimelineYear(null)} lang={lang} country={viewCountry} spans={timelineSpans} stops={timelineStops} showUnbuilt={showUnbuilt} onShowUnbuilt={setShowUnbuilt} />
        </Suspense>
      )}

      {/* === PAPER TEXTURE OVERLAY ===
          Mounted once the browser is idle, not at boot: on a phone on 4G the
          208 kB texture was fetched beside the record a reader came for, and
          the panel waited (measured 1 Oct 2026). It fades in and sits over
          the map, so arriving late moves nothing. */}
      {paperTexture && (
        <div className={appStyles.paperTexture}>
          <img src="/bg1.jpg" alt="" className={appStyles.paperTextureImg} decoding="async" fetchPriority="low" />
        </div>
      )}

      {/* === VIGNETTE === */}
      <div className={appStyles.vignette} />

      {/* === NAV BAR === */}
      <div className={appStyles.navBar}>
        <div className={`nav-btn-row ${appStyles.navRow}`}>
          <button onClick={() => setShowAbout(true)} className={appStyles.wordmarkBtn} title={t("map.about", lang)}>
            <span className={appStyles.wordmark}>FORNLAND</span>
          </button>
          <div className={`search-bar ${appStyles.searchBar}`}>
            <Search size={14} stroke="var(--text-muted)" strokeWidth={2.5} style={{ flexShrink: 0 }} />
            <input
              ref={searchInputRef}
              id="fornland-search"
              type="text"
              value={searchQuery}
              placeholder={t("search.placeholder", lang)}
              /* The arrow keys below moved a highlight that existed only as a
                 CSS class, so a screen reader was told nothing: not that
                 suggestions had appeared, not how many, not which one Enter
                 would open. aria-activedescendant is what carries that, and it
                 only works if the list it points into is a real listbox. */
              role="combobox"
              aria-label={t("search.placeholder", lang)}
              aria-autocomplete="list"
              /* Both gated on the same condition the listbox itself renders
                 under. aria-controls naming an element that is not in the DOM
                 is a critical axe failure in its own right, and it was: the
                 dropdown unmounts the moment a record opens, while the query
                 stays in the box. */
              aria-controls={searchOpen && searchQuery && searchFlat.length > 0 ? "search-listbox" : undefined}
              aria-expanded={searchOpen && searchQuery.length > 0 && searchFlat.length > 0}
              aria-activedescendant={searchActiveId ? `search-opt-${searchActiveId}` : undefined}
              onChange={e => { setSearchQuery(e.target.value); searchUnified(e.target.value); }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => {
                // Belt and braces for iOS: if the keyboard did manage to
                // shift the page, put it back. The layout never scrolls,
                // so any offset here is one the browser introduced.
                if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
                setTimeout(() => setSearchOpen(false), 200);
              }}
              onKeyDown={e => {
                if (e.key === "Escape") { setSearchOpen(false); setSearchQuery(""); setSearchResults({ local: [], places: [] }); activeSearchQueryRef.current = ""; setSearchActiveIdx(-1); (e.target as HTMLInputElement).blur(); }
                if (e.key === "Enter") { const picked = searchFlat[searchActiveIdx] || searchFlat[0]; if (picked) handleSearchSelect(picked); }
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  const next = e.key === "ArrowDown"
                    ? Math.min(searchActiveIdx + 1, searchFlat.length - 1)
                    : Math.max(searchActiveIdx - 1, -1);
                  setSearchActiveIdx(next);
                  const row = searchFlat[next];
                  if (row) document.getElementById(`search-opt-${row.id}`)?.scrollIntoView({ block: "nearest" });
                }
              }}
              className={appStyles.searchInput}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setSearchResults({ local: [], places: [] }); setSearchLoading(false); clearTimeout(searchDebounceRef.current); activeSearchQueryRef.current = ""; setSearchActiveIdx(-1); searchInputRef.current?.focus(); }}
                aria-label={t("search.clear", lang)}
                className={appStyles.searchClear}>
                ✕
              </button>
            )}
            {/* Nothing visible changes when suggestions arrive, so a screen
                reader user had no way of knowing they had. The region is
                mounted whether or not there is anything to say, because a
                live region inserted at the same moment as its text is
                usually announced by nobody. Empty while loading, so the
                count is only read once it is the final one. */}
            <div className="sr-only" role="status" aria-live="polite">
              {searchQuery && !searchLoading
                ? (searchFlat.length > 0
                    ? t("search.resultCount", lang).replace("{count}", String(searchFlat.length))
                    : t("search.noResultsArea", lang))
                : ""}
            </div>
          </div>
          <button onClick={() => { if (user || window.location.hostname === "localhost") setShowExplorePrompt(true); else setShowAuthModal(true); }}
            className={appStyles.hiddenBtn}
            aria-label={t("hidden.title", lang)}
            title={t("hidden.title", lang)}>
            <Sparkles size={16} />
          </button>
          {/* The chips: groups, then the chosen group's layers, then the
              layer's filters. One scrolling line on a phone (index.css
              .nav-chip-row); on a wider screen the wrapper is display:
              contents and they wrap with the search as before. On 26 Sep
              2026 a phone with the heritage layer chosen spent some 40 per
              cent of its height on four rows of period chips. */}
          <div className="nav-chip-row">
          {categoryGroups.filter(g => activeGroup === "all" ? g.key !== "all" : g.key === activeGroup).map((g, i) => {
            const active = activeGroup === g.key && g.key !== "all";
            return (
              <NavBtn key={g.key} index={i} active={active} onClick={() => switchGroup(active ? "all" : g.key)}>
                {g.icon === "archaeology" && <MapPin size={14} />}
                {g.icon === "built" && <Landmark size={14} />}
                {g.icon === "events" && <Sun size={14} />}
                {g.icon === "folklore_group" && <Feather size={14} />}
                {g.icon === "explore" && <Compass size={14} />}
                {g.label}
                {active && <X size={12} strokeWidth={2.5} />}
              </NavBtn>
            );
          })}
          {/* Sub-tabs (inline) */}
          {groupSubTabs.filter(st => activeTab === "all" ? st.key !== "all" : st.key === activeTab).map((st, i) => {
            const isActive = activeTab === st.key && st.key !== "all";
            return (
              <NavBtn key={st.key} index={i} active={isActive} dot={st.color} onClick={() => switchSubTab(isActive ? "all" as ActiveTab : st.key)}>
                {st.label}
                {isActive && <X size={12} strokeWidth={2.5} />}
              </NavBtn>
            );
          })}
          {/* Sub-filters (inline) */}
          {activeTab === "heritage" && timelineYear == null && periods.filter(p => activePeriod === "all" ? p.id !== "all" : p.id === activePeriod).map((p, i) => {
            const isActive = activePeriod === p.id && p.id !== "all";
            const color = PERIOD_COLORS[p.id] ?? "#9b8ec4";
            return (
              <NavBtn key={p.id} index={i} active={isActive} dot={color} onClick={() => setActivePeriod(isActive ? "all" : p.id)}>
                {p.label}
                {isActive && <X size={12} strokeWidth={2.5} />}
              </NavBtn>
            );
          })}
          {activeTab === "folklore" && folkloreCategories.filter(c => activeFolkloreCat === "all" ? c.id !== "all" : c.id === activeFolkloreCat).map((c, i) => {
            const isActive = activeFolkloreCat === c.id && c.id !== "all";
            return (
              <NavBtn key={c.id} index={i} active={isActive} dot={c.color} onClick={() => setActiveFolkloreCat(isActive ? "all" : c.id)}>
                {c.label}
                {isActive && <X size={12} strokeWidth={2.5} />}
              </NavBtn>
            );
          })}
          {activeTab === "monument" && monumentCategories.filter(c => activeMonumentCat === "all" ? c.id !== "all" : c.id === activeMonumentCat).map((c, i) => {
            const isActive = activeMonumentCat === c.id && c.id !== "all";
            return (
              <NavBtn key={c.id} index={i} active={isActive} dot={c.color} onClick={() => setActiveMonumentCat(isActive ? "all" : c.id)}>
                {c.label}
                {isActive && <X size={12} strokeWidth={2.5} />}
              </NavBtn>
            );
          })}
          </div>
        </div>
        {/* An empty, focused box says what it can do. The search accepts a
            sentence (api/ask.ts) and nothing in the placeholder says so; a
            visitor who has only ever typed names would never find out. A
            sibling of the results, not a child: that wrapper needs a query. */}
        {searchOpen && !searchQuery && (
          <div className={appStyles.searchResults}>
            <div className={appStyles.searchEmpty}>{t("search.hint", lang)}</div>
          </div>
        )}
        {/* Search results dropdown */}
        {searchOpen && searchQuery && (
          <div className={appStyles.searchResults}>
          {!searchLoading && !searchResults.ask && searchResults.local.length === 0 && searchResults.places.length === 0 && (
            <div className={appStyles.searchEmpty}>
              {t("search.noResultsArea", lang)}
            </div>
          )}
          {/* role="listbox" only once there is something in it. An empty
              listbox fails axe's aria-required-children, and this renders
              during searchLoading with no rows at all. */}
          {(searchResults.ask || searchResults.local.length > 0 || searchResults.places.length > 0 || searchLoading) && searchQuery && (
            <div
              id="search-listbox"
              role={searchFlat.length > 0 ? "listbox" : undefined}
              aria-label={searchFlat.length > 0 ? t("search.placeholder", lang) : undefined}
              className={appStyles.searchDropdown}
            >
              {/* "In this area" label. aria-hidden because each layer group
                  below carries its own accessible name, and a bare div is not
                  a thing a listbox is allowed to own. */}
              {searchResults.local.length > 0 && (
                <div className={appStyles.searchGroupLabel} aria-hidden="true">
                  {t("search.inThisArea", lang)}
                </div>
              )}
              {/* A sentence the model read as filters, first. See api/ask.ts. */}
              {searchResults.ask?.ask && (() => {
                const a = searchResults.ask.ask;
                const periodApplies = (a.tab ?? activeTab) === "heritage";
                const parts = [a.tab ? t(`tab.${a.tab}`, lang) : null, a.period && periodApplies ? t(`period.${a.period}`, lang) : null, a.place].filter(Boolean);
                return (
                  <div role="group" aria-label={t("search.ask", lang)}>
                    <div className={appStyles.searchGroupLabel} aria-hidden="true">{t("search.ask", lang)}</div>
                    <button id="search-opt-ask" onClick={() => handleSearchSelect(searchResults.ask!)}
                      role="option" aria-selected={searchActiveId === "ask"}
                      className={`${appStyles.searchRow}${searchActiveId === "ask" ? ` ${appStyles.searchRowActive}` : ""}`}>
                      <div className={appStyles.searchRowMain}>
                        <div className={appStyles.truncate}>{parts.join(" · ")}</div>
                      </div>
                    </button>
                  </div>
                );
              })()}
              {/* Local results grouped by layer type */}
              {(() => {
                const grouped = new Map<string, UnifiedSearchResult[]>();
                for (const r of searchResults.local) {
                  const arr = grouped.get(r.layerType) || [];
                  arr.push(r);
                  grouped.set(r.layerType, arr);
                }
                return Array.from(grouped.entries()).map(([layerType, items]) => (
                  <div key={layerType} role="group" aria-label={t(`tab.${layerType}`, lang)}>
                    <div className={appStyles.searchGroupLabel} aria-hidden="true">
                      {t(`tab.${layerType}`, lang)}
                    </div>
                    {items.map((r) => (
                      <button key={r.id} id={`search-opt-${r.id}`} onClick={() => handleSearchSelect(r)}
                        role="option" aria-selected={r.id === searchActiveId}
                        className={`${appStyles.searchRow}${r.id === searchActiveId ? ` ${appStyles.searchRowActive}` : ""}`}>
                        <div className={appStyles.searchRowMain}>
                          <div className={appStyles.truncate}>{localizeFolkloreTitle(r.name, lang)}</div>
                          {/* subtitle is the matched field and wins; place is
                              the fallback that tells 17 mills called De Hoop
                              apart. See UnifiedSearchResult. */}
                          {(r.subtitle || r.place) && <div className={appStyles.searchRowSub}>{r.subtitle || r.place}</div>}
                        </div>
                        {r.country && <span className={appStyles.searchRowMeta}>{r.country}</span>}
                      </button>
                    ))}
                  </div>
                ));
              })()}
              {/* Nominatim places section */}
              {(searchResults.places.length > 0 || searchLoading) && (
                <div role="group" aria-label={t("search.places", lang)}>
                  <div className={appStyles.searchGroupLabel} aria-hidden="true">
                    {t("search.places", lang)}
                  </div>
                  {searchResults.places.map((r) => (
                    <div key={r.id} className={appStyles.searchPlaceGroup}>
                      <button id={`search-opt-${r.id}`} onClick={() => handleSearchSelect(r)}
                        role="option" aria-selected={r.id === searchActiveId}
                        className={`${appStyles.searchPlaceRow}${r.id === searchActiveId ? ` ${appStyles.searchRowActive}` : ""}`}>
                        <span className={`${appStyles.searchRowMain} ${appStyles.truncate}`}>{r.name}</span>
                        <span className={appStyles.searchRowMeta}>{r.subtitle}</span>
                      </button>
                      {/* An option as well, and it has to be: a listbox may own
                          nothing but options and groups, and this button sits
                          inside one. axe calls that critical, and the
                          search-dropdown scenario in scripts/a11y-audit.mjs is
                          what found it. It is left out of searchFlat, so the
                          arrow keys skip it and Enter never lands here. */}
                      <button onClick={() => {
                        setSearchOpen(false); setSearchQuery("");
                        setHiddenHistoryLocation({ lat: r.lat, lng: r.lng, address: r.name.split(",").slice(0, 2).join(",") });
                        setShowHiddenHistory(true);
                        mapRef.current?.flyTo({ center: [r.lng, r.lat], zoom: 13, duration: 1500 });
                      }}
                        role="option" aria-selected={false}
                        className={appStyles.searchPlaceHidden}
                      >
                        <Sparkles size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                        {t("hidden.explore", lang).replace("✨ ", "")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>

      {activeTab === "monument" && monumentError && monumentFeatures.length === 0 && (
        <div className={appStyles.toast}>
          <span>{t("error.buildingsFailed", lang)}</span>
          <button
            onClick={() => { setMonumentsLoaded(false); setMonumentError(false); }}
            className={appStyles.toastBtn}
          >
            {t("error.retry", lang)}
          </button>
        </div>
      )}

      {/* Supabase redirects a dead magic link back to the site root with the
          reason in the fragment, and useAuth strips it once the client has
          read it. Before this the visitor just got the plain map, signed out,
          with nothing said. 17 landed that way between February and August
          2026 and had no way to know the link was the problem. */}
      {callbackError && (
        <div className={appStyles.toast} role="status">
          <span>{t(callbackError === "otp_expired" ? "error.linkExpired" : "error.signInFailed", lang)}</span>
          <button
            onClick={() => { clearCallbackError(); handleLoginRequest("login"); }}
            className={appStyles.toastBtn}
          >
            {t("error.signInAgain", lang)}
          </button>
        </div>
      )}

      {/* === MAP CONTROLS === */}
      <div className={appStyles.controlStack}>
        {/* Explorer-mode entry — walk-to-discover. Pulled out of the
            horizontally-scrolled nav row so it stays visible on mobile
            (the chip row pushes it offscreen otherwise). The first-run
            coachmark to its left dismisses on first interaction; it used
            to announce itself with a gold halo and an attract pulse, but
            a control that pulses at you is the opposite of institutional. */}
        <div className={appStyles.exploreRow}>
          {/* Not over an open walk: on a phone the coachmark sat on top of
              stops 6 to 8 of the route the reader was walking. */}
          {showExploreCoach && selectedType !== "walk" && (
            <button onClick={openExploreMode}
              aria-label={t("explore.coachmark", lang)}
              className={appStyles.coachmark}>
              <Compass size={14} />
              <span className={appStyles.coachmarkText}>
                <span>{t("explore.coachmark", lang)}</span>
                <span className={appStyles.coachmarkHint}>{t("explore.coachmarkHint", lang)}</span>
              </span>
              <span onClick={(e) => { e.stopPropagation(); dismissExploreCoach(); }}
                role="button" aria-label={t("action.close", lang)}
                className={appStyles.coachmarkClose}>
                <X size={12} strokeWidth={2.5} />
              </span>
              {/* Speech-bubble notch pointing at the compass button. Two
                  stacked spans: outer = border colour, inner = fill, so the
                  border continues cleanly around the arrow tip. */}
              <span aria-hidden="true" className={appStyles.coachmarkNotchBorder} />
              <span aria-hidden="true" className={appStyles.coachmarkNotchFill} />
            </button>
          )}
          <button onClick={openExploreMode}
            aria-label={t("explore.title", lang)}
            title={t("explore.title", lang)}
            className={appStyles.exploreBtn}>
            <Compass size={20} strokeWidth={2.2} />
          </button>
        </div>
        <button onClick={() => setThemeMode(resolved === "dark" ? "light" : "dark")} className={`${appStyles.mapControlBtn} ${appStyles.glass}`} title={t(resolved === "dark" ? "map.lightMode" : "map.darkMode", lang)}>
          {resolved === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button onClick={() => setSatellite(v => !v)} className={`${appStyles.mapControlBtn} ${appStyles.glass} ${satellite ? appStyles.mapControlBtnOn : ""}`} aria-pressed={satellite} title={t("map.satellite", lang)}>
          <Eye size={18} />
        </button>
        {/* The ETK mode: Denmark on the 1842-1899 survey sheets. The map only
            exists over Denmark, so switching it on from a viewport elsewhere
            flies there instead of presenting a paper-toned void. Offered only
            in the context it belongs to, the legend layer — but never hidden
            while the mode is ON, or switching tab on the old map would take
            the way back with it. */}
        {(activeTab === "folklore" || histMap) && <button
          onClick={() => {
            setHistMap(v => {
              const on = !v;
              if (on) {
                const m = mapRef.current;
                const c = m?.getMap().getCenter();
                if (m && c && (c.lat < 54.4 || c.lat > 57.9 || c.lng < 7.8 || c.lng > 15.3)) {
                  m.flyTo({ center: [SURVEY_HOME.longitude, SURVEY_HOME.latitude], zoom: SURVEY_HOME.zoom, duration: 2000 });
                }
              }
              return on;
            });
          }}
          className={`${appStyles.mapControlBtn} ${appStyles.glass} ${histMap ? appStyles.mapControlBtnOn : ""}`}
          aria-pressed={histMap}
          title={t("map.historical", lang)}
        >
          <History size={18} />
        </button>}
        {lensOffered && (
          <button onClick={() => setLensOn(v => !v)}
            className={`${appStyles.mapControlBtn} ${appStyles.glass} ${lensOn ? appStyles.mapControlBtnOn : ""}`}
            aria-pressed={lensOn} title={t("map.lens", lang)} aria-label={t("map.lens", lang)} data-control="past-lens">
            <Focus size={18} />
          </button>
        )}
        {/* The timeline. Opening it drops the period chip filter, so there is
            one filter on one question; year 1000 is where every country has
            something to show. */}
        <button onClick={() => { setTimelineYear(v => { if (v == null) { setActivePeriod("all"); return 1000; } return null; }); }}
          className={`${appStyles.mapControlBtn} ${appStyles.glass} ${timelineYear != null ? appStyles.mapControlBtnOn : ""}`}
          aria-pressed={timelineYear != null} title={t("map.timeline", lang)} aria-label={t("map.timeline", lang)} data-control="timeline">
          <Hourglass size={18} />
        </button>
        {/* {!isMobileOrTablet && (
          <button onClick={() => setTerrain3d(v => !v)} className={`${appStyles.mapControlBtn} ${appStyles.glass}`} style={terrain3d ? { border: "1px solid rgba(139,92,246,0.5)", color: "#a78bfa", background: "rgba(139,92,246,0.12)" } : undefined} title="3D Terrain">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 20L9 8l4 6 4-10 4 16" /><path d="M3 20h18" />
            </svg>
          </button>
        )} */}
        {/* Sparkles button moved to search bar */}
        <button onClick={handleLocate} className={`${appStyles.mapControlBtn} ${appStyles.glass}`} title={t("locate.title", lang)}>
          <Crosshair size={18} />
        </button>
        <div className={`${appStyles.glass} ${appStyles.zoomGroup}`}>
          <button onClick={() => { const m = mapRef.current; if (m) m.getMap().zoomIn({ duration: 200 }); }} className={appStyles.zoomBtnTop} title={t("map.zoomIn", lang)}>
            <Plus size={16} strokeWidth={2.5} />
          </button>
          <button onClick={() => { const m = mapRef.current; if (m) m.getMap().zoomOut({ duration: 200 }); }} className={appStyles.zoomBtn} title={t("map.zoomOut", lang)}>
            <Minus size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* === USER PROFILE + ABOUT BUTTONS === */}
      <div className={appStyles.profileStack}>
        <MapLegend lang={lang} sections={legendSections} variant="icon" />
        <NavBtn icon onClick={() => setShowProfile(true)} title={t("profile.title", lang)}>
          <User size={18} />
        </NavBtn>
        <NavBtn icon onClick={() => setShowAbout(true)} title={t("map.about", lang)}>
          <Info size={18} />
        </NavBtn>
      </div>

      {/* An empty folklore view says so. A German researcher panned the Harz
          with the legends chosen, saw no pins and no word why, and read it as
          an absence of folklore (reviewed 26 Sep 2026). */}
      {folkloreEmptyHere && (
        <div className={appStyles.emptyNotice} role="status">{t("folklore.emptyHere", lang)}</div>
      )}

      {/* === STATUS === */}
      <div className={`status-bar ${appStyles.statusBar}`}>
        {(loading || loadingLayers.size > 0) && (
          <div className={appStyles.statusLoading}>
            <div className={appStyles.statusDot} />
            <span className={appStyles.statusLabel}>{t("status.loading", lang)}</span>
          </div>
        )}
        {!loading && loadingLayers.size === 0 && (
          <span className={appStyles.statusCounts}>
            {[
              showHeritage && (counts.dk + counts.no + counts.se > 0) && `${counts.dk + counts.no + counts.se} ${t("status.sites", lang)}`,
              showFolklore && folkloreCounts > 0 && `${folkloreCounts} ${t("status.folklore", lang)}`,
              showRunestone && runestoneCount > 0 && `${runestoneCount} ${t("status.runestones", lang)}`,
              showBattlefield && battlefieldCount > 0 && `${battlefieldCount} ${t("status.battlefields", lang)}`,
              showMonument && (monumentCount + buildingCount) > 0 && `${monumentCount + buildingCount} ${t("status.monuments", lang)}`,
              showShipwreck && shipwreckCount > 0 && `${shipwreckCount} ${t("status.shipwrecks", lang)}`,
              showMuseum && museumCount > 0 && `${museumCount} ${t("status.museums", lang)}`,
              showRockArt && rockArtCount > 0 && `${rockArtCount} ${t("status.rockart", lang)}`,
              showUnesco && unescoCount > 0 && `${unescoCount} ${t("status.unesco", lang)}`,
              showIndustrial && industrialCount > 0 && `${industrialCount} ${t("status.industrial", lang)}`,
              showBarrow && barrowCount > 0 && `${barrowCount} ${t("status.barrows", lang)}`,
              showMegalith && megalithCount > 0 && `${megalithCount} ${t("status.megaliths", lang)}`,
              showTownscape && townscapeCount > 0 && `${townscapeCount} ${t("status.townscapes", lang)}`,
              showPlaque && plaqueCount > 0 && `${plaqueCount} ${t("status.plaques", lang)}`,
              showStolperstein && stolpersteinCount > 0 && `${stolpersteinCount} ${t("status.stolpersteine", lang)}`,
              showLighthouse && lighthouseCount > 0 && `${lighthouseCount} ${t("status.lighthouses", lang)}`,
              showThingSite && thingsiteCount > 0 && `${thingsiteCount} ${t("status.thingsites", lang)}`,
              showByleksikon && byleksikonCount > 0 && `${byleksikonCount} ${t("status.byleksikon", lang)}`,
              showRoute && routeCount > 0 && `${routeCount} ${t("status.routes", lang)}`,
              showWalk && walkCount > 0 && `${walkCount} ${t("status.walks", lang)}`,
            ].filter(Boolean).join(" \u00b7 ")}
          </span>
        )}
        {!loading && loadingLayers.size === 0 && (["CSV", "GeoJSON"] as const).map((format) => (
          <button
            key={format}
            type="button"
            className={appStyles.statusExport}
            aria-label={t("export.label", lang).replace("{format}", format)}
            title={t("export.label", lang).replace("{format}", format)}
            onClick={() => {
              const bbox = viewBboxRef.current;
              if (!bbox) return;
              // What the map is drawing, layer by layer, after its filters.
              // The export module and the lazy translations load on the click.
              const layers = ([
                [showHeritage, "heritage", heritageGeoJSON], [showFolklore, "folklore", folkloreGeoJSON, folkloreIndex.current],
                [showRunestone, "runestone", runestoneGeoJSON, runestoneIndex.current], [showBattlefield, "battlefield", battlefieldGeoJSON, battlefieldIndex.current],
                [showMonument, "monument", monumentGeoJSON, monumentIndex.current], [showMonument, "building", buildingGeoJSON, buildingIndex.current],
                [showShipwreck, "shipwreck", shipwreckGeoJSON, shipwreckIndex.current], [showMuseum, "museum", museumGeoJSON, museumIndex.current],
                [showRockArt, "rockart", rockArtGeoJSON, rockArtIndex.current], [showUnesco, "unesco", unescoGeoJSON, unescoIndex.current],
                [showIndustrial, "industrial", industrialGeoJSON, industrialIndex.current], [showBarrow, "barrow", barrowGeoJSON, barrowIndex.current],
                [showMegalith, "megalith", megalithGeoJSON, megalithIndex.current], [showTownscape, "townscape", townscapeGeoJSON, townscapeIndex.current],
                [showPlaque, "plaque", plaqueGeoJSON, plaqueIndex.current], [showStolperstein, "stolperstein", stolpersteinGeoJSON, stolpersteinIndex.current],
                [showLighthouse, "lighthouse", lighthouseGeoJSON, lighthouseIndex.current], [showThingSite, "thingsite", thingsiteGeoJSON, thingsiteIndex.current],
                [showByleksikon, "byleksikon", byleksikonGeoJSON, byleksikonIndex.current],
              ] as [boolean, string, GeoJSON.FeatureCollection, Map<string, { sourceUrl?: string }>?][])
                .filter(([on]) => on)
                .map(([, layer, fc, index]) => [layer, fc, index] as [string, GeoJSON.FeatureCollection, Map<string, { sourceUrl?: string }>?]);
              import("./utils/exportView.ts").then((m) => m.downloadView(format === "CSV" ? "csv" : "geojson", layers, bbox, lang));
            }}
          >
            {format}
          </button>
        ))}
        {/* Not gated on loading: the colours are known before the pins arrive,
            and a slow register kept the key out of reach for as long as it
            took (the a11y scenario timed out waiting for it). */}
        {(
          <MapLegend lang={lang} sections={legendSections} />
        )}
        {error && <span className={appStyles.statusError}>{error}</span>}
      </div>

      {/* === LEGAL MODAL === */}
      {showLegal && <Suspense fallback={null}><LegalModal lang={lang} onClose={() => setShowLegal(false)} /></Suspense>}

      {/* === ABOUT MODAL === */}
      {showMethodology && <Suspense fallback={null}><MethodologyModal lang={lang} onClose={() => setShowMethodology(false)} /></Suspense>}
      {showAbout && <Suspense fallback={null}><AboutModal lang={lang} onClose={() => setShowAbout(false)} onShowLegal={() => { setShowAbout(false); setShowLegal(true); }} onShowMethodology={() => { setShowAbout(false); setShowMethodology(true); }} /></Suspense>}

      {/* === EXPLORE PROMPT (address input with autocomplete) === */}
      {showExplorePrompt && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowExplorePrompt(false); }}>
          <div style={{ background: "var(--bg-base, #fff)", borderRadius: "var(--radius-sm)", padding: "28px 24px", maxWidth: 400, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t("hidden.title", lang)}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
              {t("hidden.promptDesc", lang)}
            </div>
            <button onClick={() => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const gLat = pos.coords.latitude, gLng = pos.coords.longitude;
                  setUserLocation([gLng, gLat]);
                  // Reverse geocode for address name
                  fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${gLat}&lon=${gLng}&accept-language=${lang}`, { headers: { "User-Agent": "Fornland/1.0" } })
                    .then(r => r.json()).then(data => {
                      const addr = data.address ? [data.address.road, data.address.house_number].filter(Boolean).join(" ") || data.display_name?.split(",").slice(0, 2).join(",") : t("hidden.yourLocation", lang);
                      setHiddenHistoryLocation({ lat: gLat, lng: gLng, address: addr });
                      setShowHiddenHistory(true);
                      setShowExplorePrompt(false);
                      mapRef.current?.flyTo({ center: [gLng, gLat], zoom: 14, duration: 1500 });
                    }).catch(() => {
                      setHiddenHistoryLocation({ lat: gLat, lng: gLng, address: t("hidden.yourLocation", lang) });
                      setShowHiddenHistory(true);
                      setShowExplorePrompt(false);
                      mapRef.current?.flyTo({ center: [gLng, gLat], zoom: 14, duration: 1500 });
                    });
                },
                () => { /* GPS denied — just stay on address input */ },
                { enableHighAccuracy: false, timeout: 10000 }
              );
            }} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: "11px", borderRadius: "var(--radius-lg)", marginBottom: 10,
              border: "1px solid var(--border-default)", background: "var(--bg-elevated)",
              cursor: "pointer", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", fontFamily: "inherit",
            }}>
              <Crosshair size={15} /> {t("hidden.useLocation", lang)}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
              <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{t("hidden.or", lang)}</span>
              <div style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
            </div>
            <Suspense fallback={null}><ExploreAddressInput lang={lang} onSelect={(lat, lng, address) => {
              setHiddenHistoryLocation({ lat, lng, address });
              setShowHiddenHistory(true);
              setShowExplorePrompt(false);
              mapRef.current?.flyTo({ center: [lng, lat], zoom: 13, duration: 1500 });
            }} /></Suspense>
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>{t("hidden.radiusHint", lang)}</div>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                {[1, 2, 5, 10, 25].map(r => (
                  <button key={r} onClick={() => setExploreRadius(r)}
                    style={{ padding: "5px 12px", borderRadius: "var(--radius-lg)", fontSize: 12, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
                      border: r === exploreRadius ? "1px solid var(--text-primary)" : "1px solid var(--border-default)",
                      background: r === exploreRadius ? "var(--text-primary)" : "transparent",
                      color: r === exploreRadius ? "var(--bg-base)" : "var(--text-muted)" }}>{r} km</button>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 12, textAlign: "center", lineHeight: 1.4 }}>
              {t("hidden.privacyNote", lang)}
            </div>
          </div>
        </div>
      )}

      {/* === HIDDEN HISTORY (conversational heritage narrative) === */}
      {showHiddenHistory && hiddenHistoryLocation && (
        <Suspense fallback={null}><HiddenHistory
          lat={hiddenHistoryLocation.lat}
          lng={hiddenHistoryLocation.lng}
          address={hiddenHistoryLocation.address}
          initialRadius={hiddenHistoryLocation.narrativeRadius || exploreRadius}
          initialNarrative={hiddenHistoryLocation.narrative}
          heritageFeatures={heritageFeatures}
          indexes={{
            folkloreIndex, runestoneIndex, battlefieldIndex, monumentIndex,
            buildingIndex, shipwreckIndex, museumIndex, rockArtIndex,
            unescoIndex, industrialIndex, barrowIndex, megalithIndex,
            townscapeIndex, plaqueIndex, stolpersteinIndex, lighthouseIndex,
            thingsiteIndex, routeIndex, walkIndex, byleksikonIndex,
          }}
          lang={lang}
          onClose={() => { setShowHiddenHistory(false); setHiddenHistoryLocation(null); setReturnToHH(false); }}
          onSelectSite={(_id, name, _layerType) => {
            const nameLower = name.toLowerCase();
            const hhLat = hiddenHistoryLocation!.lat;
            const hhLng = hiddenHistoryLocation!.lng;
            interface MatchablePin { id: string; name?: string; lat: number; lng: number; country?: Country }
            let bestMatch: { pin: MatchablePin; lt: string; dist: number } | null = null;

            const tryMatch = (pin: MatchablePin, lt: string) => {
              const pinName = (pin.name || "").toLowerCase();
              // Exact match or contains (for slight naming differences)
              if (pinName !== nameLower && !pinName.includes(nameLower) && !nameLower.includes(pinName)) return;
              const dist = Math.abs(pin.lat - hhLat) + Math.abs(pin.lng - hhLng);
              if (dist > 0.3) return; // ~20km max
              // Prefer exact match
              const exactBonus = pinName === nameLower ? 0 : 0.01;
              if (!bestMatch || (dist + exactBonus) < bestMatch.dist) bestMatch = { pin, lt, dist: dist + exactBonus };
            };

            const allIdxs: { ref: React.MutableRefObject<Map<string, MatchablePin>>; lt: string }[] = [
              { ref: runestoneIndex, lt: "runestone" }, { ref: battlefieldIndex, lt: "battlefield" },
              { ref: monumentIndex, lt: "monument" }, { ref: buildingIndex, lt: "building" },
              { ref: shipwreckIndex, lt: "shipwreck" }, { ref: museumIndex, lt: "museum" },
              { ref: rockArtIndex, lt: "rockart" }, { ref: unescoIndex, lt: "unesco" },
              { ref: industrialIndex, lt: "industrial" }, { ref: barrowIndex, lt: "barrow" },
              { ref: megalithIndex, lt: "megalith" }, { ref: townscapeIndex, lt: "townscape" },
              { ref: lighthouseIndex, lt: "lighthouse" }, { ref: thingsiteIndex, lt: "thingsite" },
              { ref: folkloreIndex, lt: "folklore" }, { ref: plaqueIndex, lt: "plaque" },
              { ref: stolpersteinIndex, lt: "stolperstein" },
            ];
            for (const { ref, lt } of allIdxs) {
              for (const [, pin] of ref.current) tryMatch(pin, lt);
            }
            for (const f of heritageFeatures) {
              if (f.geometry.type !== "Point" || !f.properties) continue;
              const [fLng, fLat] = (f.geometry as GeoJSON.Point).coordinates;
              tryMatch({ ...f.properties, lat: fLat, lng: fLng, id: f.properties.id }, "heritage");
            }

            setShowHiddenHistory(false);
            setReturnToHH(true);
            if (bestMatch) {
              const { pin, lt } = bestMatch as { pin: MatchablePin; lt: string; dist: number };
              if (lt === "heritage") {
                setSelected({ ...pin });
                setSelectedType("heritage");
              } else {
                handleSelectNearby({ id: pin.id, name: pin.name ?? "", layerType: lt as LayerType, distance: 0, lng: pin.lng, lat: pin.lat, country: pin.country });
              }
              mapRef.current?.flyTo({ center: [pin.lng, pin.lat], zoom: 14, duration: 1000 });
            } else {
              // Not found in current data — search
              mapRef.current?.flyTo({ center: [hhLng, hhLat], zoom: 14, duration: 1000 });
              setTimeout(() => { setSearchQuery(name); setSearchOpen(true); }, 1500);
            }
          }}
          userData={userData}
          markVisited={markVisited}
          unmarkVisited={unmarkVisited}
          toggleSaved={toggleSaved}
          isVisited={isVisited}
          isSaved={isSaved}
          addBadge={addBadge}
          addNote={addNote}
          onBadgeEarned={handleBadgeEarned}
          collections={userData.collections}
          onAddToCollection={addToCollection}
          onRemoveFromCollection={removeFromCollection}
          onCreateCollection={createCollection}
          isLoggedIn={!!user}
          onLoginRequest={handleLoginRequest}
          onCompassActivate={isMobileOrTablet ? handleCompassActivate : undefined}
        /></Suspense>
      )}

      {/* === WELCOME (extracted to WelcomeSheet) === */}
      {showWelcome && (
        <Suspense fallback={null}><WelcomeSheet
          lang={lang}
          welcomeDismiss={welcomeDismiss}
          onClose={() => setShowWelcome(false)}
          onLocate={handleLocate}
          onJumpToCity={(lng, lat, zoom) => { setShowWelcome(false); mapRef.current?.flyTo({ center: [lng, lat], zoom, duration: 1500 }); }}
          onStartExplore={() => { setShowWelcome(false); openExploreMode(); }}
          onShowWalks={() => { setShowWelcome(false); setActiveGroup("explore"); setActiveTab("walk"); void loadWalks(); }}
        /></Suspense>
      )}

      {sharedList && !selected && (
        <Suspense fallback={null}><SharedListSheet
          list={sharedList}
          lang={lang}
          onClose={closeSharedList}
          onOpen={(it) => handleSelectNearby({ id: it.id, name: it.name, layerType: it.layer, distance: 0, lng: it.lng, lat: it.lat, country: it.country })}
        /></Suspense>
      )}

      {/* === INSTALL PROMPT (extracted to InstallPromptBanner) === */}
      {/* Not while a walk is open: the banner is fixed to the bottom at
          z 45, exactly on top of the walk stepper and WalkModeBar, and an
          install nudge must not sit between the reader and the next stop.
          It returns when the walk closes — the nudge is deferred, not lost. */}
      {showInstallPrompt && !showWelcome && !(selectedType === "walk" && selected) && (
        <InstallPromptBanner
          lang={lang}
          isIOS={isIOS}
          canPromptNatively={canPromptNatively}
          onDismiss={dismissInstall}
          onInstall={handleInstall}
        />
      )}

      {/* === BADGE TOAST === */}
      {badgeToast && <BadgeToast badge={badgeToast} lang={lang} onClose={() => setBadgeToast(null)} />}

      {/* === TIP OF THE DAY === */}
      {/* {!selected && !showWelcome && !showProfile && userData.tipDismissedDate !== new Date().toISOString().slice(0, 10) && (
        <TipOfTheDay lang={lang} onDismiss={dismissTip} />
      )} */}

      {/* === EXPLORER PROFILE === */}
      {showProfile && (
        <Suspense fallback={null}><ExplorerProfile
          userData={userData} lang={lang} onClose={() => setShowProfile(false)}
          counts={counts} folkloreCounts={folkloreCounts} runestoneCount={runestoneCount}
          battlefieldCount={battlefieldCount} monumentCount={monumentCount} buildingCount={buildingCount}
          shipwreckCount={shipwreckCount} museumCount={museumCount} rockArtCount={rockArtCount}
          unescoCount={unescoCount} industrialCount={industrialCount}
          routeCount={routeCount} thingsiteCount={thingsiteCount}
          persist={persist} onSelectItem={handleSelectNearby} onSetLang={setLang}
          themeMode={themeMode} onSetThemeMode={setThemeMode}
          isLoggedIn={!!user} userEmail={user?.email} onLoginRequest={handleLoginRequest} onSignOut={signOut}
          onOpenModeration={isAdmin ? () => setShowModeration(true) : undefined}
          moderationCount={isAdmin ? moderationCount : undefined}
          onOpenCollection={(c) => { setShowProfile(false); setViewingCollection(c); }}
          onCreateCollection={createCollection}
        /></Suspense>
      )}

      {showModeration && isAdmin && (
        <Suspense fallback={null}><ModerationPanel lang={lang} onClose={() => setShowModeration(false)} /></Suspense>
      )}

      {/* === COLLECTION PAGE === */}
      {viewingCollection && (
        <Suspense fallback={null}><CollectionPage
          collection={viewingCollection}
          lang={lang}
          onClose={() => { setViewingCollection(null); setShowProfile(true); }}
          onSelectItem={(item) => {
            setViewingCollection(null);
            if (item.siteId.startsWith("hidden-")) {
              // Re-open HiddenHistory with saved coordinates + narrative
              setHiddenHistoryLocation({ lat: item.lat, lng: item.lng, address: item.name, narrative: item.narrative, narrativeRadius: item.narrativeRadius });
              setShowHiddenHistory(true);
              mapRef.current?.flyTo({ center: [item.lng, item.lat], zoom: 14, duration: 1500 });
            } else {
              handleSelectNearby({ id: item.siteId, name: item.name, layerType: item.layerType, distance: 0, lng: item.lng, lat: item.lat, country: item.country });
            }
          }}
          onRemoveItem={(siteId) => {
            removeFromCollection(viewingCollection.id, siteId);
            setViewingCollection({ ...viewingCollection, items: viewingCollection.items.filter(i => i.siteId !== siteId) });
          }}
          onDelete={() => { deleteCollection(viewingCollection.id); setViewingCollection(null); setShowProfile(true); }}
          onUpdate={(updates) => {
            updateCollection(viewingCollection.id, updates);
            setViewingCollection({ ...viewingCollection, ...updates });
          }}
        /></Suspense>
      )}

      {/* === DETAIL PANEL === */}
      {/* Walk map view swaps the sheet for the bar below; the walk itself
          stays selected, so closing is not involved and the hash survives. */}
      {!(walkMapView && selectedType === "walk") && <DetailPanel
        selected={selected}
        selectedType={selectedType}
        enrichment={enrichment}
        enrichLoading={enrichLoading}
        lang={lang}
        onClose={() => { setSelected(null); if (returnToHH) { setReturnToHH(false); setShowHiddenHistory(true); } }}
        onBack={returnToHH ? () => { setSelected(null); setReturnToHH(false); setShowHiddenHistory(true); } : undefined}
        nearbyPins={nearbyPins}
        onSelectNearby={handleSelectNearby}
        onNavigateRelation={handleNavigateRelation}
        onFolkloreContext={setFolkloreContext}
        onShowTeller={(informant) => {
          const told = [...folkloreIndex.current.values()].filter((r) => (r as { informant?: string }).informant === informant);
          if (told.length) { setSelected(told); setSelectedType("folklore"); }
        }}
        userData={userData}
        markVisited={markVisited}
        unmarkVisited={unmarkVisited}
        toggleSaved={toggleSaved}
        isVisited={isVisited}
        isSaved={isSaved}
        addBadge={addBadge}
        addNote={addNote}
        onBadgeEarned={handleBadgeEarned}
        collections={userData.collections}
        onAddToCollection={addToCollection}
        onRemoveFromCollection={removeFromCollection}
        onCreateCollection={createCollection}
        isLoggedIn={!!user}
        onLoginRequest={handleLoginRequest}
        onCompassActivate={isMobileOrTablet ? handleCompassActivate : undefined}
        activeStopId={activeStopId}
        onSelectWalkStop={onSelectWalkStop}
        onShowWalkMap={handleShowWalkMap}
        onShowWalkPast={handleShowWalkPast}
      />}

      {/* === WALK MODE BAR — the collapsed walk over a usable map === */}
      {walkMapView && selectedType === "walk" && selected && (
        <Suspense fallback={null}>
          <WalkModeBar
            walk={selected as CuratedWalk}
            activeStopId={activeStopId}
            lang={lang}
            onSelectStop={onSelectWalkStop}
            onExpand={() => setWalkMapView(false)}
            onCompassActivate={isMobileOrTablet ? handleCompassActivate : undefined}
          />
        </Suspense>
      )}

      {/* === RELATION WIKI ARTICLE VIEWER === */}
      {relationViewer && (
        <div style={{ position: "fixed", top: "var(--detail-margin, 0px)", right: "var(--detail-margin, 0px)", bottom: "var(--detail-margin, 0px)", zIndex: 31, width: 420, maxWidth: "100vw", background: "var(--bg-surface)", borderRadius: "var(--detail-radius, 0)", display: "flex", flexDirection: "column", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", animation: "slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)", paddingTop: "env(safe-area-inset-top)" }}>
          <Suspense fallback={null}><WikiArticleViewer initial={relationViewer} lang={lang} onClose={() => setRelationViewer(null)} /></Suspense>
        </div>
      )}

      {/* === COMPASS OVERLAY === */}
      {compassActive && compassTarget && compassUserPos && (
        <CompassOverlay
          target={compassTarget}
          userPos={compassUserPos}
          heading={compassHeading}
          lang={lang}
          onClose={closeCompass}
        />
      )}
      {showAuthModal && (
        <Suspense fallback={null}><AuthModal lang={lang} initialMode={authMode} onClose={() => setShowAuthModal(false)} onLogin={handleLogin} onResetPassword={resetPassword} onResendConfirmation={resendConfirmation} /></Suspense>
      )}

      {showExploreMode && (
        <Suspense fallback={null}><ExploreMode
          lang={lang}
          onClose={() => setShowExploreMode(false)}
          heritageFeatures={heritageFeatures}
          indexes={{
            folklore: folkloreIndex, runestone: runestoneIndex, battlefield: battlefieldIndex,
            monument: monumentIndex, building: buildingIndex, shipwreck: shipwreckIndex,
            museum: museumIndex, rockart: rockArtIndex, unesco: unescoIndex,
            industrial: industrialIndex, barrow: barrowIndex, megalith: megalithIndex,
            townscape: townscapeIndex, plaque: plaqueIndex, stolperstein: stolpersteinIndex,
            lighthouse: lighthouseIndex, thingsite: thingsiteIndex, route: routeIndex,
            byleksikon: byleksikonIndex,
          // useExploreMode reads these refs as `Map<string, any>` because layer-typed
          // pin shapes vary across the indexes (each has different fields beyond
          // lat/lng/name). Casting to any here keeps the hook generic.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as Record<string, React.MutableRefObject<Map<string, any>>>}
          isVisited={isVisited}
          markVisited={markVisited}
          streakWeeks={userData.streakWeeks ?? 0}
          totalVisited={userData.totalVisits ?? 0}
          onOpenSite={(id, layerType, lat, lng, name, country) => {
            setShowExploreMode(false);
            handleSelectNearby({ id, name, layerType, distance: 0, lng, lat, country });
          }}
          /* From the compact strip: open the pin and keep the strip, so a
             walker can look and come back to it. */
          onPeekSite={(id, layerType, lat, lng, name, country) => {
            handleSelectNearby({ id, name, layerType, distance: 0, lng, lat, country });
          }}
        /></Suspense>
      )}
    </main>
  );
}
